import { UploadIdentificationCommandResultStatus } from "Api/Api";
import {
  AddressType,
  Bank,
  BankEnvironment,
  LicenseCode,
  LoyaltyEventCode,
  PartyType,
  TransactionStatus,
  UploadIdentificationCommandResultStatus2,
  VatStatus,
} from "Infrastructure/Api/Api";
import type { ResourceDictionary } from "./Resources";

export const EnglishResourcesDictionary: ResourceDictionary = {
  Hooks: {
    BankingAuth: {
      Error: "Bank connection failed",
    },
    Employee: {
      AccessSent: "Employee invitation sent",
      BackOfficeTargetError:
        "This e-mail address belongs to an internal {{appName}} account (Admin, AML, onboarding) and cannot be invited. Use a different e-mail address.",
      AccessSendError: "Employee access could not be sent",
      EmployeeExistsError:
        "This person already has employee access to this company",
      InviteeNotRegisteredError:
        "No {{appName}} account exists for this e-mail — the person must register in {{appName}} first, then you can invite them",
      InvitationCancelled: "Invitation was cancelled",
      InvitationCancelError: "Invitation could not be cancelled",
      EmployeeRemoved: "Employee was removed",
      EmployeeRemoveError: "Employee could not be removed",
    },
    Notifications: {
      Created: "Notification created successfully",
      CreateError: "Error creating notification",
      Updated: "Notification updated successfully",
      UpdateError: "Error updating notification",
      Deleted: "Notification deleted successfully",
      DeleteError: "Error deleting notification",
      Restored: "Notification restored successfully",
      RestoreError: "Error restoring notification",
    },
    OCR: {
      Error: "Unable to read data from documents",
      Declined: "The document did not pass the automated authenticity check.",
    },
    Paypoint: {
      PaypointEdited: "Paypoint {{name}} was edited",
      AssignNfcCardSuccess: "NFC card was assigned to paypoint {{name}}",
      AssignNfcCardError: "Error while assigning NFC card to paypoint",
      PaypointEditError: "Error while editing paypoint",
      PaypointCreateError: "Error while creating paypoint",
      PaypointCreated: "Paypoint {{name}} was created",
      AssignNfcCardNoConflict:
        "Error - NFC card already assigned to another paypoint",
      ScanError: "Could not start the payment, please try again",
    },
    Transaction: {
      TipUpdateError: "Failed to update tip",
    },
    BankingAccount: {
      Deleted: "Bank account was deleted",
      DeleteError: "Error deleting bank account",
      PrimaryAccountChanged: "Primary account was changed",
      PrimaryAccountChangeError: "Error while changing primary account",
      StatusChanged: "Account was edited",
      StatusChangeError: "Error while editing account",
    },
    TerminalContract: {
      DownloadError: "Error downloading terminal contract",
      UcbContractPrefix: "UCB_contract_draft_",
    },
    Verifications: {
      Error: "Error while verifying registration data",
    },
    VerificationEmail: {
      Error: "Error while sending verification e-mail",
      Success: "Verification e-mail was sent",
    },
    CampaignTransaction: {
      Error: "Error while initiating campaign transaction",
      Success: "Campaign transaction initiated successfully",
    },
    Ares: {
      Error: "Error while communicating with the business register",
      SubjectNotFound: "Subject not found",
    },
  },
  Stories: {
    Bar: {
      YourStory: "Your story",
    },
    ImageUpload: {
      Prompt: "Drag an image here or click to select",
      Error: "Image upload failed",
      InvalidType: "Only PNG, JPEG or WebP images are supported",
      TooLarge: "The image can be at most {{megabytes}} MB",
    },
    VideoUpload: {
      Prompt: "Drag a video here or click to select",
      Error: "Video upload failed",
      TooLong: "The video can be at most {{seconds}} seconds long",
      InvalidType: "Only MP4, WebM or MOV videos are supported",
      TooLarge: "The video can be at most {{megabytes}} MB",
    },
    Management: {
      Title: "Stories",
      CreateButton: "New story",
      Empty: "No stories yet",
      LoadError: "Stories could not be loaded",
      Status: {
        Draft: "Draft",
        Live: "Live",
        Expired: "Expired",
        Unpublished: "Unpublished",
      },
      Table: {
        Author: "Author",
        Text: "Text",
        State: "Status",
        LastPublishedAt: "Last published",
        ExpiresAt: "Expires",
        DateCreated: "Created",
        Publications: "Times published",
        Actions: "Actions",
        Preview: "Preview",
        Publish: "Publish",
        Republish: "Publish again",
        LiveUntil: "Live until {{dateTime}}",
        Unpublish: "Unpublish",
      },
      Messages: {
        PublishSuccess: "Story published",
        PublishError: "Publishing failed",
        DeleteSuccess: "Story deleted",
        DeleteError: "Deleting failed",
        DeleteConfirmTitle: "Delete story?",
        DeleteConfirm: "The story will be removed permanently.",
        LockedWhileLive:
          "A live story cannot be edited until it expires or is taken down.",
        UnpublishSuccess: "Story taken down",
        UnpublishError: "Taking the story down failed",
        UnpublishConfirmTitle: "Take the story down?",
        UnpublishConfirm:
          "Viewers lose it immediately. You can edit it and publish it again later; publishing again uses another slot of the author's daily limit.",
        NotLiveAnymore:
          "The story is no longer live; the list has been refreshed",
      },
      Form: {
        CreateTitle: "New story",
        EditTitle: "Edit story",
        SaveAndPublish: "Save and publish",
        SaveDraft: "Save as draft",
        Save: "Save",
        SaveSuccess: "Story saved",
        SaveError: "Saving failed",
      },
    },
    PostStory: {
      StoryImage: "Story image",
      StoryVideo: "Story video",
      Text: "Text",
      ButtonLabel: "Button label",
      ButtonUrl: "Button link",
      SheetSection: "Offer detail",
      SheetTitle: "Title",
      SheetDescription: "Description",
      SheetPrice: "Price",
      SheetPriceNote: "Price note",
      SheetImage: "Offer image",
      Form: {
        Format: {
          Title: "Format",
          Subtitle: "Pick one — a story is either a photo or a video.",
          PhotoTitle: "Photo story",
          PhotoHint: "{{formats}} · shown for {{seconds}} seconds",
          VideoTitle: "Video story",
          VideoHint: "{{formats}} · up to {{seconds}} seconds",
        },
        Media: {
          PhotoTitle: "Photo",
          PhotoSubtitle: "Vertical 9:16 works best. Up to {{megabytes}} MB.",
          VideoTitle: "Video",
          VideoSubtitle:
            "Vertical 9:16 works best. Up to {{seconds}} seconds and {{megabytes}} MB.",
          PosterLabel: "Cover image",
          PosterHint:
            "Optional. Without a cover the story opens on a black frame until the video starts.",
        },
        Text: {
          Title: "Text",
        },
        Button: {
          Title: "Button",
          Subtitle:
            "Shown on the story and opens this offer. The link becomes the offer's action button.",
          LabelPlaceholder: "e.g. Get the offer",
          UrlPlaceholder: "https://…",
        },
        Offer: {
          Subtitle: "Attach a marketplace offer to this story.",
          Attach: "Attach offer",
          Remove: "Remove offer",
        },
      },
      LimitExceeded:
        "You've reached the story limit for the last 24 hours. Please try again later.",
      UsageLabel:
        "Stories published in the last 24 hours: {{used}} / {{limit}}",
      Validation: {
        MediaRequired: "A story needs an image or a video",
        ButtonUrlInvalid: "Enter a valid link starting with https://",
        ButtonLabelRequired: "An offer needs a button label to open it",
      },
    },
  },
  Common: {
    LoadMore: "Load more",
    BackToMoone: "Back to {{appName}}",
    DownloadError: "File download failed",
    SaveError: "Saving failed",
    UnknownUserType: "Unknown user type",
    Unsupported: "unsupported",
    Retry: "Try again",
    Restore: "Restore",
    TaxNumber: "Tax number",
    VatNumber: "VAT ID",
    VatStatus: {
      [VatStatus.Unknown]: "Not verified",
      [VatStatus.NotRegistered]: "Not registered for VAT",
      [VatStatus.Registered]: "Valid VAT ID",
      [VatStatus.Payer]: "VAT payer",
      [VatStatus.RegisteredNonPayer]: "Identified person for VAT",
    },
    VatRegistryStatusLabel: "VAT register status",
    VatRegistryStatus: "VAT register: {{status}}",
    VatRegistryUnavailableHint:
      "The VAT register did not answer just now. You can enter your VAT ID now or at any time later.",
    VatNumberRejectedByRegistry:
      "The VAT register does not know this VAT ID. Check that it is entered correctly — otherwise the supply will be invoiced with Czech VAT.",
    Restart: "Restart",
    Loading: "Loading",
    ThankYou: "Thank you",
    Disable: "Disable",
    Notes: "Notes",
    Copy: "Copy",
    ComingSoon: "Coming soon",
    Month: "Month",
    NotAvailable: "Not available",
    Verification: {
      Verified: "Verified",
      NotVerified: "Not verified",
    },
    User: "User",
    AnonymousUser: "{{appName}} User",
    UnnamedChannel: "Unnamed channel",
    SearchMinCharsHint:
      "Search by @handle or name, type at least {{minChars}} characters.",
    MoreOptions: "More options",
    Yes: "Yes",
    No: "No",
    Back: "Back",
    Next: "Next",
    Close: "Close",
    Open: "Open",
    Clear: "Clear",
    Pause: "Pause",
    Play: "Play",
    Mute: "Mute",
    Unmute: "Unmute",
    RiskProfileStatus: {
      NotVerified: "Not verified",
      LowRisk: "Low risk",
      MediumRisk: "Medium risk",
      HighRisk: "High risk",
      Unacceptable: "Unacceptable",
    },
    UserType: {
      Hunter: "Hunter",
      Customer: "Customer",
      Merchant: "Merchant",
      Admin: "Admin",
      Aml: "AML",
      Employee: "Employee",
      CustomerOnboarding: "Customer Onboarding",
      HunterPlus: "Hunter+",
      AdminLite: "Admin (read-only)",
      Disponent: "Disponent",
      Child: "Child",
    },
    FacilityCategory: {
      Restaurants: "Restaurants",
      Food: "Food",
      Car: "Car",
      Electronics: "Electronics",
      ClothingAndShoes: "Clothing and Shoes",
      HealthAndBeauty: "Health and Beauty",
      Finance: "Finance",
      LeisureTime: "Leisure Time",
      PhoneTvAndInternet: "Phone, TV and Internet",
      Housing: "Housing",
      Travel: "Travel",
      Insurance: "Insurance",
      Education: "Education",
      Institutions: "Institutions",
      ConstructionAndMaintenance: "Construction and Maintenance",
      ECommerceAndOnlineServices: "E-commerce and Online Services",
      PersonalServices: "Personal Services",
      LegalAndConsultingServices: "Legal and Consulting Services",
      IndustrialProductionAndProcessing: "Industrial Production and Processing",
      Other: "Other",
    },
    WeekDay: {
      Monday: "Monday",
      Tuesday: "Tuesday",
      Wednesday: "Wednesday",
      Thursday: "Thursday",
      Friday: "Friday",
      Saturday: "Saturday",
      Sunday: "Sunday",
    },
    BeneficialOwner: {
      Title: "Beneficial owner",
      Name: "Name",
      LastName: "Last name",
      Gender: "Gender",
      BirthDate: "Date of birth",
      Nationality: "Nationality",
      IsForeigner: "Is foreigner",
    },
    Address: {
      Title: "Address",
      NoAddressAvailable: "No address available",
      Type: {
        [AddressType.Permanent]: "Permanent",
        [AddressType.Mail]: "Mail",
        [AddressType.Headquarters]: "Headquarters",
      },
      Street: "Street",
      StreetNumber: "Street number",
      OrientationNumber: "Orientation number",
      PostalCode: "Postal code",
      Municipality: "City",
    },
    QRPayment: "QR payment",
    ShareToBankApp: "Share",
    AppName: "{{appName}}",
    Amount: "Amount",
    TotalAmount: "Total Amount",
    Note: "Note",
    MandatoryNoteDescription:
      "Note is mandatory! Without a note, the payment will not be paired.",
    Unknown: "Unknown",
    UnknownRecipient: "Unknown recipient",
    ShowAll: "Show all",
    ShowMore: "Show more",
    ShowLess: "Show less",
    ShowProfile: "Show profile",
    VariableSymbol: "Variable symbol",
    Required: "Required field",
    InvalidIBAN: "Invalid IBAN",
    CancelEdit: "Cancel edit",
    Cancel: "Cancel",
    Confirm: "Confirm",
    UnsavedChangesTitle: "Unsaved Changes",
    UnsavedChangesWarning:
      "You have unsaved changes. Are you sure you want to close the form? All unsaved data will be lost.",
    DiscardChanges: "Discard Changes",
    NiceDay: "Have a nice day",
    Send: "Send",
    Continue: "Continue",
    FillMissing: "Please fill in all required information",
    IncompleteSection: "Information in this section is not complete",
    FirstName: "First name",
    LastName: "Last name",
    Gender: "Gender",
    PersonalNumber: "Personal number",
    BirthPlace: "Place of birth",
    Nationality: "Nationality",
    OtherNationalities: "Other nationality",
    Number: "Number",
    Type: "Type",
    IdentityDocument: "Identity document",
    Contact: "Contact",
    Phone: "Phone",
    PhoneVerified: "Phone verified",
    PhoneVerificationDescription:
      "Please verify your phone number. It helps keep {{appName}} free of spam and fake accounts.",
    Remove: "Remove",
    Replace: "Replace",
    Optional: "optional",
    Delete: "Delete",
    Edit: "Edit",
    Verify: "Verify",
    BirthDate: "Date of birth",
    AccountNumber: "Account number",
    TransactionPaymentCode: "Transaction ID",
    IBAN: "IBAN",
    Save: "Save",
    Saved: "Saved",
    DownloadQRCode: "Download QR code",
    Email: "E-mail",
    Login: "Login",
    CompanyName: "Company name",
    CompanyNumber: "Company number",
    PartyTypeLabel: "Business form",
    GeneralInfo: "General information",
    PartyType: {
      [PartyType.LegalEntity]: "Legal entity",
      [PartyType.SelfEmployed]: "Self-employed",
      [PartyType.NaturalPerson]: "Natural person",
    },
    PartyTypeShortcut: {
      [PartyType.LegalEntity]: "LE",
      [PartyType.SelfEmployed]: "SE",
      [PartyType.NaturalPerson]: "NP",
    },
    Sent: "Sent",
    Price: "Price",
    License: "License",
    Licenses: "Licenses",
    UnknownDate: "Unknown date",
    Time: {
      TimePassed: {
        Now: "just now",
        Minute: "minute ago",
        Hour: "hour ago",
        Day: "day ago",
        Week: "week ago",
        Month: "month ago",
        Year: "year ago",
        Minute_one: "{{count}} minute ago",
        Minute_other: "{{count}} minutes ago",
        Hour_one: "{{count}} hour ago",
        Hour_other: "{{count}} hours ago",
        Day_one: "{{count}} day ago",
        Day_other: "{{count}} days ago",
        Week_one: "{{count}} week ago",
        Week_other: "{{count}} weeks ago",
        Month_one: "{{count}} month ago",
        Month_other: "{{count}} months ago",
        Year_one: "{{count}} year ago",
        Year_other: "{{count}} years ago",
      },
      TimeRemaining: {
        Minute: "minute",
        Hour: "hour",
        Day: "day",
        Week: "week",
        Month: "month",
        Year: "year",
        Minute_one: "{{count}} minute",
        Minute_other: "{{count}} minutes",
        Hour_one: "{{count}} hour",
        Hour_other: "{{count}} hours",
        Day_one: "{{count}} day",
        Day_other: "{{count}} days",
        Week_one: "{{count}} week",
        Week_other: "{{count}} weeks",
        Month_one: "{{count}} month",
        Month_other: "{{count}} months",
        Year_one: "{{count}} year",
        Year_other: "{{count}} years",
      },
    },
    QRInstructions:
      "Do not fill the data manually, use the QR code that you can read in your mobile banking app!",
    Status: "Status",
    Balance: "Balance",
  },
  CodeLists: {
    IdentificationList: {
      IDCard: "ID card",
      Passport: "Passport",
      DrivingLicense: "Driving license",
      Other: "Other",
    },
    IdentityDocumentVerdict: {
      NotChecked: "Not checked",
      Approved: "Approved",
      Declined: "Declined",
      InReview: "Manual review",
      Unavailable: "Check unavailable",
    },
  },
  Errors: {
    MissingId: "Error: missing ID",
    DataLoadingError: "Data could not be loaded",
    RequestError: "An error occurred during the request",
    ApplicationError: {
      Title: "An error occurred",
      Home: "Home",
      ErrorOccurred: "We're sorry, but an error occurred in the application",
      TeamHasBeenInformed:
        "Our developers have already been informed about the error and are working on a fix.",
      AreYouDeveloper: "Are you a developer? Look at what the error was.",
    },
    Error404: {
      Subtitle: "Page not found",
      Home: "Home",
    },
    Error403: {
      Subtitle: "You don't have access rights to view this page",
    },
    NoUserRole: "Error, user has no role assigned",
    UnknownError: "An unknown error occurred",
  },
  Offline: {
    Title: "You are offline",
    NoInternet: "Check your internet connection and try again.",
    ServerUnreachable: {
      Title: "Connection issues",
      Message:
        "We're experiencing technical difficulties. We're working on fixing them. We're sorry for the inconvenience.",
    },
    Reconnected: "Connection restored",
  },
  SignIn: {
    Title: "Sign in",
    Form: {
      Controls: {
        SignInButtonLabel: "Sign in",
        CancelButtonLabel: "Cancel",
        ForgottenPassword: "Forgotten password?",
      },
      Fields: {
        Email: "E-mail",
        Password: "Password",
      },
    },
    Error: {
      General: "An error occurred",
      Forbidden: "Incorrect password or e-mail",
      InvalidLogin: "Incorrect password or e-mail",
      InvalidCredentials: "Incorrect password or e-mail",
      ApplicationVerification: "Incorrect password or e-mail",
      PhoneNumberNotFound: "Phone number not found",
      SmsSendingFailed: "SMS sending failed",
      InvalidToken: "Invalid token",
      AccountNotFound: "Incorrect password or e-mail",
      NotFound: "Incorrect password or e-mail",
      AccountIsDeactivated:
        "Account has been deactivated. If you want to restore your account, contact support.",
      AccountLinkedToIdentity:
        "This account has been linked to your main account. Please sign in using the e-mail of your main account.",
    },
  },
  SignUp: {
    Title: {
      Merchant: "New business account",
      Customer: "New personal account",
      Hunter: "New hunter account",
    },
    Form: {
      Controls: {
        SignUpButtonLabel: "Sign up",
      },
      Fields: {
        Login: "E-mail",
        Password: "Password",
        AgreeWithTermsAndConditions: {
          MooneLite: {
            Text: "I agree with the",
            LinkText: "{{appName}} Lite terms and conditions",
            LinkUrl: "/content/terms/Moone-moone-lite.pdf",
          },
          FullMooneEULA: {
            StartText:
              "By registering in the {{appName}} application, I agree to the",
            ContractText: "Payment services contract",
            ContractUrl:
              "/content/terms/2A-Moone-precontract-contract-info.pdf",
            MiddleText:
              "with the company Mo.one a.s., which includes the following",
            RelatedDocsText: "related terms",
            RelatedDocsUrl: "/content/terms/2B-Moone-related-docs.pdf",
          },
          EULACheckboxLabel: {
            StartText:
              "By registering in the {{appName}} application, I agree to the",
            ContractText: "Payment services contract",
            ContractUrl:
              "/content/terms/2A-Moone-precontract-contract-info.pdf",
            MiddleText:
              "by the company Mo.one a.s., which includes the following",
            RelatedDocsText: "related terms",
            RelatedDocsUrl: "/content/terms/2B-Moone-related-docs.pdf",
          },
          HunterEULACheckboxLabel: {
            StartText: "By buying a hunter license, I agree to the",
            ContractText: "Digital franchise contract",
            ContractUrl:
              "/content/terms/legione-hunters-franchise-contract.pdf",
            MiddleText: "by LEGI.ONE LTD., which includes the following",
            RelatedDocsText: "Related terms",
            RelatedDocsUrl: "/content/terms/2-hunters-related-terms.pdf",
          },
        },
      },
    },
    Error: {
      General: "An error occurred",
      UserExists: "Account already exists",
      WeakPassword:
        "Password must be at least 8 characters long and contain one lowercase letter, one uppercase letter, and one number",
    },
    AffiliateLandingDisclaimer: {
      Title: "Already have a Mo.one account?",
      Body: "If you already have an account, sign in instead and let the person who invited you know your email — they can then send you the invitation directly to your in-app inbox.",
      SignInButton: "Sign in",
    },
  },
  Validation: {
    InvalidLogin: "Invalid login",
    InvalidPersonalNumber: "Invalid personal number",
    OtherNationalitySameAsPrimary:
      "The other nationality must differ from the primary one",
    InvalidVatNumber: "Invalid VAT ID format",
    PersonalNumber:
      "Personal number must be at most {{count}} digits long, enter without a slash",
    CompanyName: "Company name is required",
    CompanyNumber: "Company number must be {{count}} digits long",
    Phone: {
      InvalidFormat: "Invalid phone number format",
      MinDigits: "Phone number must contain at least {{count}} digits",
      MaxCharacters: "Phone number must contain at most {{count}} characters",
    },
    Required: "Please fill in the field",
    InvalidEmail: "E-mail is invalid",
    InvalidUrl: "URL is invalid",
    Password: {
      Required: "Please fill in the field",
      MinLength: "At least {{length}} characters",
      MinLowercase: "At least one lowercase letter",
      MinUppercase: "At least one uppercase letter",
      MinNumber: "At least one number",
      Match: "Passwords do not match",
      MatchRequirement: "Passwords match",
    },
    EulaAgreement: "You must agree to the terms and conditions",
    WalletAddress: "With your own wallet, you must fill in the wallet address",
    InvalidBankAccount: "Invalid bank account",
    InvalidPostalCode: "Enter postal code in format 763 02",
    ResidenceRequired:
      "Fill in the person's place of residence — street or house number, municipality and postal code",
    ExpirationAfterScheduled: "Expiration date must be after scheduled date",
    DateMustBeInFuture: "Date must be in the future",
    TimeMustBeInFuture: "Time must be in the future",
    MaxLength: "This field must be at most {{count}} characters long",
  },
  ValidationMessages: {
    Required: "Please fill in the field",
    ArrayMinOne: "At least one option must be selected",
    TooSmallString: "Must be at least {{minimum}} characters",
    TooBigString: "Must be at most {{maximum}} characters",
    InvalidEmail: "Please enter a valid email address",
    InvalidUrl: "Please enter a valid URL",
  },
  Menu: {
    BottomMenuItems: {
      Overview: "Overview",
      History: "Transactions",
      InitiateKeypadPayment: "Register",
      ActionButton: "Pay",
      Accounts: "Accounts",
      Settings: "More",
      Merchants: "Merchants",
      Licenses: "Licenses",
      NewMerchant: "Merchant",
      Users: "Users",
      Stardust: "Stardust",
      Valor: "Valor",
      Settlement: "Settlement",
      ThanksToYou: "Thanks to you!",
      NotificationsManagement: "Notifications",
      ToBeVerified: "Verifications",
      Hunters: "Hunters",
      WhereToPay: "Where to pay",
      Chat: "Chat",
    },
    Help: "Help",
    Logout: "Logout",
  },
  Onboarding: {
    ProcessTitle: "Information about you",
    Title: "Verify your identity",
    LoadingTitle: "Information about you",
    ErrorTitle: "Application error",
    BeneficialOwnersTitle: "Beneficial owners",
    LicenseSelectionTitle: "Welcome to Legi.one",
    ManualLicensePaymentTitle: "License Payment",
    BankAccountVerificationTitle: "Account Verification",
    PoliticallyExposed: {
      Title: "I am a politically exposed person",
      Alert:
        "Unfortunately, we cannot provide services to politically exposed persons at this time.",
    },
    HunterDescription: {
      IdentityVerification:
        "Before we officially start together, we need to verify your identity.",
      HCRMDescription:
        "HCRM (Hunter & Customer Relationship Manager) is your personal work environment within Legi.one.",
      RemoteContract:
        "From here you will register new merchants, track your network, manage rewards and have an overview of everything important. It's your digital base.",
      DocumentSecurity:
        "Because we're entering into a contract remotely, we need to verify that it's really you.",
      QuickProcess:
        "It's the same principle as when you sign a contract with an operator or financial service online – without a personal meeting, verification is essential.",
      Welcome: "Welcome to Legi.one HCRM!",
      IdentityVerificationRequired:
        "Before we officially start together, we need to verify your identity.",
      HCRMWorkEnvironment:
        "From here you will register new merchants, track your network, manage rewards and have an overview of everything important. It's your digital base.",
      RemoteContractVerification:
        "Because we're entering into a contract remotely, we need to verify that it's really you.",
      VerificationPrinciple:
        "It's the same principle as when you sign a contract with an operator or financial service online – without a personal meeting, verification is essential.",
      QuickProcessAndWelcome:
        "It only takes a minute – and then you can start using all HCRM features to the full. Welcome to the team!",
    },
    MerchantOrCustomerDescription: {
      Welcome: "Welcome to {{appName}}!",
      IdentityVerificationRequired:
        "Before we open an account for you, we need to verify your identity.",
      MoonePaymentIdentity:
        "{{appName}} is not just an app – it's your payment identity.",
      PaymentIdentityDescription:
        "With it, you will pay, confirm transactions and securely prove yourself across services. That's why it's important to verify that it's really you.",
      LicensedPaymentInstitution:
        "We are a licensed payment institution supervised by the Czech National Bank.",
      BankLevelIdentityVerification:
        "When verifying identity, the same rules apply to us as to banks – they are imposed on us by Act No. 253/2008 Coll., on certain measures against the legalization of proceeds from criminal activity and financing of terrorism.",
      DocumentsUsagePurpose:
        "Your documents serve exclusively for this purpose. They are encrypted, protected and we will never use them otherwise without your explicit consent.",
      QuickProcessAndThankYou:
        "You can do it in a minute – and then you can start using {{appName}} to the full. Thank you for trusting us.",
    },
    Steps: {
      Documents: {
        NoOwningPartyError: "Failed to load user data.",
        Declined: {
          Title: "The document did not pass the automated check",
          Description:
            "Take a new photo of the document (not a scan or a screenshot), or continue — our operator will then verify the document manually.",
          Retry: "Upload again",
          Continue: "Continue with manual review",
          AttemptsLeft: "Attempts left: {{count}}",
          AttemptsExhausted:
            "The automated check cannot be repeated any more. Continue to the next step; our operator will verify the document.",
          Reasons: {
            DocumentExpired: "The document has expired.",
            NotOriginal:
              "This is not a photo of the physical document (scan, copy or screenshot).",
            Unreadable:
              "The document is not readable — photograph the whole document, sharp and without glare.",
            Unsupported:
              "We cannot verify this type of document automatically.",
            Generic: "The automated check could not confirm the document.",
          },
        },
        ExistingDocumentsInfo:
          "Your documents are already uploaded and being processed. You can proceed to the next step.",
        LoadingError: "Failed to load data, please try again later.",
        Processing: "Processing data. Please wait.",
        ProcessingFailed: "Failed to process ID card data",
        FirstDocumentTitle: "ID Card",
        SecondDocumentTitle: "Second Document",
        SecondDocumentDescription:
          "The second document can be a driver's license or passport.",
        Forms: {
          DocumentTitle: "Upload document scan",
          DocumentDescription: "Click here to upload or take a photo",
          FrontSide: "Front side of ID card",
          BackSide: "Back side of ID card",
          SecondDocumentFrontSide: "Front side of second document",
          SecondDocumentBackSide: "Back side of second document",
          SecondDocumentTypeLabel: "Second document type",
          SecondDocumentTypePassport: "Passport",
          SecondDocumentTypeDrivingLicense: "Driver's license",
          SecondDocumentTypeOther: "Other",
        },
        Validations: {
          ForegroundMissing: "Please upload the front side of your ID card",
          BackgroundMissing: "Please upload the back side of your ID card",
          SecondForegroundMissing:
            "Please upload the front side of the second document",
          SecondBackgroundMissing:
            "Please upload the back side of the second document",
          SecondDocumentTypeMissing:
            "Please select the type of the second document",
        },
      },
      PersonalInformation: {
        SaveError: "Failed to save personal information",
        LoadingError: "Failed to load personal information",
        Title: "From the uploaded documents, we have read the following data:",
        DataConfirmation:
          "I confirm that the basic data and address is correct",
        StardustParticipation: {
          Text: "I agree to join the loyalty program",
          LinkText: "Stardust",
          LinkUrl: "/content/terms/Moone-stardust-general.pdf",
        },
        ThanksToYouParticipation: {
          Text: "I agree to join the referral program",
          LinkText: "Thanks to you!",
        },
        FormGroups: {
          PersonalInformation: "Basic Information",
          Address: "Address",
          IdentityDocument: "Identity Document",
        },
        DocumentValidity: {
          Generic: "Document validity",
          From: "Document valid from",
          To: "Document valid until",
        },
        ScanIncomplete:
          "Some values were not scanned, a manual check will be performed by our staff.",
        DataMarkedAsIncorrect:
          "Thank you for the information, within 24h a check will be performed by our staff.",
        DocumentIssued: "Document issued by",
        StreetAddress: "Street",
        ConfirmDataRequest: "You must confirm that the data is correct",
        UseCompanyAddress: "I live at the company address",
        ValueLabels: {
          PersonalInfo: {
            FirstName: "First name",
            LastName: "Last name",
            PersonalNumber: "Personal number",
            BirthPlace: "Birth place",
            Gender: "Gender",
            Nationality: "Nationality",
          },
          AddressInfo: {
            StreetWithNumber: "Street and number",
            Municipality: "Municipality",
            PostalCode: "Postal code",
          },
          DocumentInfo: {
            Type: "Type",
            Number: "Number",
            Validity: "Validity",
            Issued: "Issued by",
          },
        },
      },
      FinalStep: {
        SubmitButton: "Complete",
        LoadingError: "Error, failed to load necessary data",
        SuccessToast: "Your information was submitted successfully.",
        Form: {
          HasNonEUResidence:
            "I currently (as of the registration date) reside outside the EU for more than 12 months",
          HasNonEUNationality: "I am a citizen of a country outside the EU",
        },
      },
    },
    BusinessOnboarding: {
      Steps: {
        LicenseSelection: {
          Title: "Welcome to Legi.one",
          Description:
            "By purchasing a license, you gain the right to become a hunter for Legi.one.",
          AlreadyOwned: "You already own this license.",
          Form: {
            PriceIncludesVAT: "The listed prices include 21% VAT.",
            ChooseLicense: "Choose the license type you want to purchase.",
            WalletCheckboxLabel: "I want it sent to my private wallet",
            PrivateWalletAddress: "Private wallet address",
            PrivateWalletAddressDescription:
              "Enter your private wallet address on the Polygon blockchain.",
            NoLicensesAvailableForCountry:
              "No licenses are available for the selected country.",
            SelectCountryFirst: "Select a country first to order a license.",
            SubmitButton: "Order and pay",
            SeePaymentDetails: "See payment details",
            AlreadyOrdered: "You have already ordered this license.",
          },
        },
        BankAccount: {
          Title: "Account Connection",
          AvailableBanks: "Available banks",
          BanksComingSoon: "Banks coming soon",
        },
        FacilityCompletion: {
          Title: "Facility Details",
          Description:
            "Please complete the details for your facilities. Fill in address, contact information, and other required data. If you don't have a facility, skip this step and click Continue below.",
          IncompleteFacilitiesWarning:
            "There are some unfinished facilities. Either complete the information or remove them.",
          OrbitLFacilityRequiredWarning:
            "Activating the terminal requires adding a facility with photos.",
          ContinueWithoutFacility: "Continue without facility",
        },
        IncomeInformation: {
          SaveError: "Failed to save income information",
          Title: "Business information",
          FormGroups: {
            Business: "Your business",
            Personal: "Information about you",
          },
          Fields: {
            BusinessActivity: "Business activity",
            SourceOfIncome: "Source of income",
            RelationType: "Your position",
            IsPoliticallyExposedPerson: "I am a politically exposed person",
            HasNonEUResidence:
              "I currently (as of the registration date) reside outside the EU for more than 12 months",
            HasNonEUNationality: "I am a citizen of a country outside the EU",
            ExpectedMonthlyRevenue: "Expected monthly revenue",
            AverageTransactionValue: "Average transaction value",
            TaxResidencyCountry: "Tax residency country",
            OtherBusinessActivity: "Specify business activity",
            OtherSourceOfIncome: "Specify source of income",
          },
        },
        BusinessInformation: {
          SaveError: "Failed to save business information",
          LoadingError: "Failed to load business information",
          CompanyNumberAresMismatch:
            "The entered company number does not match the data from the business register",
          CompanyNumberAlreadyInUse:
            "This company number is already registered by another merchant",
          Title: "Business information",
          RegionLabel: "Country of registration",
          SlovakVatRegistrationNotice:
            "VAT ID notice: If you are a taxable person established in Slovakia and receive a service from a supplier in another EU member state, § 7a of Act No. 222/2004 Coll. on VAT requires you to apply for VAT registration before receiving the first such service. Registration under § 7a does not make you a VAT payer. If you do not provide us with a valid VAT ID, the supply will be invoiced with Czech VAT, which you can neither deduct nor reclaim in Slovakia.",
          FormGroups: {
            BasicInformation: "Basic information",
            Headquarters: "Headquarters",
            Contact: "Contact",
          },
        },
        OwnerModal: {
          Title: "Information about the beneficial owner",
        },
        BeneficialOwners: {
          SaveError: "Failed to save additional information",
          Title: "Beneficial Owner",
          Question: "Are you the only beneficial owner of the company?",
          SecondaryQuestion: "Do you know who the beneficial owners are?",
          Description:
            "A beneficial owner is any natural person who ultimately owns or controls a legal entity or legal arrangement",
          CheckboxTitle: "I am the beneficial owner",
          CheckboxDescription:
            "A beneficial owner is any natural person who ultimately owns or controls a legal entity or legal arrangement",
          OwnersTitle: "Additional beneficial owners",
          AddOwner: "Add beneficial owner",
          AddNextOwner: "Add another beneficial owner",
          UboChoice: {
            IAmOwner: "Yes, I am",
            IAmOwnerDescription:
              "Beneficial owner data will be automatically filled from your profile",
            NotTheOnlyOwner: "No / There are more of us",
            ManualEntry: "Yes, insert",
            ManualEntryDescription:
              "Enter beneficial owner information manually",
            Unknown: "I don't know",
            UnknownDescription:
              "The information will be verified by our AML specialist",
          },
          FormGroups: {
            BasicInformation: "Basic information",
            Address: "Address",
          },
        },
      },
    },
    BanksInPreparation: "Banks in preparation",
    PotentialYearlySavings: "Potential Annual Savings",
  },
  Transactions: {
    Title: "Transactions",
    History: "History",
    NoTransactions: "No transactions available",
    CancelTransaction: "Cancel transaction",
    RefundTransaction: "Refund transaction",
    InverseTransactionCreated:
      "An inverse transaction has been created. It needs to be confirmed from the merchant's user account.",
    TransactionCancelError:
      "An error occurred while cancelling the transaction.",
    Payment: {
      Incoming: "Incoming payment",
      Outgoing: "Outgoing payment",
      Unknown: "Payment",
    },
    Type: {
      Buy: "Purchase",
      Sell: "Sale",
      Withdrawal: "Withdrawal",
      LeadReward: "Referral reward",
      LeadRewardConsumer: "Registration reward",
      Voucher: {
        GiftCard: "Gift card",
        SignUpSubsidy: "Registration gift",
      },
      Coinback: "Coinback",
    },
    ErrorPayment: {
      Title: "An error occurred",
      TitleNotCompleted: "Payment not completed",
      Description: "We're sorry, but there was a problem during the payment.",
    },
    SuccessPayment: {
      Title: "Successfully paid!",
      DescriptionFirstLine: "Money was successfully sent",
      DescriptionSecondLine: "to the merchant's account.",
    },
    Anonymous: {
      DownloadReceipt: "Download receipt to device",
      FillEmail: "Enter your e-mail so we can send you the receipt.",
      SignupTitle: "Create an account and use {{appName}} benefits",
      Benefits:
        "✨ Collect Stardust and earn with {{appName}}<newline/>🤝 Help your local merchants<newline/>💳 Fast payment method always at hand<newline/>📖 Transaction history and finances in one place",
      CampaignSignupTitle:
        "By creating an account, you'll join the {{campaignName}} team and help with every payment!",
      CampaignSignupDescription:
        "Choose one of the available account creation options, register with one click and pay with Mo.one.",
      CreateAccountViaEmail: "Create account via E-mail",
      JustWantReceipt: "I just want a receipt",
      BackToSignup: "Back to account creation",
      LoadingSkeleton: {
        loadingInfo: {
          title: "Payment verification",
          description:
            "Payment verification is being performed <newline/> Loading payment information",
        },
        processingTransaction: {
          title: "Processing transaction",
          description:
            "Your payment is being processed <newline/> Waiting for confirmation from your bank",
        },
      },
    },
    Status: {
      Completed: "Completed",
      PendingTrade: "Processing",
      PendingPayout: "Waiting for e-shop confirmation",
      WithdrawRequested: "Withdrawal in progress",
      Withdrawn: "Completed",
    },
    TransactionStatus: {
      Created: "Waiting for payment",
      Cancelled: "Cancelled",
      Fail: "Failed",
      Initiated: "Payment started",
      Processing: "Processing",
      Success: "Successfully received",
      InvestigationNeeded: "Unknown status",
    },
    RefundState: {
      Title: "Refund",
      Refunded: "Refunded",
      Pending: "Refund pending",
    },
    TransactionDirection: {
      Incoming: "Incoming payment",
      Outgoing: "Outgoing payment",
      IncomingRefund: "Incoming refund",
      OutgoingRefund: "Outgoing refund",
      Unknown: "Unknown direction",
    },
    BankTransferInfo:
      "Bank transfer may take up to 2 business days, depending on the bank's speed.",
    TransactionStatusDescription: {
      Created: "This transaction is waiting for payment.",
      Cancelled: "This transaction was cancelled.",
      Fail: "Transaction failed.",
      Initiated: "Payment was initiated, waiting for confirmation.",
      Processing: "Transaction is being processed.",
      Success: "This transaction is completed.",
      InvestigationNeeded: "Unknown status",
    },
    ReceiptOnly: "I just want a receipt",
  },
  SetPassword: {
    Error: {
      General: "An error occurred.",
      TokenNotFound: "Token is invalid",
      TokenExpired:
        "The validity of the link for setting password has expired. ",
      TokenExpiredLinkText: "Request a new link",
      WeakPassword:
        "Password must be at least 8 characters long and contain one lowercase letter, one uppercase letter, and one number",
    },
  },
  BankIDVerification: {
    Error: {
      General: "BankID authentication failed. Please try again.",
      BankIDUserInfoError: "Failed to retrieve data from BankID.",
      VerificationAlreadyExists: "BankID user has already been verified.",
    },
  },
  Voucher: {
    ApplyError: {
      General: "An error occurred",
      NotFound:
        "The entered code is not correct. Check if it is copied correctly. If problems persist, contact us.",
      Applied: "Voucher has been used",
      UserAccountNotFound: "User not found",
      UserNotFound: "User not found",
      UserIsWaitingForEmailVerification: "User not found",
      InvalidRecaptcha:
        "Recaptcha is not valid. Refresh the application and try again.",
    },
    ToOverview: "To Overview",
    SignIn: "Sign In",
    AgreeWith: "I agree with",
    Continue: "Continue",
    CardCodeInstructions: "2. Copy the 16-digit code from the card",
  },
  Balance: {
    Title: "Overview",
    BalanceContent: {
      Title: "Available funds",
      PayMeAction: "Pay me",
      SendPaymentAction: "Payment",
      HiddenBalance:
        "To view the balance, complete your registration and wait for your account to be verified",
      ShowBalance: "Show balance",
      HideBalance: "Hide balance",
      CurrencyCount: "{{count}} currencies",
      CurrencyCount_one: "{{count}} currency",
      CurrencyCount_few: "{{count}} currencies",
      CurrencyCount_other: "{{count}} currencies",
      CurrencySheetTitle: "My currencies",
    },
  },
  Banking: {
    Auth: {
      Title: "Connect Account",
      Description: "Choose the bank you want to connect.",
      BankList: {
        Title: "Bank Offer",
        ComingSoon: "Coming Soon",
      },
      BankConnectionInfo:
        "Bank connection is a one-time process, connect the bank and use {{appName}} payments without a limit!",
      BankConnectionWarning:
        "Warning: Connection quality depends on banks. If you have a problem with logging in or payment, contact your bank.",
    },
    AccountSelection: {
      Title: "{{bankName}} Connection",
      Description:
        "In case you don't want to use some of the connected accounts with {{appName}}, unselect them in the list below.",
      Cancel: "Cancel",
      Confirm: "Confirm",
      AccountsSetUp: "Accounts were successfully set up",
      RemoveError: "Error removing accounts",
      DeleteLaterInfo:
        "You can also delete accounts later in the More -> Banking Accounts tab.",
      SelectedAccounts: "Select all",
      AllAccounts: "All accounts",
      WarningMessage:
        "You can only connect an account held under your name. If you connect someone else's account, it will be rejected by {{appName}}.",
    },
    BanksInPreparation: "Banks in preparation",
    QRPaymentForeignBank: "Payment by foreign bank using QR code",
    BatchPayments: {
      Title: "Batch Payments",
      DetailTitle: "Batch Payment Detail",
      EmptyList: "No batch payments have been created yet.",
      NotFound: "Batch payment not found.",
      LoadMore: "Load more",
      Account: "Account",
      TotalAmount: "Total Amount",
      PaymentCount: "Payment Count",
      CreatedAt: "Created",
      AuthorizedAt: "Authorized",
      CompletedAt: "Completed",
      SignInBank: "Authorize in bank",
      PaymentsTable: "Payments",
      Columns: {
        Date: "Date",
        Status: "Status",
        Account: "Account",
        PaymentCount: "Payment Count",
        Total: "Total",
        Recipient: "Recipient",
        IBAN: "IBAN",
        VS: "VS",
        Amount: "Amount",
      },
      Status: {
        Created: "Created",
        Initiated: "Awaiting authorization",
        Processing: "Processing",
        Success: "Completed",
        Fail: "Failed",
        Cancelled: "Cancelled",
      },
    },
  },
  BankNames: {
    CSOB: "CSOB",
    CSAS: "Ceska sporitelna",
    KB: "Komercni Banka",
    AirBank: "Air Bank",
    UCB: "UniCredit Bank",
    MONETA: "Moneta Money Bank",
    FIO: "Fio banka",
    MBank: "mBank",
    RB: "Raiffeisenbank",
    Citfin: "Citfin",
    TRINITY: "Trinity Bank",
    PenezniDum: "Penezni dum",
    Artesa: "Artesa",
    CREDITAS: "CREDITAS",
    NEY: "NEY savings cooperative",
    MaxBanka: "Max bank",
    NRB: "National Development Bank",
    JandT: "J&T Bank",
    PPF: "PPF bank",
    Oberbank: "Oberbank",
    PartnersBank: "Partners Bank",
    CNB: "Czech National Bank",
    Revolut: "Revolut",
    CZKT: "CZKT",
  },
  BankEnvironmentNames: {
    [BankEnvironment.CSOB_SANDBOX]: "CSOB",
    [BankEnvironment.CSOB_PROD]: "CSOB",
    [BankEnvironment.CSAS_SANDBOX]: "Ceska sporitelna",
    [BankEnvironment.CSAS_PROD]: "Ceska sporitelna",
    [BankEnvironment.KB_SANDBOX]: "Komercni banka",
    [BankEnvironment.KB_PROD]: "Komercni banka",
    [BankEnvironment.AirBank_SANDBOX]: "Air Bank",
    [BankEnvironment.AirBank_PROD]: "Air Bank",
    [BankEnvironment.UCB_SANDBOX]: "UniCredit Bank",
    [BankEnvironment.UCB_PROD]: "UniCredit Bank",
    [BankEnvironment.MONETA_SANDBOX]: "Moneta Money Bank",
    [BankEnvironment.MONETA_PROD]: "Moneta Money Bank",
    [BankEnvironment.FIO_SANDBOX]: "Fio banka",
    [BankEnvironment.FIO_PROD]: "Fio banka",
    [BankEnvironment.MBANK_SANDBOX]: "mBank",
    [BankEnvironment.MBANK_PROD]: "mBank",
    [BankEnvironment.RB_SANDBOX]: "Raiffeisenbank",
    [BankEnvironment.RB_PROD]: "Raiffeisenbank",
    [BankEnvironment.Citfin_PROD]: "Citfin",
    [BankEnvironment.TRINITY_PROD]: "Trinity Bank",
    [BankEnvironment.PenezniDum_PROD]: "Penezni dum",
    [BankEnvironment.Artesa_PROD]: "Artesa",
    [BankEnvironment.CREDITAS_PROD]: "CREDITAS",
    [BankEnvironment.NEY_PROD]: "NEY sporicitelna druzstvo",
    [BankEnvironment.MaxBanka_PROD]: "Max banka",
    [BankEnvironment.NRB_PROD]: "Narodni rozvojova banka",
    [BankEnvironment.JandT_PROD]: "J&T Banka",
    [BankEnvironment.PPF_PROD]: "PPF banka",
    [BankEnvironment.Oberbank_PROD]: "Oberbank",
    [BankEnvironment.PartnersBank_PROD]: "Partners Banka",
    [BankEnvironment.CNB_PROD]: "Czech National Bank",
    [BankEnvironment.Revolut_SANDBOX]: "Revolut",
    [BankEnvironment.Revolut_PROD]: "Revolut",
    [BankEnvironment.CZKT_SANDBOX]: "CZKT",
    [BankEnvironment.CZKT_PROD]: "CZKT",
  },
  BankRating: {
    Banks: {
      [Bank.CSOB]: {
        Rating: "1.5",
        AdditionalInfo: "",
        Warnings:
          "• To connect your account, you need to click on the QR code or know your username and password<linebreak/> • Delivering push notifications from Smart Key sometimes takes up to 1 minute<linebreak/> • Returning from the bank app back to {{appName}} must be done manually",
        HowToConnect:
          "When connecting your account via mobile phone, click on the QR code that will take you directly to the Smart Key app. If you don't receive a push notification, open Smart Key directly from your phone menu and confirm the requested action (e.g. payment) there. After connecting your account/payment, click the back arrow to return from Smart Key back to {{appName}}. Smaller payments (e.g. up to 500,- Kč) sometimes don't need to be confirmed in the app - in this case, scroll the web page all the way down and click the green button on the right side.",
        Review:
          "We rate the API connection from ČSOB as average. To connect your account, you need to click on the QR code, which may be confusing for some users. The texts on the ČSOB website are displayed in English on the phone. Push notifications from the Smart Key app sometimes take a long time to deliver. There is no automatic redirection back to {{appName}}. We positively rate the use of one-factor authentication for smaller payments on verified accounts, when the payment is completed very quickly.",
        Email: "ombudsman@csob.cz",
      },
      [Bank.CSAS]: {
        Rating: "2.5",
        AdditionalInfo: "",
        Warnings:
          "• To connect your account, you need to fill in your username/client number and date of birth<linebreak/> • Returning from the George app back to {{appName}} must be done manually",
        HowToConnect:
          "Before connecting your account, check that you have your username/client number, which you use to log in to your internet banking. After connecting your account/payment, click the back arrow to return from George back to {{appName}}.",
        Review:
          "We rate the API connection from Česká spořitelna as good and fairly stable. Push notifications work correctly and quickly. To connect your account, the user must enter their username/client number and date of birth, which may be annoying for many users. There is no automatic redirection from George back to {{appName}}.",
        Email: "ombudsman@csas.cz",
      },
      [Bank.KB]: {
        Rating: "0.5",
        AdditionalInfo: "",
        Warnings:
          "• To connect your account, you need to fill in your username and your phone number<linebreak/> • Returning from the bank app back to {{appName}} must be done manually<linebreak/> • A confirmed payment will only be made after the user returns to {{appName}}!",
        HowToConnect:
          "To connect your account, you need to enter your username, which you use to log in to your internet banking. <bold>After confirming the payment, you must return from the bank app back to {{appName}} - otherwise the payment will not be made!</bold> If you use both the KB+ app and the KB Key app (corporate banking), you will receive two notifications - you only need to confirm the requested action (e.g. payment) in either of them.",
        Review:
          "We currently rate the API connection from Komerční banky as user-unfriendly and almost non-functional. To connect your account, the user must enter their username and then their phone number, which may be annoying for many users. For one action, two notifications appear simultaneously (from the KB+ app and the KB Key app), which is very confusing. There is no automatic redirection back to {{appName}}. If the user does not return from the bank app (KB+ or KB Key) manually back to {{appName}}, the confirmed payment will not be made, which we consider a major obstacle - we have reported the problem and are doing our best to have it removed by the bank.",
        Email: "ombudsman@kb.cz",
        NotRecommended: {
          Reason: "Significant violation of CNB regulation.",
          WarningText:
            "You are trying to connect Komerční banka, which currently violates the regulation of the Czech National Bank.<linebreak/><linebreak/>Connecting this bank will significantly complicate payments via {{appName}}. Please consider connecting a different bank.",
          RiskItButton: "I'LL RISK IT WITH KB",
        },
      },
      [Bank.AirBank]: {
        Rating: "2.5",
        AdditionalInfo: "",
        Warnings:
          "• The website is not responsive<linebreak/> • Delivering push notifications from the MyAir app sometimes takes longer <linebreak/> • After connecting your account, uncheck all confirmations in MyAir<linebreak/> • Returning from the bank app back to {{appName}} must be done manually (iOS)",
        HowToConnect:
          "To connect your account, click on the black button “Login via app”, which will take you directly to the MyAir app. If you don't receive a push notification, open MyAir directly from your phone menu and confirm the requested action (e.g. payment) there. Due to the unresponsive website of the bank, it may happen that you get lost for a moment - we recommend moving your finger on the screen, zooming in and finding the correct field or button. After connecting your account/payment, click the back arrow to return from MyAir back to {{appName}} (applies to iPhone).",
        Review:
          "We rate the API connection from Air Bank as one of the best. To connect your account, you don't need to enter your username or password. We consider the negative aspect of the unresponsive website and the problem with occasional late delivery of push notifications. The redirection back to {{appName}} is accompanied by unnecessary 5-second wait. There is no automatic redirection back to {{appName}} (applies to iPhone).",
        Email: "info@airbank.cz",
      },
      [Bank.UCB]: {
        Rating: "1.5",
        AdditionalInfo: "",
        Warnings:
          "• The SmartBanking app is very slow<linebreak/> • The bank repeatedly asks for permission to use cookies<linebreak/> • The bank asks you before every payment whether you are on a mobile phone or a computer",
        HowToConnect:
          "The app is slow, wait and don't hurry - the payment will be completed after a while. Before every payment, you must confirm whether you are on a computer (Online Banking), a mobile phone (SmartBanking) or whether you are a corporate customer (Corporate). If the bank asks you for permission to use cookies, you must uncheck it.",
        Review:
          "We rate the API connection from UniCredit Bank as average. The SmartBanking app is very slow, which unreasonably prolongs the entire payment process. We negatively rate the annoying requests for permission to use cookies and the constant requests, whether the user is currently connecting from a mobile phone, a computer or whether they are a corporate customer. The balance of the account is updated only 4 times a day. On the positive side, we rate the connection of the account directly via the SmartBanking app without the need to know the username or password, and the automatic redirection from the bank app back to {{appName}}.",
        Email: "info@unicreditgroup.cz",
      },
      [Bank.MONETA]: {
        Rating: "4.75",
        AdditionalInfo: "",
        Warnings:
          "Nothing major. If you run into an error in the flow, let us know – MONETA actively communicates with us and together we will try to resolve the issue.",
        HowToConnect:
          "Connecting your MONETA bank account takes one strong authentication and one click; after confirmation you are automatically redirected from Smart Banka back to {{appName}}. Payments are just as smooth – you confirm the payment in Smart Banka and the app returns you to {{appName}} on its own.",
        Review:
          "The MONETA Money Bank API connection underwent significant improvement in 2026 and is now among the best on the Czech market. Both account connection and payment work with one strong authentication and one tap, without unnecessary logins and with automatic return to {{appName}}. From a user-experience perspective, it is a proper implementation compliant with regulation and with the Czech National Bank's interpretative opinion RS 2025-28 of 27 November 2025. We also appreciate that MONETA responded to our feedback and delivered the promised payment-flow fix. If you still discover an error, please report it to us – the bank communicates with us and will try to remove it.",
        Email: "ombudsmanproklienty@moneta.cz",
      },
      [Bank.FIO]: {
        Rating: "4.25",
        AdditionalInfo: "",
        Warnings:
          'After connecting your account and after confirming a payment, you need to tap the "Done" button – only then are you returned to {{appName}}. The payment status loads in {{appName}} with a slight delay – please wait a moment, the payment is in progress.',
        HowToConnect:
          'You confirm both the account connection and the payment directly in the Fio banka app, to which {{appName}} redirects you automatically. After confirming, tap "Done" and you will be redirected back to {{appName}}.',
        Review:
          'We rate the Fio banka API connection very positively, on both Android and iOS. Connecting an account no longer requires entering a username and password, and the redirection directly to the Fio banka app and back works reliably on both platforms. Compared with the Czech National Bank\'s interpretative opinion RS 2025-28 of 27 November 2025, one extra click remains in the flow – confirmation via the "Done" button – and the payment status loads in {{appName}} a bit more slowly than we would like. These are minor details that separate Fio banka from the highest rating. If you discover an error in the flow, please report it to us.',
        Email: "fio@fio.cz",
      },
      [Bank.MBank]: {
        Rating: "0.5",
        AdditionalInfo: "",
        Warnings:
          "• To connect an account and make a payment, you need to enter your numeric Identifier and password<linebreak/> • Logging into the mBank mobile app is always required before making a payment<linebreak/> • Returning from the mBank mobile app back to {{appName}} must be done manually",
        HowToConnect:
          "To connect your account, you need to know your numeric Identifier and password. If you don't have them, you must create them in mBank's internet banking. After confirming login in the mBank mobile app, return to the browser and select the bank account you want to connect to {{appName}}. Before confirming a payment, you must log in again to the mBank mobile app, so payments with this bank will practically always take a bit longer than with the competition.",
        Review:
          'The mBank API connection shows a number of obstacles that lower our rating to just half a star. Connecting a bank account to mBank is made unpleasant by the need to know the numeric Identifier and password. We negatively rate the forced login to the mBank app before making a payment, which disproportionately prolongs the payment process. There is no automatic redirection back to the {{appName}} environment - if you don\'t want to wait 15 seconds for the redirect, you must click the "I understand" button.',
        Email: "kontakt@mBank.cz",
      },
      [Bank.RB]: {
        Rating: "2",
        AdditionalInfo: "",
        Warnings:
          "• To connect an account, you need to enter your client number<linebreak/> • Returning from payment confirmation back to {{appName}} must be done manually",
        HowToConnect:
          'To connect an account, you must enter your client number that you use to log into internet banking. When confirming payment, you must first click on the yellow "Sign" button on the bank\'s website, only then will the app open. After confirming the payment, click to return back to {{appName}} first on the back arrow and then on the "Redirect back to app" button.',
        Review:
          "We rate Raiffeisenbank's API connection above average among the current competition. We appreciate the payment speed and stability of push notification delivery. The payment process is slowed down by double payment confirmation - once on the Raiffeisenbank website by clicking the yellow \"Sign\" button and the second time in the bank's app. Automatic redirection back to the {{appName}} environment after payment execution is missing - you must click your way back to Mo.one, otherwise the merchant will not see that the payment was actually made.",
        Email: "reklamace@rb.cz",
      },
      [Bank.Citfin]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.TRINITY]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.PenezniDum]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.Artesa]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.CREDITAS]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.NEY]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.MaxBanka]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.NRB]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.JandT]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.PPF]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.Oberbank]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.PartnersBank]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.CNB]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.Revolut]: {
        Rating: "",
        AdditionalInfo: "",
        Warnings: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
      [Bank.CZKT]: {
        Rating: "",
        Warnings: "",
        AdditionalInfo: "",
        HowToConnect: "",
        Review: "",
        Email: "",
      },
    },
    NotRecommended: {
      Badge: "NOT RECOMMENDED",
      InfoboxTitle: "NOT RECOMMENDED",
      BetterButton: "I WANT SOMETHING BETTER",
    },
    Title: "Bank Connection Rating",
    Description:
      "We bring merchants and customers simple and convenient instant payments in physical stores and online. We constantly improve our system and functionality.<linebreak/> However, the experience of paying with {{appName}} is directly influenced by the quality of the connection to the technology provided by the individual banks. We recommend that you pre-study and test the limitations that you may encounter when connecting your account or making a payment. Together with the banks, we are working on removing the individual barriers so that the payment experience is truly great by the end of 2026.<linebreak/> We have introduced a rating system, thanks to which you can see how your bank is doing. Our rating is called “Miss API” and serves as a positive incentive for banks to improve their technology and your experience of paying with {{appName}}. If you are not satisfied with the quality of your bank's connection, we recommend using another bank with a better rating. You can open an account with another bank today in a few minutes online from your phone or computer.",
    AccessibilityLabel: "",
    ContactUs: {
      PreText:
        "If you encounter any other limitations that are not mentioned for your bank, please let us know at",
      Email: "info@mo.one",
      PostText: "We appreciate your feedback!",
    },
    Detail: {
      CurrentRating: "Current rating: {{rating}}/5.00",
      Titles: {
        Warning: "Warning:",
        HowToConnect: "How to connect:",
        Review: "Rating:",
        Email:
          "If you want things to change, contact the ombudsman of your bank at",
      },
    },
  },
  Transfer: {
    FromAccount: "Select source account",
    ToAccount: "Select destination account",
    Amount: "Enter amount",
    Note: "Add note",
    SubmitButton: "Transfer",
    TransferButton: "Transfer money",
    ConnectMoreAccounts: "Connect another account",
    NoBankAccountsError: "Failed to load bank accounts.",
  },
  Merchant: {
    Employees: {
      Title: "Employees",
    },
    LicenseShares: {
      Title: "License Shares",
    },
    Payment: {
      LoadingError: "The data couldn't be loaded",
      Title: "Today's Revenue",
      InitiatePaymentButton: "Initiate Payment",
      InitiateKeypadPaymentButton: "PAYMENT {{amount}}",
    },
    Initiate: {
      PaypointHasNoAccountError:
        "The selected paypoint cannot be used for payments because it has no bank account assigned.",
      NoBankAccountsError: "Error, we couldn't load your bank accounts.",
      ActiveTransactionConflict: {
        Title: "Transactions Conflict",
        Message: "There is already an active transaction on this paypoint.",
        Description:
          "You can either return back and initiate the transaction on another paypoint, or overwrite the existing transaction on this paypoint.",
        OverwriteButton: "Overwrite Existing",
        Overwriting: "Overwriting...",
      },
      ConnectBankAccountLink: {
        PreText: "You can start using the terminal after",
        LinkText: "Setting up your primary account.",
      },
      GenerateButton: "Generate Transaction Data",
      Form: {
        BusinessAccountSelectTitle: "Select Business Account",
        PersonalAccountSelectTitle: "Select Account for incoming payment",
      },
    },
    InitiateDetail: {
      MerchantInstructions:
        'You can pay by scanning the QR code, or by entering the transaction code in the "Pay" form.',
      CustomerInstructions:
        "Show the unique QR code for payment to the payer, or share the 3-digit transaction ID with them.",
      QRInstructions:
        "You can make the payment by scanning the QR code or by sharing the payment code.",
      QRCodeInstructionsSuccess:
        "To view the payment details, load <newline/> the QR code on your phone.",
      QRCodeWarning:
        "DO NOT USE the reader in your banking app! Open {{appName}} by scanning the QR code with your camera or QR reader.",
      UsePaypointWarning:
        "No PayPoint is set for the selected payment. Please set up a PayPoint to improve the {{appName}} payment process.",
      PaymentStatus: "Payment Status",
      TransactionFee: "Transaction Fee",
      ProgressHeader: {
        [TransactionStatus.Created]: "Waiting for payment",
        [TransactionStatus.Initiated]: "Initiated",
        [TransactionStatus.Processing]: "Processing",
        [TransactionStatus.Success]: "Successfully received",
        [TransactionStatus.Cancelled]: "Cancelled",
        [TransactionStatus.Fail]: "Failed",
        [TransactionStatus.InvestigationNeeded]: "Unknown status",
      },
      ShareTitle: "{{appName}} payment",
      ShareText:
        "{{name}} is calling you! The amount {{amount}} is ready to go. Click and send it to the destination! Thanks for paying with {{appName}}!",
      ShareReceiptTitle: "Payment Receipt - {{appName}}",
      ShareReceiptText:
        "Payment Receipt\n\nTransaction ID: {{transactionId}}\nAmount: {{amount}}\nNote: {{note}}\nReference: {{reference}}\nAccount/IBAN: {{iban}}\n\nThanks for using {{appName}}!",
      ActionButtons: {
        Share: "Share",
        Info: "Info",
        Qr: "QR {{appName}}",
      },
      NFCInstruction: "Scan NFC and complete payment on your phone.",
      Error: "An error occurred while loading the payment instruction",
      Loading: "Preparing payment...",
      CreateAnotherPayment: "Create another payment",
      BackToOverview: "Back to overview",
      ThankYouMessage: "Thank you for paying with {{ appName }}!",
    },
    MerchantStatus: {
      InDraft: "Registration started",
      InvitedWaitingForConfirmation: "Invitation sent",
      Onboarding: "Onboarding",
      WaitingForPaymentTerminal: "Waiting for payment terminal",
      Active: "Active",
      BlockedFinancialServices: "Blocked",
    },
    NoProductAssigned: "You have no product assigned.",
    NoPaypointProduct:
      "You have no product assigned that allows creating a paypoint.",
    DataFailedToLoad: "Failed to load data.",
    PaymentDetails: "Payment details:",
    AccountNumber: "Account number:",
    VariableSymbol: "Variable symbol:",
    IBAN: "IBAN:",
    PaymentReference: "Payment reference:",
    Amount: "Amount:",
    Note: "Note:",
    NoAccountSet: "No account is set",
    ConnectedBankAccounts: "Connected bank accounts",
  },
  Customer: {
    Pay: {
      Title: "Pay",
      DescriptionText:
        "You can also pay by scanning the counterparty's QR code.",
      PayButton: "Pay",
      PayButtonWithAmount: "Pay {{amount}}",
      ChooseBankAccount: "Choose the account you want to pay with",
      ChooseBank: "Choose the bank you want to pay with",
      AddNextBankAccount: "+ Add another account",
      MooneLiteAccountExplanation:
        "= restricted account functionality.<newline/>To unlock all {{appName}} features, you first need to complete your registration.",
      SelectBankAccount: "Select bank account",
      TransactionIdentifier: "Transaction ID",
      Amount: "Amount",
      BillAmount: "Bill Amount",
      Tip: "Tip",
      AddTip: "Add tip",
      fivePercent: "5%",
      tenPercent: "10%",
      twentyPercent: "20%",
      custom: "Custom",
      TipContinueWithout: "Continue without tip",
      TipCustomAmountLabel: "Tip amount",
      BankAccount: "Bank account",
      QuickPayment: {
        Processing: "Processing quick payment...",
        Error: "Quick payment failed",
        QuickPaymentFailed:
          "The transaction could not be completed with quick payment.",
        UseAnotherAccount: "Use another account",
      },
      AnonymousCollector: {
        UnsupportedMode:
          "This payment link can't be used here. Please check the link and try again.",
      },
      ManualEntry: {
        Title: "Pay",
        NfcTitle: "Tap your phone on the paypoint",
        NfcSubtitle: "Contactless payment via NFC.",
        ManualTitle: "Enter the transaction ID",
        ManualTitleWithNfc: "Or enter the transaction ID manually",
        ManualSubtitle: "You'll find it at the counterparty.",
        InputPlaceholder: "e.g. A5DH8Q",
        InputLabel: "Transaction ID",
        SubmitButton: "Continue",
        InvalidId: "Please enter a valid transaction ID",
        TransactionIdNotProvided: "No transaction ID was provided.",
      },
    },
  },
  BankAccounts: {
    List: {
      Description:
        'These bank accounts are paired with your account. If you want to add another bank account, click the "Connect bank account" button.',
      NoAccounts: "You don't have any paired bank accounts.",
      SaveOrder: "Save order",
      EditOrder: "Edit order",
      AddAccount: "Connect bank account",
    },
    VerificationStatus: {
      Inactive: "Inactive",
      AwaitingVerification: "Waiting for verification",
      Rejected: "Rejected",
      Active: "Active",
      Restricted: "Lite account",
      PreApproved: "Pre-approved",
    },
    VerificationStatusDescription: {
      Active:
        "The account is fully verified and can be used for all transactions.",
      AwaitingVerification:
        "The account is being verified. It cannot be set as primary until verification is complete.",
      Restricted:
        "The account is in Lite mode with limited functionality. It cannot be set as primary.",
      Inactive: "The account is inactive and cannot be used for transactions.",
      Rejected:
        "The account verification was rejected. The account cannot be used.",
      PreApproved:
        "The account is pre-approved. You can receive account-to-account payments up to a limited amount until the account is fully verified.",
    },
    BankVerificationSource: {
      AIS: "AIS",
      QR: "Micro-payment",
    },
  },
  Form: {
    PersonalData: {
      Sex: {
        Female: "Female",
        Male: "Male",
        NotSet: "Not specified",
      },
    },
    PartyType: {
      NaturalPerson: "Natural person",
      LegalEntity: "Legal entity",
      SelfEmployed: "Self-employed person (OSVČ)",
    },
    BusinessActivity: {
      RetailAndGoodsSales: "Retail and Goods Sales",
      FoodServicesAndGastronomy: "Food Services and Gastronomy",
      AccommodationAndTourism: "Accommodation and Tourism",
      BeautyHealthAndWellness: "Beauty, Health and Wellness",
      ConstructionCraftsAndMaintenance: "Construction, Crafts and Maintenance",
      TransportAutoAndLogistics: "Transport, Auto and Logistics",
      FinancialLegalAndConsulting: "Financial, Legal and Consulting Services",
      EducationSportsAndLeisure: "Education, Sports and Leisure",
      TechnologyITAndOnline: "Technology, IT and Online Services",
      IndustryManufacturingAndWholesale:
        "Industry, Manufacturing and Wholesale",
      OtherAndPublicServices: "Other and Public Services",
      RetailAndCommerce: "Retail and Commerce",
      FoodAndBeverageServices: "Food and Beverage Services",
      AccommodationAndHospitality: "Accommodation and Hospitality",
      BeautyAndPersonalCare: "Beauty and Personal Care",
      HealthcareAndPersonalCare: "Healthcare and Personal Care",
      ConstructionAndMaintenance: "Construction and Maintenance",
      TransportationAndLogistics: "Transportation and Logistics",
      FinancialServicesAndInsurance: "Financial Services and Insurance",
      RealEstateAndPropertyServices: "Real Estate and Property Services",
      LegalAndConsultingServices: "Legal and Consulting Services",
      EducationAndTraining: "Education and Training",
      ArtsCultureAndEntertainment: "Arts, Culture and Entertainment",
      SportsAndRecreation: "Sports and Recreation",
      TechnologyAndITServices: "Technology and IT Services",
      ECommerceAndOnlineServices: "E-Commerce and Online Services",
      IndustrialProductionAndProcessing: "Industrial Production and Processing",
      Other: "Other",
      PersonalServices: "Personal Services",
    },
    SourceOfIncome: {
      GoodsSalesRevenue: "Goods Sales Revenue",
      ServiceAndCraftIncome: "Service and Craft Income",
      HospitalityAndAccommodation: "Hospitality and Accommodation",
      CommissionsAndLicensing: "Commissions and Licensing",
      PropertyAndInvestmentIncome: "Property and Investment Income",
      PublicBudgetsAndGrants: "Public Budgets and Grants",
      OtherBusinessIncome: "Other Business Income",
      Employment: "Employment",
      SelfEmployment: "Self-employment",
      Other: "Other",
    },
    SourceOfIncomeHint: {
      GoodsSalesRevenue:
        "Retail, wholesale, market stalls, digital products, e-commerce.",
      ServiceAndCraftIncome:
        "All services from hairdressers to plumbers to IT, consulting and marketing.",
      HospitalityAndAccommodation:
        "Restaurants, bars, cafés, hotels, guesthouses and short-term rentals.",
      CommissionsAndLicensing:
        "Real estate agents, consultants, affiliate partners, copyrights and patents.",
      PropertyAndInvestmentIncome:
        "Long-term property rentals, dividends, profit shares and holding activities.",
      PublicBudgetsAndGrants:
        "Non-profit income, state subsidies, churches and foundation collections.",
      OtherBusinessIncome:
        "Specific cases that do not fall into the above categories.",
    },
    ExpectedMonthlyRevenue: {
      LessThan100K: "Less than 100,000 CZK",
      From100KTo500K: "100,000 to 500,000 CZK",
      From500KTo1M: "500,000 to 1,000,000 CZK",
      Over1M: "More than 1,000,000 CZK",
    },
    AverageTransactionValue: {
      LessThan1K: "Less than 1,000 CZK",
      From1KTo10K: "1,000 to 10,000 CZK",
      From10KTo50K: "10,000 to 50,000 CZK",
      Over50K: "More than 50,000 CZK",
    },
    RelationType: {
      BeneficialOwner: "Beneficial owner",
      ProxyHolder: "Proxy holder",
      HuntersHunter: "Hunter's hunter",
      BranchManager: "Branch manager",
      Mayor: "Mayor",
      Governor: "Governor",
      CityMayor: "City mayor",
      ContactPerson: "Contact person",
      HuntersMerchant: "Hunter's merchant",
      MerchantsEmployee: "Merchant's employee",
      HuntersCustomer: "Hunter's customer",
      CustomersCustomer: "Customer's customer",
      MerchantsCustomer: "Merchant's customer",
      StatutoryBody: "Statutory body",
      InsolvencyAdministrator: "Insolvency administrator",
      Liquidator: "Liquidator",
      ForcedAdministrator: "Forced administrator",
      BranchOfficeManager: "Branch office manager",
      ManagingDirector: "Managing director",
      BoardChairman: "Board chairman",
      BoardMember: "Board member",
      SupervisoryBoardChairman: "Supervisory board chairman",
      SupervisoryBoardMember: "Supervisory board member",
      Partner: "Partner",
      GeneralPartner: "General partner",
      Chairman: "Chairman",
      CommitteeMember: "Committee member",
      TrustManager: "Trust manager",
    },
    RoleCategory: {
      StatutoryBody: "Statutory body",
      StatutoryBodyHint:
        "A statutory body member is mainly a managing director, board member/chairman, supervisory board member/chairman",
      ProxyHolder: "Proxy holder",
      Other: "Other",
      SpecificRole: "Specific role",
    },
    BankVerification: {
      NotVerified: "Not verified",
      Verified: "Verified",
      InProgress: "Verification in progress",
    },
    BankVerificationSource: {
      AIS: "AIS",
      QR: "Micro-payment",
    },
    IdentityVerification: {
      NotVerified: "Not verified",
      Verified: "Verified",
      Processing: "Verification in progress",
      ToVerify: "To verify",
      Expired: "Expired",
    },
    AdditionalInfo: {
      NotVerified: "Not verified",
      Verified: "Verified",
      NeedsInvestigation: "Requires investigation",
    },
    UserStatus: {
      Unknown: "Unknown",
      InvitedWaitingForConfirmation: "Invitation sent",
      Active: "Active",
      WaitingForEmailConfirmation: "Waiting for e-mail confirmation",
      Deactivated: "Deactivated",
      BlockedFinances: "Blocked",
      Anonymous: "Anonymous",
      Onboarding: "Onboarding",
      EmailConfirmed: "E-mail confirmed",
      RoleSelection: "SSO role selection",
    },
    IdentityUserStatus: {
      Active: "Active",
      Pending: "Awaiting acceptance",
      Suspended: "Suspended",
      Deactivated: "Revoked",
      Onboarding: "Onboarding",
    },
    AddressType: {
      Permanent: "Permanent residence",
      Mail: "Correspondence",
      Headquarters: "Headquarters",
    },
    Version: {
      MooneLite: "{{appName}} Lite",
      FullMoone: "Full {{appName}}",
    },
  },
  FormSelectors: {
    BusinessForm: {
      Title: "Business form",
    },
  },
  User: {
    AccessRole: {
      Admin: "Administrator",
      Customer: "User",
      Employee: "Employee",
      Merchant: "Merchant",
      Hunter: "Hunter",
      Aml: "AML",
      HunterPlus: "Hunter+",
      CustomerOnboarding: "Customer Onboarding",
      AdminLite: "Admin (read-only)",
      Disponent: "Disponent",
      Child: "Child",
    },
    RoleFilter: {
      CustomerOnly: "User (no other roles)",
    },
  },
  Hunter: {
    Dashboard: {
      UnderConstruction:
        "The hunter dashboard, leaderboards, academy, and more are coming soon...",
      MissingLicenseHolderBankAccount:
        "To receive rewards, connect a bank account owned by the franchise license holder.",
      HunterPortalBanner: {
        Title: "Hunter Portal",
        Description:
          "Terminals, acquisition, events, academy and more in one place.",
      },
      Prerequisites: {
        Title: "Hunter first steps",
        Description:
          "Become a sales expert on Mo.one in a few steps and master sales practices.",
        Completed: "Completed",
        GreatWork: "Great job!",
        InfoMessage:
          "To get full access to Hunter features, you first need to get familiar with the basics of Mo.one. Complete the task list and unlock Full Hunter!",
        CompletedMessage:
          "All done! Sending invitations to merchants has been enabled.",
        Items: {
          RegisterAsMerchant:
            "Register as a Merchant and explore the application",
          InviteUserViaAffiliate: "Invite a new user via Thanks to You!",
          BanksAndPayments: "Connect at least 3 banks and make 30 payments",
          OfflineTraining: "Attend",
          OfflineTrainingLink: "offline Mo.one training",
          OfflineTrainingUrl: "https://events.legi.one",
          BringMerchantsWithHunter:
            "Bring 3 Merchants in cooperation with an experienced Hunter",
        },
      },
    },
    LicensePaymentPending: {
      Title: "Your license is not active yet",
      ProcessingInfo:
        "You currently don't have an active license. If you have already sent the payment, please wait for it to be processed and your license activated — a bank transfer may take 1–2 business days.",
      NotPaidYetLink: "Haven't sent the payment yet?",
      NotPaidYetSubtitle: "Show payment details",
      PaymentDetailsTitle: "Payment details",
      NoLicenseTitle: "You have no license",
      NoLicenseInfo:
        "You currently have no active license. To enter the {{appName}} Hunter section, order a license first.",
      OrderLicense: "Order a license",
      OrderAnotherLicense: "Order another license",
      SelectedLicenseLabel: "Ordered license",
      BackToMoone: "Back to {{appName}}",
    },
    Merchants: {
      Title: "Merchants",
      NewRegistration: {
        Title: "New merchant",
        StartRegistrationButton: "Initiate registration",
        StartRegistrationError: "Error initiating registration",
        StartRegistrationSuccess: "Registration initiated",
        CreateMerchantError: "Error creating merchant",
        Form: {
          Legend: {
            License: "License selection",
            CommissionSharing: "Commission sharing",
          },
          Fields: {
            License: "License",
            Country: "Country",
            Region: "Country",
            CompanyNumber: "Company number",
            ConfirmationLabel:
              "I confirm that I have personally spoken with the owner of the company or the person responsible for the payment service in the company.",
            RemoveHunter: "Remove hunter",
            CommissionSettings: "Commission settings",
            ShareCommissionWithHunters: "Share commission with other Hunters",
            ShareCommissionWithHuntersButton: " + Share with other Hunters",
          },
          NoLicenseForRegion:
            "You don't have an active license for this market. Pick a different country or get a license first.",
          StatusIndicator: {
            Loading: "Checking availability",
            EnterCompanyNumber: "Enter the company number for validation.",
            CompanyAvailable:
              "Great! {{company}} is not using {{appName}} yet.",
            CompanyNotAvailable:
              "We're sorry, {{company}} is already using {{appName}}, or its registration is being processed.",
            CompanyNotFound:
              "The entered company number was not found in the business register.",
          },
        },
      },
      AddNew: "Add new merchant",
      OpenRegistrations: {
        Table: {
          Title: "Potential merchants",
          Columns: {
            CompanyName: "Company name",
            CompanyNumber: "Company number",
            Phone: "Phone",
            Email: "E-mail",
          },
        },
        Title: "Open registrations",
        LoadingError: "Error - Could not load open registrations",
        RegisterNewMerchant: "Register new merchant",
      },
      ListPage: {
        LoadingError: "Error - Could not load merchants list",
        LicenseError: "Error - Could not load license data",
        LicenseNotActive:
          "Your license is not active. Once it is activated, you will see the list of merchants here.",
        ShowLicenses: "Show licenses",
        MerchantsTitle: "Merchants",
        SharedMerchantsTitle: "Merchants with shared commissions",
        Table: {
          Columns: {
            MerchantStatus: "Merchant status",
            ContactPerson: "Contact person",
            Party: {
              CompanyName: "Company name",
              Phone: "Phone",
              Email: "E-mail",
              SettlementStatus: "Settlement status",
            },
            LicenseNumber: "License",
            SharePercentage: "Share %",
            TransactionsCount: "Transactions count",
            DaysRegistered: "Days registered",
          },
        },
        SharedMerchantsError:
          "Error - Could not load merchants with shared commissions",
      },
      CompleteRegistration: {
        MerchantCompany: {
          LoadingError: "Error - Could not load merchant data",
        },
        ContactPerson: {
          Title: "Contact person",
          OptionalTitle: "Optional contact persons",
          OptionalNoItems: "No contact persons added",
        },
        Facilities: {
          RequiredInfo: "Please fill in facility details",
          ContinueWithoutFacility: "Continue without facility",
        },
        SendAccess: {
          SendAccessError:
            "Error while attempting to send registration instructions",
          SendAccessSuccess:
            "Instructions for merchant registration have been sent",
          Title: "Summary",
          MandatoryStepsTitle:
            "Before sending access, all the following steps must be completed:",
          Login: "Merchant login",
          LoginDescription:
            "This is the e-mail that merchant will use to login to {{appName}}.",
          SendAccessButton: "Send access",
          BackOfficeTarget:
            "This e-mail address belongs to an internal {{appName}} account (Admin, AML, onboarding) and cannot be invited. Use a different e-mail address.",
          MerchantAlreadyExists: "Merchant with this e-mail already exists",
          MerchantAlreadyHasAccessToThisCompany:
            "This user already has merchant access to this company.",
          ExistingIdentityHint:
            "This e-mail is already in {{appName}}. The invitation will appear in their in-app inbox — no e-mail will be sent.",
          NewIdentityHint:
            "We'll send an activation e-mail with a link to set the password.",
          CompanyNumberAlreadyRegistered:
            "Merchant with company number {{companyNumber}} is already registered in {{appName}}.",
          TestMerchantBannerTitle: "Trial registration",
          TestMerchantBanner:
            "Before completing all first steps, you have one trial merchant registration available so you can try the app from the merchant's perspective.",
          Todos: {
            BasicInfo: "Filling in the general data of the merchant",
            BeneficialOwners: "Adding beneficial owners of the company",
            ContactPerson: "Adding a contact person for settlement",
            Facilities: "Adding a facility",
            Pricing: "Setting up pricing for all selected products",
            HunterPrerequisites:
              "Completing all items from Hunter's first steps list",
          },
        },
        SaveMerchantPartyError: "Error saving merchant party",
        SaveMerchantPartySuccess: "Merchant party data saved",
        GeneralTitle: "Merchant registration",
        Title: "Registration {{company}} ",
        MerchantError: "Error - Merchant not selected or doesn't exist",
        LoadingError: "Error - Could not load merchant data",
        Steps: {
          notes: "Notes",
          party: "Merchant",
          facilities: "Facilities",
          company: "Company",
          products: "Products",
          fees: "Fees",
          completion: "Completion",
        },
        NextStep: "Next",
        PreviousStep: "Previous",
        Notes: {
          SaveSuccess: "Notes saved",
          Title: "Notes",
          Label: "Your notes about the merchant",
          SaveError: "Error saving notes",
          NoNotes: "No notes...",
        },
      },
      Detail: {
        ActivateButton: "Activate merchant",
        ActivateSuccess: "Merchant activated.",
        ActivateError: "Failed to activate the merchant.",
        ResendAccessButton: "Re-send invitation",
        ResendAccessSuccess: "Access e-mail sent successfully",
        ResendAccessSuccessWithUser:
          "Invitation re-sent to <bold>{{userLogin}}</bold>",
        ResendAccessError: "Error sending access e-mail",
        CancelAccessButton: "Cancel invitation",
        CancelAccessConfirmTitle: "Cancel the invitation?",
        CancelAccessConfirmBody:
          "This withdraws the pending invitation for {{userLogin}}. You can send a new invitation afterwards.",
        CancelAccessSuccess: "Invitation cancelled",
        CancelAccessError: "Error cancelling invitation",
        CancelAccessForbidden:
          "Only the inviting hunter or an administrator can cancel this invitation.",
        Tabs: {
          Merchant: "Merchant",
          Products: "Products",
          TerminalContracts: "Terminal drafts ",
          Paypoints: "Paypoints",
          Settlements: "Settlements",
        },
      },
    },
    AllMerchants: {
      LoadingError: "Error - Failed to load merchants",
      OwnedByMe: "My Merchant",
      TableHeaders: {
        Status: "Merchant Status",
        CompanyNumber: "Company Number",
        CompanyName: "Company Name",
        LicenseOwner: "License Owner",
        LicenseType: "License Type",
        DateCreated: "Date Created",
        TransactionsCount: "Transactions Count",
      },
    },
    UnassignedMerchants: {
      Description:
        "Merchants that registered themselves and have no hunter assigned. Click a row to assign a hunter and set commission shares.",
      LoadingError: "Error - Failed to load merchants without a hunter",
      EmptyState:
        "No merchants without a hunter. Every merchant has a hunter assigned.",
      OpenDetailLabel: "Open merchant detail",
      TableHeaders: {
        Status: "Merchant Status",
        CompanyNumber: "Company Number",
        CompanyName: "Company Name",
        DateCreated: "Date Created",
        Detail: "Detail",
      },
      AssignDialog: {
        Title: "Assign hunter for",
        Subtitle: "{{companyName}} (Company No: {{companyNumber}})",
        PrimaryLicenseLabel: "Primary license",
        MainCommission: "Main commission",
        ShareLegend: "Share commission with other licenses",
        ShareCommissionWithHuntersButton: "Share commission with hunters",
        AssignButton: "Confirm assignment",
        AssignWarning:
          "After assignment, hunters and commissions can no longer be modified",
        CancelButton: "Cancel",
        AssignSuccess: "Hunter assigned successfully.",
        AlreadyHasHunterError: "This merchant already has a hunter assigned.",
        GenericError: "Failed to assign the hunter.",
        Validation: {
          PrimaryRequired: "Primary license is required.",
          ShareOutOfRange:
            "Shared commission must be greater than 0% and less than 100%.",
          PrimaryUnderMin:
            "The primary hunter must keep at least 1% commission.",
        },
      },
    },
    HuntersOverview: {
      LoadingError: "Error - Failed to load hunters",
      Detail: {
        UserInfo: "User Information",
        PartyInfo: "Party Information",
        ContactInfo: "Contact Information",
        Login: "Login",
        Status: "Status",
        FirstName: "First Name",
        LastName: "Last Name",
        BirthDate: "Birth Date",
        CompanyName: "Company Name",
        CompanyNumber: "Company Number",
        TaxNumber: "Tax Number",
        Email: "Email",
        Phone: "Phone",
        Licenses: "Licenses",
        NoLicensesFound: "No licenses found",
        AffiliatedTo: "Affiliated To",
        EditUser: "Edit",
        DateCreated: "Date Created",
        PartyType: "Party Type",
      },
      TableHeaders: {
        Login: "Login",
        FirstName: "First Name",
        LastName: "Last Name",
        CompanyName: "Company Name",
        DateCreated: "Date Created",
        Status: "Status",
        AffiliatedTo: "Affiliated To",
        Prerequisites: "Prerequisites",
      },
    },
  },
  ApiStatuses: {
    UnknownError: "An unexpected error occurred while processing the request",
    UploadIdentificationCommandResultStatus: {
      [UploadIdentificationCommandResultStatus.Success]:
        "Successfully uploaded",
      [UploadIdentificationCommandResultStatus.MissingFiles]:
        "Please upload all files",
    },
    UploadIdentificationCommandResultStatus2: {
      [UploadIdentificationCommandResultStatus2.Success]:
        "Successfully uploaded",
      [UploadIdentificationCommandResultStatus2.MissingFiles]:
        "Please upload all files",
      [UploadIdentificationCommandResultStatus2.VerificationAttemptsExhausted]:
        "The automated document check cannot be repeated; please contact support.",
    },
  },
  MobileApp: {
    InstallBanner: {
      Title: "Get the {{appName}} app",
      Description: "Faster access, right from your phone's home screen.",
      AppStoreBadgeAlt: "Download on the App Store",
      PlayStoreBadgeAlt: "Get it on Google Play",
    },
  },
  Language: {
    Czech: "Czech",
    Language: "Application language",
    Settings: "Language",
  },
  System: {
    Addresses: "Addresses",
    BlockchainFee: "Blockchain fee",
    MempoolFeeLevel: "Mempool fee level",
    Exchange: "Exchange",
    Trades: "Trades",
    Moone: "{{appName}}",
    HotWallet: "+ To Hot Wallet",
    ColdWallet: "+ To Cold Wallet",
    ExchangeWithdrawal: " + To exchange withdrawal ",
    UserAccounts: "- To user accounts",
    ControlSum: "Control sum",
    ExchangeWithdrawalFee: "Exchange withdrawal fee",
    TradingVolume: "Trading volume",
    PerMonth: "Per month",
    PerWeek: "Per week",
    Per24h: "Per 24h",
    PurchasedBTC: "Purchased BTC",
    WithdrawnOrPending: "Withdrawn or pending",
    BTCLedgerSum: "BTC ledger sum",
    CZKLedgerSum: "CZK ledger sum",
  },
  Error: {
    Page403: "Error403",
    Page404: "Error404",
    Unexpected: "An unexpected error occurred",
    NoAccess: "You do not have access to this page.",
    OpenRegistrationsFailed: "Failed to load open registrations.",
    MerchantNotSelected: "Error - Merchant is not selected or does not exist",
    LicenseInfoFailed: "Failed to load information about your license.",
    InvalidPaypointId: "Invalid or missing paypoint ID",
    SomethingWentWrong: "Something went wrong, please try again later.",
    InvalidUserRole: "Error, invalid user role or merchant",
    GoogleLoginFailed: "Error during Google login",
    Error: "Error",
    ProgrammerHint: "Are you a programmer? Look at what the error was.",
    AccountsFailed: "Error - Failed to load your accounts",
    UserDataFailed: "Error, failed to load user data",
    VerificationDataFailed: "Error, failed to load verification data",
    DocumentsFailed: "Error, failed to load documents",
    PersonDataFailed: "Person data failed to load.",
    IncomeDataFailed: "Data failed to load.",
    FormPreparationFailed: "An error occurred while preparing the form",
    RelatedPartyDataFailed: "Error loading related party data.",
    MerchantFacilitiesFailed: "Failed to load facility data.",
    UserFailed: "Error, failed to load user",
    ActingPersonFailed: "Error, failed to load acting person",
    DocumentsFailedToLoad: "Error, failed to load documents",
    AffiliateCodeFailed: "Failed to get your affiliate code.",
    NoInvitationsSent: "You haven't sent any invitations yet.",
    QRPaymentFailed: "Sorry, but something went wrong, please try again later.",
  },
  Dashboard: {
    EmptyForRole: "Your role does not have a dashboard page assigned.",
    NoAccess: "You do not have access to this page.",
    NoAccessRetry:
      "Try to <signInLink>sign in</signInLink> again. If the problem persists, please contact our support.",
    AlreadyHaveAccount: "Already have a {{appName}} account?",
    IncomeTypeQuestion: "What is your income type?",
    Back: "BACK",
    Loading: "Loading...",
  },
  Auth: {
    Redirecting: "Redirecting",
    Error: "Error",
    StateSync: {
      LoggedInFromAnotherTab: "You have been logged in from another tab.",
      LoggedOutFromAnotherTab: "You have been logged out in another tab.",
    },
    AuthPageLayout: {
      DividerText: "or",
      SignInLinkText: "Sign in",
      SignUpLinkText: "Sign up",
      SignInPrompt: "Already have an account?",
      SignUpPrompt: "Don't have an account yet?",
    },
    SSO: {
      CompleteSSORegistration: {
        Welcome: "Welcome to {{appName}}!",
        Title: "Complete registration",
        Description:
          "Since this is your first login, we need to find out whether you are registering with us as a User, Merchant or Hunter.",
        SignUpAs: "Sign up as",
        RoleDescription: {
          Merchant:
            "As a merchant, you can accept payments from your customers with lower fees and use contactless paypoints.",
          Customer:
            "As a user, you can send direct payments, pay for your purchases and collect Stardust points.",
          Hunter:
            "As a hunter, you can get a Legione license, manage your merchants, and earn commissions from their transactions",
        },
        RoleLabel: {
          Merchant: "Merchant",
          Customer: "User",
          Hunter: "Hunter",
        },
      },
      Google: {
        ButtonLabel: "Sign in with Google",
      },
      Apple: {
        ButtonLabel: "Continue with Apple",
        SignInFailed: "Apple sign-in failed. Please try again.",
        NotSupportedInApp:
          "Sign in with Apple is not supported in the app. Please use a web browser.",
      },
      BankID: {
        ButtonLabel: "Continue with BankID",
      },
    },
    Migration: {
      Title: "Link account",
      Welcome: "Link your account to an Identity",
      Description:
        "Your account must be linked to a main Identity to match the new account model. Please complete one of the steps below.",
      RoleHint: "Your current account role: {{role}}",
      LinkToCustomerOption: "I already have a Customer account — link",
      CreateIdentityOption: "Create a new Identity from this account",
      CreateIdentityDeferred:
        "Creating a new Identity is not supported for this account type — please link to an existing Identity instead.",
      LinkToCustomerTitle: "Link to an existing Customer account",
      LinkToCustomerDescription:
        "Enter the credentials of your existing Customer account. Your current role will be added under that Identity.",
      LoginLabel: "Customer account e-mail",
      PasswordLabel: "Customer account password",
      LinkButton: "Link account",
      BackButton: "Back",
      LinkError: "Linking failed. Please check your credentials.",
      LinkSsoDivider: "or",
      LinkSsoDescription:
        "Sign in with the Google or Apple account you use for your Customer account.",
      LinkSsoNoAccount:
        "There is no Customer account under this Google/Apple e-mail. Use the account with the same e-mail, or sign in with your password.",
      LinkSsoError: "Linking via Google/Apple failed. Please try again.",
      CreateError: "Creating Identity failed. Please try again.",
      AccountNotReady:
        "This account isn't ready to use — registration wasn't completed. Please contact support or your sales representative to have the account recreated.",
      EmployeeNotSupported:
        "Employee accounts cannot be linked to an Identity yet. Please contact your merchant or support.",
      BackOfficeNotSupported:
        "Internal accounts (Admin, AML, onboarding) are neither linked nor created self-service — an administrator provisions them. Please contact an administrator.",
      SignOut: "Sign out",
      MerchantCreateIdentity: {
        Title: "Create a new Identity — statutory representative",
        Description:
          "A company's Identity must derive from its statutory body. Please pick which of the statutory representatives you are, or enter your details manually.",
        CompanyLabel: "Company",
        CompanyNumber: "(Company No: {{number}})",
        UnknownCompany: "(unknown)",
        PickPersonTitle: "Statutory representatives of the company",
        AlreadyLinkedHint: "already has their own Identity",
        UnnamedPerson: "(no name)",
        StatutoryDeclaration:
          "I declare that I am the statutory body, or a member of the statutory body, of {{company}}.",
        CreateAsButton: "Create Identity as {{name}}",
        CreateAsButtonNoSelection: "Create Identity",
        ManualEntryToggle: "I'm not in the list — enter my details",
        ManualEntryToggleEmpty: "Enter details manually",
        ManualTitle: "Your personal details (statutory representative)",
        FirstNameLabel: "First name",
        LastNameLabel: "Last name",
        BirthDateLabel: "Date of birth",
        PhoneLabel: "Phone",
        SubmitManualButton: "Create Identity",
        SubmitError: "Creating Identity failed. Please try again.",
        LoadError: "Failed to load company representatives.",
        RelationTypes: {
          BeneficialOwner: "Beneficial owner",
          ProxyHolder: "Proxy holder",
          BranchManager: "Branch manager",
          BranchOfficeManager: "Branch office manager",
          ContactPerson: "Contact person",
          StatutoryBody: "Statutory body",
          Mayor: "Mayor",
          Governor: "Governor",
          CityMayor: "City mayor",
          InsolvencyAdministrator: "Insolvency administrator",
          Liquidator: "Liquidator",
          ForcedAdministrator: "Forced administrator",
        },
      },
    },
    Invitations: {
      PageTitle: "Invitations",
      PageDescription:
        "Pending invitations to take on additional roles in {{appName}}. Accept to add the role to your account, decline to dismiss.",
      EmptyState: "You don't have any pending invitations.",
      MerchantInviteTitle:
        "You've been invited to operate {{companyName}} as a Merchant.",
      MerchantInviteUnnamedCompany:
        "You've been invited to operate a Merchant account.",
      EmployeeInviteTitle:
        "You've been invited to work at {{companyName}} as an employee.",
      EmployeeBellTitle: "New company invitation",
      EmployeeBellMessage:
        "{{company}} has invited you as an employee. Confirm the invitation in the Invitations section.",
      MerchantBellTitle: "New invitation to a company",
      MerchantBellMessage:
        "You've been invited to manage {{company}}. Confirm the invitation in the Invitations section.",
      InvitedBy: "Invited by {{inviter}}",
      ExpiresAt: "Expires {{date}}",
      AcceptButton: "Accept",
      DeclineButton: "Decline",
      KycRequired:
        "Complete identity verification on your Customer account before accepting.",
      AcceptError: "Could not accept the invitation. Please try again.",
      AcceptedButSwitchFailed:
        "Invitation accepted — but we couldn't switch you to it. Reload the page, or switch context from the account menu.",
      DeclineError: "Could not decline the invitation. Please try again.",
      LoadError: "Failed to load your invitations.",
    },
    ContextSwitcher: {
      Title: "Switch role",
      AddRole: "Create role",
      Switching: "Switching…",
      PersonalAccountHint: "Personal account",
      Roles: {
        Customer: "Customer",
        Hunter: "Hunter",
        HunterPlus: "Hunter Plus",
        Merchant: "Merchant",
        Disponent: "Disponent",
        Employee: "Employee",
        Admin: "Administrator",
        Aml: "AML",
        CustomerOnboarding: "Customer onboarding",
        AdminLite: "Admin (read-only)",
        Child: "Child",
      },
    },
    ForgottenPassword: {
      Title: "Forgotten password",
      Email: "E-mail",
      ResetPasswordButton: "Reset password",
      SuccessMessage:
        "If a user with this e-mail exists, we have sent instructions for resetting the password.",
    },
    EmailVerification: {
      EmailSentTo: "An e-mail with activation link was sent to",
      Redirecting: "Redirecting",
      EmailVerification: "E-mail verification",
      EmailSent: "E-mail was sent",
      ResendEmail: "Resend e-mail",
      EmailInstructions:
        "Before continuing, it is necessary to activate the account by clicking on the link in this e-mail.",
      ResendEmailLabel: "No e-mail arrived?",
      ResendEmailText:
        "Try checking the SPAM or Bulk folder. Or have the e-mail sent again.",
    },
  },
  Account: {
    ThankYouFriendInvited:
      "Thank you. Your friend has been successfully invited.",
  },
  Components: {
    PasswordInput: {
      ShowPassword: "Show password",
      HidePassword: "Hide password",
    },
    EmployeeAutocomplete: {
      Placeholder: "Select employees",
      SelectAll: "Select all",
    },
    CategoryAutocomplete: {
      Placeholder: "Select categories",
    },
    ShareButton: {
      AccessibilityLabel: "Share",
      Error: "Sharing failed",
    },
    NoPrimaryAccountPopup: {
      Message:
        "Please select a primary account from your connected bank accounts. It will be used as the default account for your transactions.",
      Accounts: "Manage connected accounts",
    },
    LanguageSwitcher: {
      ComingSoon: "Coming soon",
    },
    CountrySelector: {
      LoadingError: "Country list is not available",
      Label: "Select the country in which you want to buy a license",
      ComingSoon: "coming soon",
      SlovakLicensesComingSoon:
        "Slovak licenses are coming soon. We're preparing them now.",
    },
    RegionRequiredModal: {
      Title: "Choose your market",
      Description:
        "Your market determines the currency and settlement you operate in. This choice cannot be changed later.",
      LoadingError: "Market list is not available",
      Error: "We couldn't set your region. Please try again.",
      Confirm: "Confirm",
    },
    LicenseOrder: {
      CreateError: "Error creating license order",
      CreateSuccess: "License order created",
      LoadingError: "Error loading license order",
      Coupon: {
        Title: "Discount coupon",
        CheckboxLabel: "I have a discount coupon",
        Label: "Coupon code",
        Placeholder: "Enter coupon code",
        Validate: "Apply",
        Applied: "Coupon applied",
        Remove: "Remove coupon",
        OriginalPrice: "Original price",
        Discount: "Discount",
        FinalPrice: "Price after discount",
        Error: {
          CouponNotFound: "Coupon not found",
          CouponExpired: "Coupon has expired",
          CouponOutOfStock: "Coupon has been used up",
          CouponInvalidType: "Coupon cannot be used for this license type",
          CouponRegionMismatch: "Coupon is not valid for the selected country",
          Unavailable: "Coupon can no longer be used",
        },
      },
    },
    LicensePayment: {
      PendingActivationInfo:
        "The license will be activated after the payment is credited. If you have already paid, please wait for the payment to be credited.",
    },
    Invoice: {
      PaymentData: "Payment data",
      AccountNumber: "Account number",
      VariableSymbol: "Variable symbol",
      SpecificSymbol: "Specific symbol",
      IBAN: "IBAN",
      Reference: "Reference number",
      Amount: "Amount",
      Note: "Note",
      MandatoryNoteDescription:
        "Note is mandatory! Without a note, the payment will not be matched",
      EmployerGuidelineButton: "Download Employer guideline",
      BulkDownload: {
        Download: "Download invoices",
        Success: "Invoices downloaded successfully",
        Error: "Failed to download invoices",
      },
    },
    RevalidateBankingAuthModal: {
      Title: "Authorization for some of your banks is about to expire",
      Description:
        "To continue using {{appName}} as you're used to, please revalidate them.",
      InvalidTitle: "Authorization for some of your banks has expired",
      InvalidDescription:
        "To restore functionality please revalidate access to the following banks.",
      InvalidPispDescription:
        "To restore payments via the following banks please revalidate access.",
      ExpiringPispTitle: "Payment authorization is about to expire",
      ExpiringPispDescription:
        "To keep paying via the following banks, please revalidate them.",
    },
    UnpaidSettlementPopup: {
      PayMonthlySettlement: "Pay monthly settlement of {{amount}}.",
      PayMonthlySettlements: "Pay monthly settlements",
      NoSettlementTransactionError:
        "Error, settlement has no transaction assigned.",
    },
    LicensePicker: {
      LoadingError:
        "We're sorry, but we couldn't load the licenses. Please try again later.",
      LicenseDescription: {
        [LicenseCode.Gold]:
          "Hunter license in Legi.one with a large physical image.",
        [LicenseCode.Silver]:
          "Hunter license in Legi.one with a medium physical image.",
        [LicenseCode.Bronze]:
          "Hunter license in Legi.one with a small physical image.",
        [LicenseCode.Standard]:
          "Hunter license in Legi.one with a digital image.",
      },
      Perks: {
        Terminals: "terminals",
        Terminals_one: "terminal",
        Terminals_other: "terminals",
        SubAccounts: "sub-accounts",
        SubAccounts_one: "sub-account",
        SubAccounts_other: "sub-accounts",
        NoSubAccounts: "No sub-accounts",
      },
    },
    BanksList: {
      DataLoadingError: "Sorry, we could not load the bank list.",
      ShowAll: "Show all ({{count}})",
      HideAll: "Hide",
    },
    LimitedAccountPopup: {
      Title: "Limited account",
      Message:
        "The information you provided needs to be verified by our employee. Once verified, we will inform you.",
    },
    DocumentExpirationWarning: {
      UploadNewDocument: "Upload new document",
      UploadSuccess: "Document uploaded successfully. Awaiting verification.",
      AdminWarningTitle: "User's document is expiring soon",
      AdminWarningMessage:
        "This user's identity document will expire on {{date}}.",
      AdminExpiredTitle: "User's document has expired",
      AdminExpiredMessage:
        "This user's identity document has expired. The user has restricted access.",
      WarningPrimaryTitle: "Your identity document is expiring soon",
      WarningPrimaryMessage: "Your identity document will expire on {{date}}.",
      ExpiredPrimaryTitle: "Your identity document has expired",
      ExpiredPrimaryMessage: "Your identity document has expired.",
      WarningSecondaryTitle: "Your secondary document is expiring soon",
      WarningSecondaryMessage:
        "Your secondary document will expire on {{date}}.",
      ExpiredSecondaryTitle: "Your secondary document has expired",
      ExpiredSecondaryMessage: "Your secondary document has expired.",
      AdminWarningSecondaryTitle: "User's secondary document is expiring soon",
      AdminWarningSecondaryMessage:
        "This user's secondary document will expire on {{date}}.",
      AdminExpiredSecondaryTitle: "User's secondary document has expired",
      AdminExpiredSecondaryMessage:
        "This user's secondary document has expired.",
      SecondDocumentValidTo: "Secondary document valid to",
      AdminNotificationExpiredTitle: "A user's document has expired",
      AdminNotificationExpiredMessage:
        "The identity document of user {{user}} has expired.",
    },
    RelatedPartyDocumentExpiration: {
      Title: "Related party documents need attention",
      WarningMessage:
        "{{name}} ({{relation}}) — identity document expires on {{date}}.",
      ExpiredMessage:
        "{{name}} ({{relation}}) — identity document has expired.",
      UpdateDocuments: "Update documents",
    },
    MooneLitePopup: {
      Title: "Lite account",
      Message:
        "Your account is operating in a limited Lite mode. To fully use {{appName}}, you need to have at least one bank account connected and complete your registration by adding your personal data and verifying your identity document.",
      BankVerificationMessage:
        "We already have your personal data and identity document. To fully use {{appName}}, connect and verify your bank account.",
      CompleteRegistrationButton: "Complete registration",
      ConnectBankAccountButton: "Connect bank account",
      LimitExceeded:
        "You have exceeded the limit for using {{appName}} Lite. To continue using {{appName}}, you need to complete your registration by adding your personal data and verifying your identity document.",
      RestrictionList: {
        NoPeerToPeerPayments: "Payments can only be sent to merchants",
        NoBalance: "You cannot see balances on your accounts",
        NoIncomingPayments: "You cannot receive payments",
        NoLoyaltyPoints: "You cannot earn Stardust loyalty points",
        MaxPaymentLimit:
          "The maximum amount of outgoing payments is 1000 EUR (or equivalent in CZK)",
        MaxTransactionsLimit:
          "If you exceed the set limit of transactions, services will be automatically suspended.",
      },
    },
    SetupChecklist: {
      Title: "Finish your registration",
      Subtitle:
        "Complete the steps below to use {{appName}} to its full potential.",
      LiteSubtitle:
        "You're on {{appName}} Lite — complete the steps below to upgrade to Full {{appName}} and use the app to its full potential.",
      MerchantTitle: "Finish your setup",
      MerchantSubtitle:
        "Complete the following steps to receive payments with {{appName}}.",
      StepsCompleted: "{{completed}} of {{total}} steps completed",
      Start: "Start",
      InReview: "In review",
      Steps: {
        ConnectBank: {
          Title: "Connect a bank account",
          Description: "Add your bank account so you can send payments.",
          Action: "Connect account",
        },
        SetPrimaryAccount: {
          Title: "Set a primary bank account",
          Description:
            "For outgoing payments, quick payments and receiving payments.",
          Action: "Choose primary account",
          BlockedDescription:
            "You'll be able to choose a primary account once your identity is verified.",
        },
        VerifyIdentity: {
          Title: "Verify your identity",
          Description: "Unlocks balances, incoming payments and Stardust",
          Action: "Verify identity",
        },
      },
    },
    Bitcoin: "Bitcoin",
    IBAN: "IBAN",
    Maximum: "maximum",
    SAT: "SAT",
    Or: "or",
    RateConfirmation: "Rate confirmation",
    Account: {
      DeactivateUser: {
        Title: "By this step you will deactivate your {{appName}} account",
        Description:
          "Your account will be deactivated and you will no longer be able to log into the account.",
        Confirmation:
          "Do you really want to deactivate the account? Deactivating your {{appName}} account will result in the loss of access to your {{appName}} account.",
        DeactivateButton: "Deactivate account",
        SuccessMessage: "Account has been successfully deactivated.",
        InfoSection: {
          Title: "What does account deactivation mean?",
          Paragraph1:
            "By deactivating your account you immediately lose access to the {{appName}} app (sign-in, payments, licences, account management) and all ongoing processes stop, including cashback payouts, settlements and affiliate commissions.",
          Paragraph2:
            "However, your data and transaction history remain in the system. Under the rules of the Czech National Bank (ČNB) and the Anti-Money Laundering Act (AML), we are obliged to archive this data for 10 years from the end of the cooperation for the purposes of potential inspection by the authorities.",
        },
      },
      AccountTab: "Settings",
      PublicInformationTab: "Profile",
      Products: "Products",
      ContactPersons: "Contact persons",
      NoRoleAssignedError: "An error occurred - the user has no role assigned.",
      Primary: "Primary",
      VerificationAccount: "Verification account",
      AccountOwner: "Account owner",
      ConnectionDate: "Connection date",
      ManualVerificationRequired: "This account needs to be verified manually",
      ActivateAccount: "Activate",
      RejectAccount: "Reject",
      AccountActivationConfirm:
        "Do you really want to activate this account?\n\nThis action is irreversible.",
      AccountRejectionConfirm:
        "Do you really want to reject this account?\n\nThis action is irreversible.",
      TerminalDocuments: "Terminal documents",
      TerminalDocumentsFailed: "Terminal document data failed to load.",
      MerchantFacilitiesFailed: "Facility data failed to load.",
      RelatedParties: "Related parties",
      RelatedPartySaved: "Related party data saved",
      RelatedPartyError: "Error occurred while saving related party data",
      NoDocumentsForPerson: "No uploaded documents found for this person.",
      VerificationSaved: "Verification data saved",
      VerificationError: "Error occurred while saving verification data",
      GeneralInformation: "General information",
      Facilities: "Facilities",
      Verification: "Verification",
      Company: "Company",
      Declaration: "Declaration",
      PersonalInformation: "Information about you",
      CompanyName: "Company name",
      TaxNumber: "Tax number",
      TaxResidence: "Tax residence",
      BusinessSubject: "Business subject",
      SpecifyBusinessSubject: "Specify business subject",
      IncomeSource: "Income source",
      LegacyIncomeSource: "Previous source of income",
      SpecifyIncomeSource: "Specify income source",
      ExpectedMonthlyVolume: "Expected monthly volume",
      AverageTransactionValue: "Average transaction value",
      PoliticallyExposed: "Politically exposed person",
      NonEUPermanentResidence:
        "I currently (as of the registration date) reside outside the EU for more than 12 months",
      NonEUNationality: "I am a citizen of another country outside the EU",
      StreetNumber: "Street number",
      OrientationNumber: "Orientation number",
      PostalCode: "Postal code",
      DocumentNumber: "Document number",
      BankAccount: "Bank account",
      PartyError: "Error occurred while saving party data",
      VatRegistration: "VAT registration",
      VatNumberRestoredByRegistry:
        "The VAT register confirmed VAT ID {{vatNumber}}, so it stays on file — the register is the source of truth.",
      VatStatusNotVerifiedHint:
        "The VAT register status is not verified yet. You can enter your VAT ID at any time.",
      PersonalDataChangeNotification:
        "If you need to change your personal data, please send the new information to info@mo.one.",
      SecondDocumentUploadError: "Failed to upload the second document",
      SecondDocumentUploadSuccess:
        "Second document was successfully uploaded and processed",
      FirstDocumentUploadError: "Failed to upload the document",
      FirstDocumentUploadSuccess:
        "Document was successfully uploaded and processed",
      FirstDocumentUpload: {
        UploadButton: "Upload document",
        FileDescription: "Click here to upload or take a photo",
      },
      SecondDocumentUpload: {
        Title: "Second Document",
        Description:
          "The second document can be a driver's license or passport.",
        UploadButton: "Upload second document",
        FileLabel: "Front side of second document",
        FileDescription: "Click here to upload or take a photo",
      },
    },
    Forms: {
      Validation: {
        InvalidEmail: "E-mail is not valid",
        PasswordMinLength: "Password must be at least 8 characters",
        PasswordLowercase:
          "Password must contain at least one lowercase letter",
        PasswordUppercase:
          "Password must contain at least one uppercase letter",
        PasswordNumber: "Password must contain at least one number",
        TermsAgreement: "You must agree to the terms and conditions",
        UserAlreadyExists: "User already uses {{appName}}.",
        InvitationAlreadySent:
          "You have already sent an invitation to this e-mail.",
        InvitationError: "Error occurred during invitation.",
      },
    },
    Merchant: {
      Logo: {
        FacilityTitle: "Facility logo",
        Hint: "Supported formats: PNG, JPEG, WebP, SVG (max. 2 MB). Use a dark logo on a white or transparent background.",
        FileTooLarge: "File is too large (max. 2 MB)",
        InvalidFileType: "Unsupported file type",
        UploadSuccess: "Logo uploaded",
        UploadError: "Failed to upload logo",
        DeleteSuccess: "Logo deleted",
        DeleteError: "Failed to delete logo",
      },
      Avatar: {
        Title: "Merchant avatar",
        Hint: "Supported formats: PNG, JPEG, WebP (max. 2 MB). The avatar is shown as the merchant's image and in chat.",
        RemoveConfirm: "Remove the merchant avatar?",
        LockedAfterOnboardingHint:
          "You can change the merchant avatar only while the merchant is onboarding. The merchant can change it any time in their account.",
        UploadSuccess: "Merchant avatar updated",
        UploadError: "Failed to upload merchant avatar",
        DeleteSuccess: "Merchant avatar removed",
        DeleteError: "Failed to remove merchant avatar",
      },
      Pricings: {
        PricingSettingError: "Error - Failed to set merchant pricing",
        PricingChangeError: "Error - Failed to change merchant pricing",
        LoadingError: "Error - Failed to load merchant pricings",
        DirectPaymentError:
          "Error - Failed to load merchant direct payment fee, please contact the administrator.",
        SelectPricingLabel: "Select pricing",
        FeeWithFee: "{{totalFee}}%",
        FeeWithCategoryAndFee: "{{categoryName}} {{totalFee}}%",
        FeeWithFeeAndAssignedCount:
          "{{totalFee}}% - {{assignedCount}}× assigned",
        FeeWithCategoryFeeAndAssignedCount:
          "{{categoryName}} {{totalFee}}% - {{assignedCount}}× assigned",
        CategoryNames: {
          Partner: "Partner",
          Prime: "Prime",
          Standard: "Standard",
          Individual: "Individual",
        },
      },
      LoadingError: "Error - Failed to load merchant data",
      PaymentFailed: "Data failed to load.",
      AccountLoadingError: "Error - Failed to load your accounts",
      PayPoints: {
        NoBankAccountConnectedWarning: {
          title: "No bank account connected",
          linkPretext: "Paypoint payments can be created only after",
          linkText: "connecting a bank account.",
        },
        NoProductAssigned: "You have no product assigned.",
        NoPaypointProduct:
          "You have no product assigned that allows creating a paypoint.",
      },
      ContactPersons: {
        Title: "Contact persons",
        LoadingError: "Error - Failed to load contact persons",
        DeleteSuccess: "Contact person deleted successfully",
        DeleteError: "Error - Failed to delete contact person",
        DeleteConfirmation:
          "Are you sure you want to delete contact person {{name}}?",
        AddContactPerson: "+Add contact person",
        NewContactPerson: "New contact person",
        ContactPersonDetail: "Contact person detail",
        NoContactPersons: "No contact persons added",
        Form: {
          AddSuccess: "Contact person added successfully",
          AddError: "Error - Failed to add contact person",
          UpdateSuccess: "Contact person updated successfully",
          UpdateError: "Error - Failed to update contact person",
          Legend: "Contact person",
        },
      },
      BeneficialOwners: {
        BeneficialOwnerDetail: "Beneficial owner detail",
        AddBeneficialOwner: "+ Add beneficial owner",
        NewBeneficialOwner: "New beneficial owner",
        Title: "Beneficial owners",
        LoadingError: "Beneficial owners data failed to load.",
        NoBeneficialOwners: "No beneficial owners added",
        Form: {
          DeleteConfirmation:
            "Are you sure you want to delete beneficial owner {{name}}?",
          AddSuccess: "Beneficial owner added successfully",
          AddError: "Error - Failed to add beneficial owner",
          UpdateSuccess: "Beneficial owner updated successfully",
          UpdateError: "Error - Failed to update beneficial owner",
          Legend: "Beneficial owner",
        },
      },
      Facilities: {
        Title: "Facilities",
        LoadingError: "Facilities data failed to load.",
        DeleteSuccess: "Facility deleted successfully",
        DeleteError: "Error - Failed to delete facility",
        DeleteConfirmation:
          "Are you sure you want to delete facility {{name}}?",
        AddFacility: "+ Add facility",
        NewFacility: "New facility",
        EditFacility: "Edit facility",
        FacilityDetail: "Facility detail",
        NoFacilities: "No facilities added",
        NoAddress: "No address",
        Incomplete: "Incomplete",
        NoPhotos: "The facility has no photos uploaded.",
        PhotosLoadingError: "Failed to load the facility photos.",
        Form: {
          AddSuccess: "Facility added successfully",
          AddError: "Error - Failed to add facility",
          UpdateSuccess: "Facility updated successfully",
          UpdateError: "Error - Failed to update facility",
          PhotoUploadSuccess: "Photos uploaded successfully",
          PhotoUploadError: "Error - Failed to upload photos",
          PhotoTooLarge: "The photo is too large (max. {{maxSizeMb}} MB).",
          PhotoInvalidType: "Unsupported file type. Use JPEG, PNG or WebP.",
          PhotosNotSaved:
            "The facility was saved, but the photos were not uploaded. Fix the highlighted photo and save again.",
          PhotosDescription:
            "Upload photos of your facility. These photos will be visible to customers. JPEG, PNG or WebP, max. {{maxSizeMb}} MB each.",
          UploadPhotos: "Upload photos",
          PhotoSelected: "Selected file: {{name}}",
          PhotoUploadButton: "Upload",
          PhotoChangeButton: "Change",
          PhotoDeleteButton: "Delete",
          Legend: {
            BasicInformation: "Basic information",
            Contact: "Contact",
            OpeningHours: "Opening hours",
            Address: "Address",
            Turnover: "Turnover",
            Terminals: "Terminals",
            PublicFacilityInfo: "Public facility information",
            DetailedOpeningHours: "Detailed opening hours",
            Photos: "Facility photos",
          },
          RequiredSection: "Required information",
          OptionalSection: "Optional information",
          OptionalSectionDescription:
            "The merchant will complete these details during their registration. If you already have the information, you can pre-fill it.",
          PublicFacilityDescription:
            "Public facilities require additional information. Please fill them below.",
          IsPublicHelpText:
            'This will allow customers to see your facility in the "where to pay" list.',
          UseRegisteredAddress: "Same as registered address",
          PrefillContact: "Prefill contact from",
          NoName: "No name",
          Validation: {
            CategoryRequiredWhenPublic:
              "At least one category is required when facility is public",
          },
          Fields: {
            Name: "Name",
            ProvidedServices: "Provided services",
            ResponsiblePerson: "Responsible person",
            Phone: "Phone",
            Email: "E-mail",
            IsPublic: "I want this facility to be public",
            Category: "Category",
            OpeningHoursFrom: "From",
            OpeningHoursTo: "To",
            Address: {
              Street: "Street",
              StreetNumber: "Street number",
              OrientationNumber: "Orientation number",
              PostalCode: "Postal code",
              Municipality: "Municipality",
              District: "District",
            },
            WeekDays: {
              Monday: "Monday",
              Tuesday: "Tuesday",
              Wednesday: "Wednesday",
              Thursday: "Thursday",
              Friday: "Friday",
              Saturday: "Saturday",
              Sunday: "Sunday",
            },
            YearlyTurnover: "Yearly turnover",
            RealCardTurnover: "Real card turnover",
            AverageTransactionValue: "Average transaction value",
            EstimatedTurnover: "Estimated turnover",
            EstimatedTurnoverHelperText:
              "Please fill in this field only if you are registering a new company",
            TerminalCount: "Number of physical terminals",
            TerminalCountHelperText:
              "Physical terminals do not include SoftPOS.",
            SoftPosCount: "SoftPOS count",
            SoftPosCountHelperText:
              "Payment app on the phone. No physical terminal is reserved.",
            Description: "About us",
            PhotoOutside: "Photo of the exterior",
            PhotoInside: "Photo of the interior",
          },
          OpeningHours: {
            Title: "Opening Hours",
            ClosedAllDay: "Closed all day",
            OpenAllDay: "Open 24 hours",
            AddTimeRange: "Add time range",
            DeleteTimeRange: "Delete time range",
            From: "From",
            To: "To",
            BulkEdit: "Edit all",
            ExitBulkEdit: "Exit Bulk Edit",
            ApplyToSelected: "Apply to Selected Days",
            SelectDays: "Select Days",
            QuickSelectTitle: "Quick Select",
            Cancel: "Cancel",
            Save: "Save",
            TimeRange: "From - To",
            CurrentHours: "Current Hours",
            ShowDetailedEdit: "Edit Individual Days",
            HideDetailedEdit: "Hide Individual Day Editing",
            QuickSelect: {
              Weekdays: "Mon-Fri",
              AllWeek: "Mon-Sun",
              Weekend: "Weekend",
            },
            Validation: {
              CannotBeBothOpenAndClosed:
                "Cannot be both open and closed all day",
              InvalidTimeFormat: "Invalid time format. Expected HH:MM:SS",
              OpeningTimeBeforeClosingTime:
                "Opening time must be before closing time",
            },
          },
        },
      },
      OrbitL: {
        Title: "Payment terminals",
        StatusLabel: "Order status",
        RefreshTooltip: "Refresh status",
        Tariff: {
          Label: "Pricing plan",
          Pos1Cz: "Pricing 1 — 0.49 % MIF++",
          Pos2Cz: "Pricing 2 — 0.41 % MIF++",
          Pos3Cz: "Pricing 3 — 0.35 % MIF++",
          Pos4Cz: "Pricing 4 — 0.29 % MIF++",
        },
        Financing: {
          Label: "Terminal financing",
          Legione: "Terminal financed by Legi.one",
          Hunter: "Terminal financed by Hunter",
        },
        HunterCapacity: {
          AvailableHelper: "Hunter has {{count}} terminal(s) available",
          ExceedsError:
            "Terminal count ({{requested}}) exceeds the available hunter capacity ({{available}}).",
        },
        Progress: {
          Steps: {
            Completion: "Data completion",
            Signing: "Document signing",
            Processing: "Processing",
            Dispatch: "Terminal dispatch",
            DispatchSoftPos: "SoftPOS activation",
            Done: "Done",
          },
        },
        Checklist: {
          Heading: "Requirements checklist",
          CompletionSuccess:
            "All requirements are met. You can proceed to download the contract.",
          ToCompleteSection: "To complete",
          GoToSettingsLink: "Go to settings",
          Items: {
            BasicInfo: "Merchant basic info",
            FacilityInfo: "Facility information",
            BankAccountInfo: "Payout bank account",
            ExecutiveInfo: "Executive details",
            ExecutiveInfoSelfEmployed: "Your personal details",
            ExecutiveDocuments: "Executive identity documents",
            ExecutiveDocumentsSelfEmployed: "Your identity documents",
            TerminalAssignment: "Terminal assignment to facilities",
          },
          Validation: {
            BasicInfo: "Please complete merchant basic information",
            FacilityInfo: "Please complete facility information",
            BankAccountInfo: "Please add or verify a payout bank account",
            ExecutiveInfo: "Please complete executive details",
            ExecutiveInfoSelfEmployed: "Please complete your personal details",
            ExecutiveDocuments: "Please upload executive identity documents",
            ExecutiveDocumentsSelfEmployed:
              "Please upload your identity documents",
            TerminalAssignment:
              "Terminals are not fully assigned to all facilities",
          },
          BankReconnect: {
            Explanation:
              "Your bank account is added, but its ownership is not verified. Please reconnect your bank – you will sign in to your internet banking and come back here.",
            Button: "Reconnect bank",
          },
          MissingFields: {
            MissingExecutive: "no statutory representative on record",
            PrimaryBankAccount: "no primary payout bank account",
            BankVerificationAudit:
              "missing bank account ownership verification",
            BirthDate: "date of birth",
            Email: "e-mail",
            Phone: "phone",
            PermanentAddress: "place of residence",
            PermanentAddressStreet: "street or house number",
            PermanentAddressMunicipality: "municipality of residence",
            PermanentAddressPostalCode: "postal code of residence",
            Documents: "identity documents",
            FirstDocumentNumber: "first document number",
            FirstDocumentValidity: "first document validity",
            FirstDocumentPhotos: "first document photos",
            SecondDocumentNumber: "second document number",
            SecondDocumentValidity: "second document validity",
            SecondDocumentPhotos: "second document photos",
          },
        },
        Rejection: {
          Title: "The processor returned documents for correction",
          Intro:
            "The documents listed below need to be corrected. Update them in settings and we will resubmit the order.",
          ContractGroup: "Contract and company details",
          DocumentLabel: "{{type}} no. {{number}}",
          DocumentTypes: {
            IdCard: "ID card",
            Passport: "Passport",
            DriverLicense: "Driving licence",
            OtherDocument: "Other document",
          },
        },
        Phase: {
          RequiresCompletion: "Requires completion",
          ReadyToDownloadContract: "Ready to download",
          ContractDownloaded: "Contract downloaded",
          SubmittedToComgate: "Submitted for processing",
          ProcessingByComgate: "Processing",
          TerminalDispatched: "Terminals dispatched",
          Completed: "Completed",
        },
        FinancingModel: {
          Hunter: "Hunter",
          Legione: "LEGI.ONE",
        },
        Messages: {
          LoadStatusError: "Failed to load Orbit L order status",
          Processing:
            "Your order is being processed. We will notify you about the next steps via email.",
          CompletedSuccess:
            "Order is complete. Terminals have been delivered and are active.",
        },
        Contract: {
          DocumentsHeading: "Contract documents",
          LoanTitle: "Terminal loan agreement",
          LoanFileName: "Terminal_loan_agreement",
          PaymentServicesTitle: "Payment services agreement",
          PaymentServicesFileName: "Payment_services_agreement",
          DownloadedAt: "Contract was downloaded on {{date}}",
          DownloadButton: "Download contract",
          DownloadAgainButton: "Download contract again",
          AcceptanceLabel:
            'I confirm that I have read both contracts and agree to their terms. By clicking "Place binding order" I electronically sign the contracts.',
          SubmitButton: "Place binding order for card services",
          SubmittingButton: "Submitting...",
          SubmitNote:
            "After submitting we will process your application. You will be notified about the next steps via email.",
          DownloadSuccess: "Contract was downloaded successfully",
          DownloadError: "Contract download failed",
          SubmitSuccess: "Order has been submitted for processing.",
          SubmitError: "Orbit L order submission failed: {{error}}",
          SubmitErrorGeneric: "Order submission failed",
        },
        Handover: {
          DispatchedNoData:
            "Terminals have been dispatched. Terminal information will be filled in automatically after status update.",
          Title: "Terminal handover",
          DispatchedReady:
            "Your terminals have been dispatched and are ready for activation.",
          TableTID: "TID (Terminal ID)",
          TableSerialNumber: "Serial number",
          TableModel: "Model",
          Note: "After confirming the handover, terminals will be automatically activated as payment places (PayPoints) on your account.",
          ConfirmButton: "Confirm receipt of terminals",
          ConfirmingButton: "Confirming...",
          ConfirmSuccess: "Terminal handover confirmed successfully",
          ConfirmError: "Terminal handover confirmation failed: {{error}}",
          ConfirmErrorGeneric: "Handover confirmation failed",
        },
        TerminalSelection: {
          Title: "Terminal selection",
          SelectionCount: "{{selected}} of {{required}} terminals selected",
          NoTerminalsAvailable:
            "You have no terminals available for assignment",
          StockExceeded:
            "Total terminal count ({{total}}) exceeds available stock ({{available}})",
          SerialNumber: "SN",
          HardwareModel: "Model",
        },
        FacilityTerminals: {
          Title: "Assign terminals to facilities",
          TerminalCountLabel: "Physical terminal count",
          TerminalCountHelperText: "Physical terminals do not include SoftPOS.",
          SoftPosCountLabel: "SoftPOS count",
          SoftPosCountHelperText:
            "Payment app on the phone, no physical terminal.",
          SoftPosRequestHelperText:
            "You don't have SoftPOS yet. Enter the count and send the request; SoftPOS is activated once support confirms it.",
          SoftPosStatus: "SoftPOS: {{count}}",
          TerminalStatus: "{{assigned}}/{{total}} assigned",
          NoTerminalsConfigured: "No terminals configured",
          NoAddress: "No address",
          FullyReserved: "All terminals are assigned",
          SaveButton: "Save terminals",
          Saving: "Saving...",
          SaveSuccess: "Terminals assigned successfully",
          SaveError: "Terminal assignment failed",
          InsufficientStock:
            "Not enough terminals in stock. Please contact support.",
          CountsLocked:
            "Terminal counts can no longer be changed directly, the order is with Comgate. Send a change request and support will arrange it.",
          LoadError: "Failed to load terminal data",
          AssignedTerminals: "Assigned terminals",
        },
        TerminalCountChange: {
          NotificationTitle: "Terminal count change request",
          NotificationMessage:
            "{{company}} is asking to change the terminal count from {{from}} to {{to}}.",
          LockedHint:
            "The order is already with Comgate, so the terminal count can no longer be changed directly. Send a request to support instead.",
          RequestButton: "Request a change",
          ModalTitle: "Terminal count change request",
          ModalDescription:
            "Once the order has been submitted to Comgate the terminal count cannot be changed automatically. Sending this creates a request for our support team, who will arrange the change with Comgate and get back to you.",
          ModalSummaryHeading: "Change summary",
          ModalFacilityChange: "{{facility}}: {{from}} → {{to}}",
          ModalTotal: "Total: {{from}} → {{to}}",
          ModalFacilitySoftPosChange:
            "{{facility}} (SoftPOS): {{from}} → {{to}}",
          ModalSoftPosTotal: "SoftPOS total: {{from}} → {{to}}",
          ModalNoteLabel: "Note for support (optional)",
          ModalNotePlaceholder: "E.g. we are opening a new branch on 1 Sep.",
          ModalConfirm: "Send request",
          ModalCancel: "Cancel",
          PendingTitle: "Terminal count change request sent",
          PendingMessage:
            "A request to change the terminal count from {{from}} to {{to}} has been sent to support. We will get back to you once the change is agreed with Comgate.",
          PendingNote: "Your note: {{note}}",
          PendingSoftPosMessage: "SoftPOS licences: {{from}} → {{to}}.",
          RequestSuccess: "Request sent to support",
          RequestError: "Failed to send the request",
          RequestConflictError:
            "You already have an open terminal count change request. Please wait until it is resolved.",
        },
      },
      TerminalContracts: {
        Title: "Terminal contract drafts",
        LoadingError: "Terminal contract drafts data failed to load.",
        NoTerminalContracts: "No terminal contract drafts added",
        AddSuccess: "Terminal contract draft added successfully",
        AddError: "Error - Failed to add terminal contract draft",
        UpdateSuccess: "Terminal contract draft updated successfully",
        UpdateError: "Error - Failed to update terminal contract draft",
        DeleteSuccess: "Terminal contract draft deleted successfully",
        DeleteError: "Error - Failed to delete terminal contract draft",
        DeleteConfirmation:
          "Are you sure you want to delete terminal contract draft {{type}}?",
        AddButtonText: "+ Add terminal contract draft",
        NewContractDialogTitle: "New terminal contract draft",
        EditContractDialogTitle: "Edit terminal contract draft",
        ViewContractDialogTitle: "View terminal contract draft",
        ProductType: {
          UCB: "Orbit M - Physical terminal without printer (Android)",
          COMGATE: "Orbit L - Physical terminal with printer (Android)",
          ELCOM: "Orbit ELCOM - ELCOM terminal",
        },
        Form: {
          Fields: {
            Type: "Type",
            GprsCount: "Number of GPRS terminals",
            TerminalCount: "Number of physical terminals",
            TerminalCountHelperText:
              "Physical terminals do not include SoftPOS.",
            SoftPosCount: "SoftPOS count",
            CountsFromFacilitiesHint:
              "Sum over facilities; edit it in the facility form.",
            Currencies: "Currencies",
            Tariff: "Tariff",
            PayoutIBAN: "Payout IBAN",
            PayoutBIC: "Payout BIC",
            PayoutCurrency: "Payout currency",
            PayoutAccountNumber: "Payout account number",
          },
        },
        Comgate: {
          SubmitButton: "Submit to Comgate",
          Submitting: "Submitting...",
          SubmitConfirmation:
            "This will generate the loan agreement, auto-accept it, and submit the merchant data to Comgate. Continue?",
          SubmitSuccess: "Successfully submitted to Comgate. MID: {{mid}}",
          SubmitError: "Failed to submit to Comgate. Please try again.",
          SubmitViaOrbitL:
            "Use the Orbit L onboarding flow to submit this contract to Comgate.",
          StatusLabel: "Comgate status",
          Statuses: {
            REQUESTED: "Requested",
            PROCESSING: "Processing",
            WAITING_FOR_DOCUMENTS: "Waiting for documents",
            WAITING_FOR_AUTHORIZATION_PAYMENT:
              "Waiting for authorization payment",
            ERRORS_IN_DOCUMENTS: "Errors in documents",
            ERROR_ACTION_PENDING: "Error - action pending",
            REJECTED: "Rejected",
            ASSOCIATION_REGISTERING: "Association registering",
            READY: "Ready",
          },
        },
      },
      AppProducts: {
        Title: "Products",
        LoadingError: "Products data failed to load.",
        NoProducts: "No products added",
        Form: {
          AddError: "Error - Failed to add product",
          AddErrorCountsLocked:
            "The order is already with Comgate, so the product can't be added directly. Send a terminal count change request and our support will handle it.",
          RemoveError: "Error - Failed to remove product",
          Prerequisites: {
            Title: "Prerequisites",
            FacilityAdded: "Facility added",
            FacilityPhotos: "Facility photos (exterior + interior)",
          },
        },
        UnknownProduct: "Unknown product",
        Products: {
          OrbitS: "Orbit S",
          OrbitM: "Orbit M",
          OrbitL: "Orbit L",
          SoftPos: "SoftPOS",
        },
        ProductDescriptions: {
          OrbitS: "No physical terminal, 3 NFC cards",
          OrbitM: "Orbit M - Physical terminal without printer (Android)",
          OrbitL: "Orbit L - Physical terminal with printer (Android)",
          SoftPos: "SoftPOS - Payment app on the phone, no physical terminal",
        },
        ProductAccordion: {
          Active: "Active",
          Activate: "Activate product",
          Deactivate: "Deactivate",
          Preview: "Coming soon",
          Default: "Default",
          ProductNotActivated: "This product has not been activated",
          SoftPosViaChangeRequest:
            "The terminal order is already with Comgate. Add SoftPOS on the Orbit L card: enter the SoftPOS count for the facility and send a terminal count change request. SoftPOS is activated once support confirms it.",
          SoftPosContactSupport:
            "The terminal order is already with Comgate. Contact support to add SoftPOS.",
          FeeWithPricing: "Fee set: {{totalFee}}% | {{productName}}",
          FeeWithCategoryAndPricing:
            "{{categoryName}} | Fee set: {{totalFee}}% | {{productName}}",
          IndividualPricing: "Individual pricing",
        },
      },
    },
    ProfilePhoto: {
      ChangeAction: "Change photo",
      RemoveAction: "Remove photo",
      CropDialogTitle: "Crop your photo",
      ZoomLabel: "Zoom",
      CropSave: "Save",
      CropCancel: "Cancel",
      FileTooLarge: "File is too large (max. 2 MB)",
      InvalidFileType: "Unsupported file type. Use PNG, JPEG or WebP.",
    },
    UserAccount: {
      ProfilePhoto: {
        Hint: "Supported formats: PNG, JPEG, WebP (max. 2 MB)",
        RemoveConfirm: "Remove your profile photo?",
        UploadSuccess: "Profile photo updated",
        UploadError: "Failed to upload profile photo",
        DeleteSuccess: "Profile photo removed",
        DeleteError: "Failed to remove profile photo",
      },
      ChangePassword: {
        SectionTitle: "Password",
        OpenAction: "Change password",
        DialogTitle: "Change password",
        OldPasswordLabel: "Current password",
        NewPasswordLabel: "New password",
        NewPasswordConfirmLabel: "Confirm new password",
        Submit: "Change password",
        Cancel: "Cancel",
        Success:
          "Password changed. Please sign in again with your new password.",
        NewMustDiffer: "The new password must differ from the current one.",
        WrongOldPassword: "The current password is incorrect.",
        GenericError: "Failed to change password. Please try again.",
        RateLimitError:
          "Too many attempts. Please wait a moment and try again.",
      },
    },
    Onboarding: {
      LogoutButtonLabel: "Leave registration",
      LicensePaymentReceived: "Payment received",
      LicensePaymentProcessing: "We are processing your payment",
      SSO: {
        MerchantDescription:
          "As a merchant, you can accept payments from your customers with lower fees and use contactless paypoints.",
        CustomerDescription:
          "As a user, you can send direct payments, pay for your purchases and collect Stardust points.",
        HunterDescription:
          "As a hunter, you can get a Legione license, manage your merchants, and earn commissions from their transactions",
        Merchant: "Merchant",
        Customer: "User",
        FirstLogin:
          "Since this is your first login, we need to find out whether you are registering with us as a User, Merchant or Hunter.",
      },
    },
    Shared: {
      Filters: {
        Title: "Filters",
        TimePeriod: "Period",
        TimePeriodLabels: {
          day: "Today",
          week: "This week",
          month: "This month",
          custom: "Custom",
        },
        Unlimited: "Unlimited",
        DateFrom: "From",
        DateTo: "To",
        DateRange: "Date range",
        PayPoint: "PayPoint",
        AllPayPoints: "All PayPoints",
        Employee: "Employee",
        AllEmployees: "All employees",
        Status: "Status",
        AllStatuses: "All statuses",
        Apply: "Apply",
        Reset: "Clear filters",
        RefundOrCancellation: "Refund or cancellation",
        ShowOnlyRefundsAndCancellations: "Only refunds and cancellations",
      },
      NotificationTypeSelect: {
        SelectNotificationType: "Select notification type",
      },
      CodeListAutocomplete: {
        Placeholder: "Select options",
        SelectAll: "Select all",
      },
      Carousel: {
        ScrollLeft: "Scroll left",
        ScrollRight: "Scroll right",
      },
      Progress: {
        AriaLabel: "Progress",
      },
      Dropdown: {
        NoOptions: "No options available",
      },
      PoliticallyExposedDescription: `A politically exposed person (PEP) is a person who holds or has held a significant public function, such as president, prime minister, minister, ambassador, supreme court judge, senior military officer or member of parliament. Politically exposed persons may also include their close family members or close associates.

      The legal definition of PEP can be found in Section 4(5) of Act No. 253/2008 Coll., on certain measures against the legalization of proceeds from criminal activity and financing of terrorism.`,

      DownloadPartyIdentificationsButtonLabel: "Download documents",
      Payments: {
        Amount: "Amount",
        VariableSymbol: "Variable symbol",
        Note: "Note",
      },
      SettlementPeriodSelector: {
        Placeholder: "Select period",
        NoSettlementPeriods:
          "No settlement available for download at the moment.",
      },
      BankingAccountSelector: {
        NoBankAccounts: "You don't have any connected bank accounts.",
        ConnectBankAccount: "Connect bank account",
      },
      CardsList: {
        View: "View",
        Edit: "Edit",
        Delete: "Delete",
        Download: "Download",
      },
      HunterAutocomplete: {
        SearchHunter: "Select another hunter",
        SearchHunterPlaceholder: "Enter name or license ID",
        SearchHunterMinLength: "Enter at least 2 characters to search",
        SearchHunterNoResults: "No hunters found",
      },
      MerchantUserAutocomplete: {
        Placeholder: "Enter company name or number",
        MinLength: "Type at least 2 characters to search",
        NoResults: "No merchant users found",
      },
      AddressAutocomplete: {
        MinLength: "Type at least 3 characters to search",
        NoResults: "No address found",
        Error:
          "Address lookup is unavailable, please enter the address manually",
      },
      AddressInput: {
        SearchLabel: "Address from registry",
        EnterManually: "Enter address manually",
        SearchInRegistry: "Search the address registry",
        NotInRegistry:
          "This address could not be found in the address registry, please double-check it.",
        HasErrors: "The address is incomplete or invalid, please check it.",
        UncommittedQuery:
          "Pick an address from the list to apply it — typing alone won't change the saved address.",
      },
      HunterList: {
        MainHunterCard: {
          YourCommission: "Your commission",
        },
      },
    },
    Transactions: {
      Tabs: {
        Transfers: "Transfers",
        Cards: "Cards",
        AriaLabel: "Transaction type",
      },
      Title: "Transactions",
      LoadMore: "Load more",
      Export: "Export transactions",
      NoTransactions: "No transactions yet",
      PendingRefunds: "Pending refund transactions",
      MerchantDetailTransactionFees: {
        Title: "Transaction fees this month",
      },
      TransactionPaymentOptions: {
        DirectPaymentButton: "Direct payment",
        ShowPaymentDataButton: "Show payment instructions",
      },
      Filters: {
        Direction: "Direction",
        AllDirections: "All directions",
      },
      Summary: {
        Revenue: {
          day: "Daily revenue",
          week: "Weekly revenue",
          month: "Monthly revenue",
          custom: "Revenue for period",
        },
        UnlimitedRevenue: "Total revenue",
        FilteredRevenue: "Filtered revenue",
        FilterSuffix: "by filters",
      },
      Search: {
        Placeholder: "Search by ID...",
      },
      ExportActions: {
        Download: "Download",
        Email: "Send to email",
      },
      ExportDrawer: {
        ExportSelectionPdf: "Export filtered (.pdf)",
        ExportSelectionXlsx: "Export filtered (.xlsx)",
        ExportSelectionCsv: "Export filtered (.csv)",
        ExportTodayRevenueCsv: "Export today's revenue (.csv)",
        SendToEmail: "Send to email (PDF + XLSX)",
        EmailAddress: "Email address",
        EnterEmailAddress: "Enter email address",
        Send: "Send",
        Cancel: "Cancel",
        EmailSentSuccess: "Export has been sent to your email",
        EmailSentError: "Failed to send export to email",
      },
      Direction: {
        Incoming: "Incoming",
        Outgoing: "Outgoing",
        IncomingRefund: "Incoming refund",
        OutgoingRefund: "Outgoing refund",
      },
    },
    CardTransactions: {
      Title: "Card transactions",
      LoadMore: "Load more",
      NoCardTransactions: "No card transactions yet",
      Columns: {
        DateTime: "Date and time",
        Terminal: "Terminal",
        Operator: "Operator",
        Amount: "Amount",
        Status: "Status",
        CardBrand: "Card",
        Pan: "Card number",
        Refund: "Refund/cancellation",
      },
      Status: {
        Approved: "Approved",
        Rejected: "Rejected",
        Canceled: "Cancelled",
        Pending: "Pending",
        Unknown: "Unknown",
      },
      Direction: {
        Sale: "Sale",
        Refund: "Refund",
        Cancellation: "Cancellation",
        Adjustment: "Adjustment",
      },
      Summary: {
        NetAmount: "Net amount",
        Sales: "Sales",
        SalesCount: "Payments count: {{count}}",
        Refunds: "Refunds",
        RefundsCount: "Refunds count: {{count}}",
      },
      Export: {
        Download: "Download",
        Email: "Send to email",
        ExportPdf: "Export filtered (.pdf)",
        ExportXlsx: "Export filtered (.xlsx)",
        ExportCsv: "Export filtered (.csv)",
        SendToEmail: "Send to email (PDF + XLSX)",
        EmailAddress: "Email address",
        EnterEmailAddress: "Enter email address",
        Send: "Send",
        Cancel: "Cancel",
        EmailSentSuccess: "Export has been sent to your email",
        EmailSentError: "Failed to send export to email",
        DownloadError: "Failed to download export",
      },
    },
    Sell: {
      OfferValid: "Offer valid for {{seconds}}s more",
    },
    Affiliate: {
      LeadStatus: {
        AnonymousPaymentPerformed: "Anonymous payment",
        Invited: "Invited",
        Registered: "Registered",
        Completed: "Completed",
        CompletedRewardSent: "Completed",
        CompletedRewardApplied: "Completed",
        Withdrawn: "Withdrawn",
      },
      ThanksToYou: {
        Participation: {
          Title: "Thanks to you! participation",
          OptedInStatus:
            "You take part in Thanks to you! and earn rewards for your referrals.",
          OptOutButton: "Leave the program",
          OptOutDialogTitle: "Leave Thanks to you!",
          OptOutDialogIrreversible:
            "Leaving is final. You will not be able to rejoin the program.",
          OptOutDialogLoseTitle: "You lose",
          OptOutDialogLoseReferrals:
            "All referrals collected so far and the rewards from them",
          OptOutDialogLoseInviter: "The link to the user who invited you",
          OptOutDialogKeepTitle: "You keep",
          OptOutDialogKeepRewards: "Rewards you have already earned",
          OptOutDialogKeepAccount:
            "Your account and everything else in {{appName}}",
          OptOutDialogEmailNote:
            "We will send an e-mail to {{email}}; you complete leaving by clicking the link in it.",
          OptOutDialogConfirm: "Send confirmation e-mail",
          EmailSent:
            "We have sent a confirmation e-mail to {{email}}. Click the link in it to complete leaving; it is valid until {{expiresAt}}. Until you click it, you stay in the program.",
          ResendButton: "Send the e-mail again",
          EmailSentToast: "The confirmation e-mail is on its way.",
          EmailSendError:
            "The confirmation e-mail could not be sent. Please try again in a moment.",
          EmailCooldownError:
            "We sent you the confirmation e-mail a moment ago. Try again in a minute.",
        },
        Join: {
          Title: "You do not take part in Thanks to you!",
          Text: "You declined the program when you signed up. Once you join, you can invite friends and earn rewards from their payments. Earlier invitations and referrals are not restored.",
          Button: "Join the program",
          JoinedToast: "You have joined Thanks to you!",
        },
        Withdrawn: {
          Title: "Thanks to you!",
          Text: "You have left Thanks to you! for good. Rewards you have already earned stay yours. Rejoining the program is not possible.",
          PayoutBalance: "Remaining payout: {{amount}}",
        },
        WithdrawalPage: {
          Title: "Leaving Thanks to you!",
          Prompt:
            "Click to confirm leaving Thanks to you! for good. You lose all your referrals and the link to the person who invited you; rewards you have already earned stay yours.",
          ConfirmButton: "Confirm leaving",
          Success: {
            Title: "You have left the program",
            Text: "You have left Thanks to you! for good. Rewards you have already earned stay yours.",
          },
          AlreadyWithdrawn: {
            Title: "You have already left",
            Text: "You have already left the Thanks to you! program. There is nothing else to do.",
          },
          Expired: {
            Title: "This link no longer works",
            Text: "It has expired, or we have sent you a newer one since. If you still want to leave, request a new e-mail in the Thanks to you! section.",
          },
          Error: {
            Title: "Leaving did not go through",
            Text: "Leaving the program could not be completed. Open the link again, or request a new e-mail in the Thanks to you! section.",
          },
          ContinueButton: "Continue to the app",
          OpenSectionButton: "Open the Thanks to you! section",
        },
      },
      AffiliateStats: {
        Invited: "Invited users",
        Registered: "Registered users",
        WithActiveLicense: "With active license",
        AnonymousPayment: "First payment with {{appName}}",
      },
      AffiliateLink: {
        Title: "Your affiliate link",
        LoadingError: "Error - Failed to load affiliate link",
        CopyLink: "Copy link",
        LinkCopied: "Copied!",
        ShareButton: {
          Title: "{{appName}}",
          Text: "{{appName}} invitation",
        },
      },
      AffiliateLinkWithActions: {
        InviteFriend: "Invite a friend",
        ShareButton: "Share",
        EmailButton: "Send to e-mail",
        QrButton: "Show QR",
        QrCodeFileName: "Affiliate QR code",
        QrInstructions:
          "Show this QR code to a friend you want to invite to {{appName}}",
        LinkTypeToggle: {
          Title: "Choose destination",
          Registration: "Direct registration",
          MooneInfo: "Via {{appName}} website",
        },
      },
      CodeField: {
        SectionTitle: "Referral",
        Label: "Affiliate code",
        Placeholder: "Fill in if someone referred you",
        Verified: "The code is valid.",
        RecommendedBy: "Referred by: {{name}}",
        Locked: "The affiliate is already active and can't be changed.",
        LockedBy:
          "The affiliate is already active and can't be changed. Referred by: {{name}}",
        ApplyFailed:
          "The affiliate code couldn't be saved, the registration continues without it.",
        Error: {
          AffiliateCodeNotFound: "This code doesn't exist.",
          AffiliateCodeOwn: "You can't use your own code.",
          AffiliateCodeNotApplicable:
            "This code can't be used for this registration.",
          AffiliateReferrerAlreadySet: "The affiliate is already active.",
          AffiliateReferrerTooLate: "An affiliate code can no longer be added.",
          TooManyRequests: "Too many attempts, please try again in a moment.",
          Unavailable: "The code couldn't be verified, please try again.",
          Unverified: "Verify the code or remove it.",
        },
      },
      LeadInvitation: {
        NewInvitation: "New invitation",
        FriendInvited: "Thank you! Your friend has been invited.",
        UserAlreadyExists: "User with this email already exists",
        InvitationAlreadySent: "Invitation already sent",
        GeneralError: "Error occurred while sending invitation",
        SendInvitation: {
          Hunter: "Send hunter invitation",
          Customer: "Send invitation",
          Merchant: "Send merchant invitation",
        },
      },
      UserAffiliate: {
        MooneLiteWarning:
          "To receive rewards from the Thanks to You program, you must first complete the registration and wait for account verification.",
        Title: "Thanks to you!",
        Subtitle: "Payments that make your earnings.",
        Description:
          "Share your affiliate code, invite friends and earn commissions from their purchases. The more people you invite, the higher your rewards.Make money from payments in the long term like banks and card companies.",
        HowItWorksTitle: "How it works:",
        HowItWorks:
          "• Verify your account in {{appName}}.<newline/>• Share your affiliate code with friends.<newline/>• Earn rewards - every time your referrals pay at merchants, you earn a share of their spend.",
        Rewards:
          "💡 Rewards are credited automatically, you just watch how your passive income grows.",
        TermsLinkPretext:
          "📄 More information can be found in the Affiliate program terms",
        YourAffiliateLink: "Your affiliate link",
        TermsLink: "„Díky Tobě!“",
        TermsLinkUrl:
          "https://legi.one/assets/cesta/Affiliate_Hunters_Rulers_11-2024.pdf",
        Tabs: {
          Customer: "Users",
          Campaigns: "Campaigns",
        },
      },
      HunterAffiliate: {
        Title: "Become a legend & earn long-term! 🚀",
        Description:
          "Invite your friends to Legi.one and get 10% commission for each new hunter! But that's not all – if they are successful, you'll get long-term commissions from their one business and share the second one 50% with you. You'll also get an increased commission from part of your businesses.",
        InviteNow:
          "Share your affiliate code now and actively build passive income!",
        YourAffiliateLink: "Your affiliate link",
        TermsLinkUrl:
          "https://legi.one/assets/cesta/Affiliate_Hunters_Rulers_11-2024.pdf",
        TermsLinkPretext: "Need more information? See the complete",
        TermsLink: "Affiliate program terms",
        Tabs: {
          Hunter: "Hunter",
          Customer: "User",
          Merchant: "Merchant",
          Campaigns: "Campaigns",
        },
        MerchantLicenseSelector: {
          Label: "Default license for merchant",
        },
        InviteExistingUser: {
          Title: "Inviting an existing Mo.one user?",
          Description:
            "If the person you want to invite already has a Mo.one account, use the new merchant registration flow. When you enter their email, the form will detect their existing account and send the invitation straight to their in-app inbox instead of forcing them through registration again.",
          ButtonLabel: "Invite existing user as merchant",
        },
      },
      MerchantAffiliate: {
        Title: "Invite merchants & earn rewards! 🚀",
        Description:
          "Invite your friends to {{appName}} and earn points for our rewards system, from which you will continuously receive benefits!",
        InviteNow:
          "Share your affiliate code now and actively build passive income!",
        TermsLinkPretext: "Need more information? See the complete",
        YourAffiliateLink: "Your affiliate link",
        TermsLinkUrl:
          "https://legi.one/assets/cesta/Affiliate_Hunters_Rulers_11-2024.pdf",
        TermsLink: "Affiliate program terms",
        Tabs: {
          Customer: "Users",
          Campaigns: "Campaigns",
        },
      },
      Earnings: {
        Title: "Your earnings",
        TotalRegisteredUsers: "Registered users",
        TierBreakdown: "T1: {{t1}}, T2: {{t2}}, T3: {{t3}}",
        CurrentPayout: "Available for payout",
        TotalEarned: "Total earned",
        RegisteredUsersHint:
          "Total number of users who registered on {{appName}} through your affiliate link.<newline/><newline/><bold>Tier breakdown:</bold><newline/>T1 (0.03%): {{t1}} users<newline/>T2 (0.02%): {{t2}} users<newline/>T3 (0.01%): {{t3}} users",
        PayoutHint:
          "Amount currently available for payout from your affiliate rewards.",
        TotalEarnedHint:
          "Total sum of all affiliate rewards earned from transactions of invited users.",
        CombinedPayoutHint:
          "Combined payout from Stardust program and Thanks to You affiliate program.",
        CombinedTotalEarnedHint:
          "Combined earnings from Stardust program and Thanks to You affiliate program.",
        CombinedValorPayoutHint:
          "Combined payout from Valor program and Thanks to You affiliate program.",
        CombinedValorTotalEarnedHint:
          "Combined earnings from Valor program and Thanks to You affiliate program.",
      },
      Campaigns: {
        SelectCampaign: "Select campaign",
        SelectCampaignPlaceholder: "Select campaign from the list",
        SelectCampaignDescription:
          "Select a campaign to view details and affiliate link.",
        Validity: "Validity:",
      },
      AnonymousPayer: "Anonymous customer payment",
    },
    Coinback: {
      Commission: "up to {{commission}}",
    },
    Settlements: {
      SettlementStatus: {
        Paid: "Paid",
        Unpaid: "Waiting for payment",
        Overdue: "Overdue",
      },
      TotalEarnings: "Total transaction earnings",
      CardEarnings: "Card transaction commission this period",
      TransactionCosts: "Transaction costs",
      SettlementCount: "Settlements {{count}} of {{total}}",
      NoSettlementsFound: "No settlements found",
      LoadingError: "Error occurred while loading settlements",
      SettlementControls: {
        Filter: {
          Title: "Filter",
          All: "All ({{total}})",
          Paid: "Paid ({{paid}})",
          Unpaid: "Unpaid ({{unpaid}})",
          Overdue: "Overdue ({{overdue}})",
        },
        Sort: {
          Title: "Sort by",
          DateDesc: "Date (latest)",
          DateAsc: "Date (oldest)",
          AmountDesc: "Amount (highest)",
          AmountAsc: "Amount (lowest)",
        },
      },
      SettlementList: {
        UpdateStatusSuccess: "Settlement has been marked as paid",
        UpdateStatusError: "Error occurred while updating status",
        DownloadReportError: "Error occurred while downloading report",
        DownloadReportFilePrefix: "settlement_report_",
        SettlementPeriod: "Period",
        SettlementDate: "Settlement date",
        DueDate: "Due date",
        Amount: "Amount",
        Status: "Status",
        Actions: "Actions",
        DownloadReport: "Download report",
        MarkAsPaid: "Mark as paid",
        Pay: "Pay",
        SettlementStatusTooltip:
          "We check the payment status every 2 hours between 8:00 AM and 10:00 PM.",
        SettlementTypeHunterCommissionFunding: "Hunter commission funding",
        SettlementTypeDefault: "Settlement",
        DownloadInvoice: "Download invoice",
        DownloadInvoiceError: "Error occurred while downloading invoice",
        InvoiceFilePrefix: "invoice_",
        RegenerateInvoice: "Regenerate invoice",
        RegenerateInvoiceSuccess: "Invoice has been regenerated",
        RegenerateInvoiceError:
          "An error occurred while regenerating the invoice",
      },
      SettlementDownloadButton: {
        Title: "Settlement",
        Downloading: "Downloading...",
        Download: "Download",
        RegionPlaceholder: "Select market",
        Regions: {
          CZ: "Czechia (CZK)",
          SK: "Slovakia (EUR)",
        },
      },
    },
  },
  Coinback: {
    NoEshopsFound: "No eshops found",
    Eshops: "Eshops",
  },
  FormLabels: {
    AccountType: "Account type for transfer",
    TransferAccountType: "Account type for transfer",
    RecipientIBAN: "Recipient IBAN",
    Amount: "Amount",
  },
  AML: {
    BasicInfo: "Basic Information",
    CompanyName: "Company Name",
    FirstName: "First Name",
    LastName: "Last Name",
    CompanyNumber: "Company Number",
    TaxNumber: "Tax Number",
    PersonalNumber: "Personal Number",
    BirthDate: "Birth Date",
    BirthPlace: "Birth Place",
    Nationality: "Nationality",
    Gender: "Gender",
    Phone: "Phone",
    Email: "E-mail",
    Unknown: "Unknown",
    Documents: "Documents",
    DocumentNumber: "Document Number",
    DocumentIssuer: "Document Issuer",
    DocumentValidity: "Document Validity",
    DocumentType: "Document Type",
    NoDocuments: "No uploaded documents found for this person.",
    IdentificationImageType: {
      FirstIDCardFrontSide: "ID Card Front Side",
      FirstIDCardBackSide: "ID Card Back Side",
      SecondIDCardFrontSide: "Second ID Card Front Side",
      SecondIDCardBackSide: "Second ID Card Back Side",
    },
    PdfUploaded: "User uploaded a PDF file.",
    OpenPdf: "Open PDF",
    ActingPerson: "Acting Person",
    BeneficialOwners: "Beneficial Owners",
    UboUnknownAlert: "Merchant marked beneficial owners as unknown",
    Loading: "Loading...",
  },
  PersonalInfo: {
    ConfirmDataRequest: "Was the data above read correctly?",
    FillMissingData: "Fill in missing or correct incorrect data",
    DataConfirmationMessage:
      "Please also confirm the accuracy of the identity document data.",
  },
  TableHeaders: {
    Verification: "Verification",
    Login: "Login",
    Role: "Role",
    Status: "Status",
    FirstName: "First Name",
    LastName: "Last Name",
    Company: "Company",
    ClientIdentifier: "ID",
    DateCreated: "Date Created",
    PaymentReference: "License Payment Reference",
    Version: "{{appName}} Version",
    Settlement: "Settlement",
    Admin: "Admin",
  },
  Nfc: {
    NfcCard: "NFC card",
    InvalidNfcCardNoFormat: "Invalid NFC card identifier format!",
    NfcCardNo: "NFC card identifier:",
    CopyNfcCardNo: "Copy card identifier",
    CopyNfcCardNoSuccess: "Card identifier copied",
    CardNotAssigned: "This NFC card is currently not assigned to anything.",
    AssignToPaypoint: "Assign to a paypoint",
    SelectPaypoint: "Select a paypoint to assign the card to",
    CardOverwriteConfirmation:
      "Paypoint {{name}} already has a different NFC card assigned. Do you want to overwrite it with this one?",
    CardAssignedSuccessfully: "NFC card assigned successfully",
    CardAssignmentFailed: "Error - Failed to assign NFC card",
    CardAssignmentFailedFormatError:
      "Error - NFC card identifier is in the wrong format",
    Redirecting: "You are being redirected automatically.",
    RedirectFallbackHint:
      "If nothing happens within 10 seconds, use the button below.",
    RedirectManually: "Redirect manually",
    RedirectNotConfigured:
      "This NFC tag has no address set yet. Please ask the staff.",
    SelectMerchant: "Select a merchant to assign the card to",
  },
  ExternalIntegrations: {
    Title: "External integrations",
    Description:
      "Accept {{appName}} payments in your e-shop. Download the plugin for your platform and follow the attached documentation.",
    DownloadPlugin: "Download ZIP",
    DownloadDocumentation: "Download documentation (PDF)",
    WooCommerce: {
      Name: "WooCommerce",
      Description: "Payment plugin for WordPress / WooCommerce.",
      PluginUrl: "/content/integrations/moone-payments-for-woocommerce.zip",
      DocumentationUrl:
        "/content/integrations/Mo.one-Payments-WooCommerce-EN.pdf",
    },
    PrestaShop: {
      Name: "PrestaShop",
      Description: "Payment module for PrestaShop.",
      PluginUrl: "/content/integrations/moonepayments-for-prestashop.zip",
      DocumentationUrl:
        "/content/integrations/Mo.one-Payments-PrestaShop-EN.pdf",
    },
    PaymentGateway: {
      Name: "Payment gateway",
      Description:
        "Merchant guide to the payment gateway: setup, access credentials and refunds.",
      DocumentationUrl: "/content/integrations/Mo.one-Payment-Gateway-CS.pdf",
    },
  },
  Paypoint: {
    Paypoint: "Paypoint",
    NoPaypoint: "No paypoint",
    NoPaypointPlaceholder: "No paypoint",
    UnnamedPaypoint: "Unnamed paypoint",
    AddPaypointButton: "Add new paypoint",
    DownloadQRCode: "Download QR",
    Individual: "Individual",
    SeeOptions: "See options",
    IndividualUnderConstruction:
      "Individual paypoints are still under construction, coming soon...",
    NewPaypoint: "New PayPoint",
    UpdatePaypoint: "Update PayPoint",
    IdlePaypointInfo: "No active transaction on this paypoint.",
    BackToDashboard: "Back to dashboard",
    LoadingError: "Error while loading paypoint.",
    UnknownOwner: "Unknown owner",
    Form: {
      Name: "Name",
      Description: "Description",
      Legend: {
        GeneralInfo: "General information",
        NFC: "NFC Card",
        PaypointSettings: "Paypoint settings",
        Device: "Payment device",
        PaypointType: "PayPoint settings",
      },
      PaymentTypeLabel: "Payment method",
      PaymentType: {
        Standard: {
          label: "Standard payment",
          tooltip: "You create the payment each time at the till.",
        },
        FixedPrice: {
          label: "Fixed price payment",
          tooltip:
            "The payment amount is preset and cannot be changed by the customer.",
        },
        EditablePrice: {
          label: "Editable price payment",
          tooltip:
            "Customer can choose from preset amounts or enter a custom one.",
        },
        NfcLink: {
          label: "NFC link",
          tooltip:
            "Tapping a phone on the NFC tag opens the address you enter instead of a payment.",
        },
        NfcTableService: {
          label: "NFC table service",
          tooltip:
            "Tapping a phone opens the table page. The address is set by Mo.one Hub and cannot be edited here.",
        },
      },
      FixedAmountLabel: "Fixed amount",
      CurrencyLabel: "Currency",
      FixedAmountRequired: "Enter a fixed amount greater than zero",
      CurrencyRequired: "Select the paypoint currency",
      TipsLabel: "Tips",
      TipsCheckbox: "Offer the customer a tip option",
      GatewaySettingsHint:
        "For an e-commerce gateway the amount and tips are controlled by the e-shop for each payment, so these settings are not available.",
      RedirectUrlLabel: "Address (URL)",
      RedirectUrlInvalid: "Enter a valid address starting with https://",
      TableServiceUrlMissing:
        "Mo.one Hub has not set the table address yet. Until it does, tapping a phone will not redirect anywhere.",
      NfcCardNo: "NFC card identifier",
      NfcCardNoHelperText:
        "The NFC card identifier is a 14-digit alphanumeric code printed on the {{appName}} NFC card. If you don't have the card yet you can use the paypoint virtually via QR code and pair it with the NFC card later.",
      Create: "Create",
      AssignNfcCardNoConflict: "NFC card already assigned to another paypoint",
      NfcCardNoFormatError:
        "NFC card identifier must be in the format XX-XX-XX-XX-XX-XX-XX",
      NameMaxLengthError: "Name must not exceed {{max}} characters",
      BankAccountNotAccessible:
        "Setting up a bank account is not available for you.",
      EmployeesNotAccessible:
        "Setting up employee access is not available for you.",
      Employees: "Employees with access",
      PaypointAccount: "Bank account",
      Facility: "Facility",
      Type: "Type",
      TypeNFC: "NFC",
      TypeTerminal: "Terminal",
      TypeGateway: "E-commerce gateway",
      TypeGatewayShort: "Gateway",
      Device: {
        NoActiveDevice: "No device linked yet",
        LinkDevice: "Link device",
        LinkDeviceTitle: "Link a new device",
        ActiveDevice: "Linked device",
      },
      Activation: {
        Legend: "SoftPOS activation",
        Code: "Activation code",
        HelperText:
          "Enter the code in the SoftPOS app on the device. It works for one device; when moving to another one, deactivate the app on the old device or ask support to free the code.",
        Copy: "Copy code",
        Copied: "Copied",
      },
    },
  },
  Roles: {
    AddRole: {
      Banner: {
        Hunter:
          "You have an invitation to become a hunter — finish the role registration.",
        Merchant:
          "You have an invitation to become a merchant — finish the role registration.",
        SignUpIntent: {
          Hunter: "Finish the hunter registration you chose at sign-up.",
          Merchant: "Finish the merchant registration you chose at sign-up.",
        },
        Button: "Finish",
      },
      Completion: {
        Submit: "Finish role registration",
        Success: "The role is now active.",
        Error: "Failed to finish the role.",
      },
      PickerTitle: "Choose the role you want to add to your account.",
      MigrationRequired:
        "Adding a role is available after your account migration is finished.",
      KycRequired:
        "Please finish the registration of your customer account first (identity verification). The Hunter or Merchant role can be added only after that.",
      BankVerificationRequired:
        "Please connect and verify your bank account first. The Hunter or Merchant role can be added only after that.",
      BackOfficeAccount:
        "An internal account (Admin, AML, onboarding) always holds a single role — no other role can be added to it. For personal use of Mo.one, create a separate account with a different e-mail address.",
      HunterCard: {
        Title: "Hunter",
        Description: "Register merchants and earn commissions.",
        AlreadyHeld: "You already have the hunter role.",
      },
      MerchantCard: {
        Title: "Merchant",
        Description: "Accept payments from customers in your business.",
      },
      HunterForm: {
        Description:
          "Enter the company number (IČO) of the business you will operate under as a hunter.",
      },
      MerchantForm: {
        Description:
          "Pick the market and enter the company number of the business that will accept payments.",
      },
      BankStep: {
        Connected: "Your bank is connected — you can continue.",
      },
      Form: {
        CompanyNumber: "Company number (IČO)",
        Region: "Market",
        Submit: "Add role",
        Success: "Role added, switching context…",
        Error: "Failed to add the role.",
      },
    },
  },
  Social: {
    Handle: {
      Label: "Handle",
      Placeholder: "e.g. jan.novak",
      ValidationError:
        "The handle must be 3–30 characters: lowercase a–z, digits, dot or underscore, with at least one letter.",
      Reserved: "This handle cannot be used",
      Available: "This handle is available",
      Taken: "This handle is already taken",
      SaveError: "The handle could not be saved. Please try again.",
      StepTitle: "Choose your public handle",
      StepDescription:
        "Your handle (@handle) is your public name in {{appName}}. Others can find you by it.",
      StepBenefitSearch:
        "Friends can easily find you in search by <strong>{{handle}}</strong> and your profile will be available at <strong>app.mo.one/@{{bareHandle}}</strong>",
      StepBenefitSearchHandleFallback: "@handle",
      StepBenefitSearchUrlFallback: "handle",
      StepBenefitFollows: "Others can follow you and send you follow requests",
      StepBenefitChat: "You can message friends and merchants",
      StepBenefitLeaderboard:
        "It will be shown instead of your name in the Stardust and Valor leaderboards",
      StepMissingWarning:
        "Without a handle, others can't find you and community features will be unavailable. You can also add it anytime in your profile.",
      ContinueWithoutHandle: "I'll add it later",
      ChangeNote:
        "Changing your handle releases the old one and changes your public profile URL.",
      MissingBannerText:
        "You haven't set up your handle (@handle) yet — others can't find you.",
      MissingBannerAction: "Set it up",
    },
    Terms: {
      CheckboxLabel:
        "I accept the <terms>terms and conditions</terms>, the <chatTerms>Chat terms</chatTerms> and the <communityRules>community rules</communityRules>",
      Note: "Chat is free and provided by Mo.one Digital Services s.r.o. You can terminate it any time in the App, and as a consumer also withdraw within 14 days by e-mail. How we handle your data is described in the <privacy>Privacy Policy</privacy>. Download and keep the <summary>contract summary</summary> and the documents, you may need them later.",
      ShowSummary: "Summary and documents",
      HideSummary: "Hide summary",
      AcceptAndContinue: "Accept and continue",
      Required:
        "You must accept the terms and conditions, the Chat terms and the community rules",
      Documents: {
        ContractSummary: {
          Title: "Contract summary",
          Url: {
            Customer: "/content/terms/Moone-chat-contract-summary-customer.pdf",
            Merchant: "/content/terms/Moone-chat-contract-summary-business.pdf",
            Hunter: "/content/terms/Moone-chat-contract-summary-business.pdf",
          },
        },
        Terms: {
          Title: "General terms and conditions",
          Url: {
            Customer: "/content/terms/Moone-chat-business-terms-customer.pdf",
            Merchant: "/content/terms/Moone-chat-business-terms-business.pdf",
            Hunter: "/content/terms/Moone-chat-business-terms-business.pdf",
          },
        },
        ChatTerms: {
          Title: "Chat product terms",
          Url: {
            Customer: "/content/terms/Moone-chat-terms-customer.pdf",
            Merchant: "/content/terms/Moone-chat-terms-business.pdf",
            Hunter: "/content/terms/Moone-chat-terms-business.pdf",
          },
        },
        CommunityRules: {
          Title: "Community rules",
          Url: {
            Customer: "/content/terms/Moone-chat-community-rules.pdf",
            Merchant: "/content/terms/Moone-chat-community-rules.pdf",
            Hunter: "/content/terms/Moone-chat-community-rules.pdf",
          },
        },
        ComplaintRules: {
          Title: "Complaints procedure",
          Url: {
            Customer: "/content/terms/Moone-chat-complaint-rules-customer.pdf",
            Merchant: "/content/terms/Moone-chat-complaint-rules-business.pdf",
            Hunter: "/content/terms/Moone-chat-complaint-rules-business.pdf",
          },
        },
        TechnicalTerms: {
          Title: "Technical terms",
          Url: {
            Customer: "/content/terms/Moone-chat-technical-terms.pdf",
            Merchant: "/content/terms/Moone-chat-technical-terms.pdf",
            Hunter: "/content/terms/Moone-chat-technical-terms.pdf",
          },
        },
        PrivacyPolicy: {
          Title: "Privacy policy",
          Url: {
            Customer: "/content/terms/Moone-chat-privacy-policy.pdf",
            Merchant: "/content/terms/Moone-chat-privacy-policy.pdf",
            Hunter: "/content/terms/Moone-chat-privacy-policy.pdf",
          },
        },
        ListLabel: "Documents",
      },
      Terminate: {
        Title: "Terminate Chat & social features",
        Description:
          "Terminating the Chat contract removes your handle and all community features — chat, public profile, search and following. Your messages and follows are kept and reappear if you choose a new handle later.",
        Action: "Terminate Chat & social features",
        ConfirmTitle: "Really terminate Chat & social features?",
        ConfirmText:
          "Your handle will be released and chat, public profile, search and following will become unavailable to you. The termination takes effect immediately. You can reactivate Chat any time by choosing a new handle and accepting the terms.",
        Success: "Chat & social features have been terminated.",
      },
      Summary: {
        Service: {
          Label: "Service",
          Value: "Messaging, groups and sharing in the {{appName}} app",
        },
        Provider: {
          Label: "Provider",
          Value:
            "Mo.one Digital Services s.r.o., IČO 245 32 819, Palackého třída 3048/124, 612 00 Brno, info@mo.one, +420 226 633 296",
        },
        Price: { Label: "Price", Value: "Free" },
        Duration: { Label: "Duration", Value: "Indefinite" },
        Termination: {
          Label: "Termination",
          Value:
            "Any time and without giving a reason in the App: My {{appName}} › Settings › Terminate Chat & social features. Effective as soon as you confirm it.",
        },
        Withdrawal: {
          Label: "Withdrawal",
          Value:
            "As a consumer within 14 days without giving a reason, an e-mail to info@mo.one is enough",
        },
        Payments: {
          Label: "Payments",
          Value: "Provided by Mo.one a.s. under your contract with it",
        },
        Complaints: {
          Label: "Complaints",
          Value:
            "Under the complaints procedure; out of court with the Czech Trade Inspection Authority",
        },
        Devices: {
          Label: "Devices",
          Value: "iOS 15 and later, Android 7 and later",
        },
        Important: {
          Label: "Important",
          Value:
            "Chat is not for emergency calls. The contract is concluded in Czech; you can correct the handle before confirming.",
        },
      },
    },
    PublicProfile: {
      PrivateAccount: "Private account",
      NotFound: "This handle doesn't exist.",
      Follow: "Follow",
      Following: "Following",
      FollowBack: "Follow back",
      Requested: "Requested",
      Mutual: "Mutual",
      ApproveRequest: "Approve request",
      DeleteRequest: "Delete request",
      MutualConfirmTitle: "Follow each other with @{{handle}}?",
      MutualConfirmInbox:
        "Your messages will go straight to each other's inbox, any message request between you will be accepted, and you'll be able to add each other to groups directly.",
      MutualConfirmName: "@{{handle}} will also see your real name.",
      SendMessage: "Send message",
      SendMessageError: "Couldn't open the conversation. Please try again.",
      SendMessagePhoneRequired:
        "Verify your phone number to start sending messages.",
      MoreActions: "More actions",
      Block: "Block",
      BlockConfirmTitle: "Block user",
      BlockConfirmDescription:
        "Blocking removes any follows and follow requests between you, and you won't be able to find or connect with each other again. You can unblock them anytime on the People page.",
      Report: "Report",
    },
    People: {
      SearchPlaceholder: "Search",
      SearchAllUsersPlaceholder: "Search all users by @handle or name",
      SearchNoResults: "No one found.",
      RequestsTitle: "Follow requests",
      OutgoingTitle: "Sent requests",
      Approve: "Approve",
      Delete: "Delete",
      CancelRequest: "Cancel",
      MutualTitle: "Mutual followers",
      NoMutuals:
        "No mutual followers yet — people you follow who follow you back appear here.",
      FollowingTitle: "Following",
      NoFollowing: "You aren't following anyone yet.",
      FollowersTitle: "Followers",
      Tabs: {
        Mutual: "Mutual",
        Following: "Following",
        Followers: "Followers",
      },
      NoFollowers: "No one follows you yet.",
      Unfollow: "Remove",
      BlockedTitle: "Blocked users",
      NoBlocked: "You haven't blocked anyone.",
      Unblock: "Unblock",
      HandleGateTitle: "Set your handle first",
      HandleGateText:
        "To follow people you need a handle (@nickname) — others use it to find you.",
      HandleGateAction: "Set handle",
    },
    PublicPermissions: {
      IdentityLabel: "Identity",
      IdentityVerifiedTooltip: "Identity verified",
      IdentityVerificationPendingTooltip: "Identity verification pending",
      PublicProfileLabel: "Public profile",
      PhoneVerifiedTooltip: "Phone number verified",
      PhoneVerificationPendingTooltip: "Phone verification pending",
      PhoneUnverifiedTooltip: "Phone number not verified",
      VerifyAction: "Verify",
      BioLabel: "Bio",
      BioTooLong: "Bio can be at most 160 characters",
      BioSaveError: "The bio could not be saved. Please try again.",
      SectionDescription:
        "With a public profile, others can see your real name and will be able to find you by it.",
      GoPublicConfirmTitle: "Make your profile public?",
      GoPublicConfirmRequests:
        "All {{count}} pending follow requests will be approved.",
      GoPublicConfirmMutual:
        "You already follow {{count}} of them, so you'll become mutual followers: their messages will go straight to your inbox and they'll be able to add you to groups directly.",
      GoPublicConfirmAction: "Make public",
      GoPublicSummaryUnavailable:
        "The pending follow requests could not be loaded, so the profile cannot be made public yet.",
    },
    Stats: {
      Mutual: "Mutual",
      Followers: "Followers",
      Following: "Following",
    },
  },
  Pages: {
    Titles: {
      AddRole: "Add role",
      Valor: "Valor",
      Notifications: "Notifications",
      Invitations: "Invitations",
      ThanksToYou: "Thanks to you!",
      IncomingTransaction: "Incoming payment",
      OutgoingTransaction: "Outgoing payment",
      UnknownTransaction: "Payment",
      Licenses: "Licenses",
      NewPayment: "New payment",
      MerchantOverview: "Merchant",
      Employees: "Employees",
      LanguageSettings: "Language settings",
      PayPoints: "Payment devices",
      BankAccountDetail: "Bank account",
      SetPassword: "Set password",
      AmlUsers: "AML users",
      NotificationsManagement: "Notifications management",
      NewNotification: "New notification",
      EditNotification: "Edit notification",
      Settings: "More",
      Stardust: "Stardust",
      People: "People",
      Followers: "Followers",
      BlockedUsers: "Blocked users",
      Profile: "Profile",
      Dashboard: "Overview",
      BankAccounts: "Bank accounts",
      BankAccountOrder: "Account order",
      FeeCalculator: "Fee calculator",
      Merchants: "Merchants",
      Settlement: "Settlement",
      Users: "Users",
      ToBeVerified: "To be verified",
      TransactionDetail: "Transaction detail",
      Welcome: "Welcome",
      User: "User",
      Withdrawal: "Withdrawal",
      Settlements: "Settlements",
      MerchantOpenRegistrations: "Open registrations",
      LicenseOrder: "License order",
      Onboarding: "Information about you",
      CompleteSSORegistration: "Complete registration",
      CompleteCustomerOnboarding: "Complete registration",
      LeadInvitation: "Invite friend",
      MerchantInitiatePayment: "Select account",
      MerchantAcceptPayment: "Accept payment",
      MerchantCompleteRegistration: "Registration",
      MerchantNewRegistration: "New merchant",
      LegalDocuments: "Documents",
      Redeem: "Gift card",
      AnonymousDashboard: "Overview",
      BankingAuthQr: "Bank account verification",
      SystemReport: "System report",
      SystemControl: "System settings",
      BankingAuth: "Bank account verification",
      UserAccount: "My {{appName}}",
      Help: "Help",
      ColdWallet: "System wallet",
      MerchantSettlements: "Settlements",
      AnonymousTransactionDetail: "Transaction was successful",
      MerchantFeeCalculator: "Fee calculator",
      RedeemPublic: "Gift card",
      ApplicationError: "Application error",
      GoogleCallback: "Redirecting",
      AwaitingEmailVerification: "E-mail verification",
      BankIDRedirect: "Redirecting",
      Campaigns: "Campaigns",
      Coupons: "Coupons",
      Terminals: "Terminals",
      Raffle: "Prize draws",
      SsoClients: "SSO applications",
      SsoAuthorize: "Sign in to application",
      ConnectedApps: "Connected applications",
      AdminCampaignCreate: "Create Campaign",
      AdminCampaignEdit: "Edit Campaign",
      Chat: "Chat",
      Devices: "Payment devices",
      PaymentGateways: "Payment gateways",
      Error403: "Access denied",
      DeactivateUser: "Delete account",
      CustomerAnonymousPaymentPay: "Sign in",
      TransactionsHistory: "Transactions",
      Contact: "Contact",
      WhereToPay: "Where to pay",
      RolesPermissions: "Roles & Permissions",
      AllMerchants: "All Merchants",
      UnassignedMerchants: "Merchants without hunter",
      Hunters: "Hunters",
      HunterPortal: "Hunter Portal",
      Transfer: "Transfer money",
      BatchPayments: "Batch Payments",
      BatchPaymentDetail: "Batch Payment Detail",
    },
    Content: {
      WhereToPay: {
        Tabs: {
          Stores: "Stores",
          EShops: "E-shops",
          Campaigns: "Campaigns",
        },
        StoresSection: {
          CategoryLabel: "Category",
          CategoryPlaceholder: "Category",
          CategoryAccessibility: "Filter by category",
          MunicipalityLabel: "Municipality",
          MunicipalityPlaceholder: "Municipality",
          MunicipalityAccessibility: "Filter by municipality",
          NoStoresFound: "No stores found matching the selected filters.",
        },
        Campaigns: {
          Title: "MO.ONE CAMPAIGNS",
          ShareLinkText: "Share link with others and help",
          CopyLink: "Copy link",
          LinkCopied: "Link copied",
          Description:
            "Try paying with Mo.one and help a good cause. Choose one of the partner foundations and donate any amount through Mo.one for the needy.",
        },
        StoreDetail: {
          NotFound: "Store not found",
          AboutUs: {
            Title: "ABOUT US",
            Address: "Address",
            Phone: "Phone",
            Email: "E-mail",
          },
          OpeningHours: {
            Title: "OPENING HOURS",
          },
        },
      },
      Stardust: {
        PointsLabel: {
          Points: "{{count}} Stardust",
          Points_other: "{{count}} Stardusts",
        },
        Participation: {
          Title: "Stardust participation",
          OptedInStatus: "You are enrolled in Stardust and earning points.",
          OptOutButton: "Leave the program",
          OptOutDialogTitle: "Leave Stardust?",
          OptOutDialogText:
            "After leaving you will not earn new Stardust points, neither for payments nor for referring friends. The points and rewards you already earned stay yours. You can rejoin at any time.",
          OptOutDialogConfirm: "Leave",
          OptedOutToast:
            "You have left the Stardust program. The points and rewards you earned stay yours.",
        },
        Consent: {
          Title: "Stardust loyalty program",
          Description:
            "You earn Stardust points for payments and activity in the app and receive a share of the monthly reward pool. Participation is voluntary and you can change it at any time in the Stardust section.",
          TermsLink: "Stardust program terms",
          Confirm: "I agree to join the program",
        },
        PilotDisclaimer:
          "The Stardust loyalty program is in pilot mode from December 1, 2025. We would be happy if you try Stardust and give us feedback using the Report a bug button or by emailing info@mo.one.",
        Overview: {
          Points: {
            title: "Stardust",
            explanationHint:
              "Stardust is a reward unit in {{appName}} for which you will receive a share of the reward package at the end of each month. The reward package is made up of hundredths of a percent of all payments made.<newline/><newline/> Our goal is to give some of the money back to the community through Stardust and thus give the opportunity to earn by paying through {{appName}} in a similar way to how banks and card institutions earn. <newline/><newline/><bold>At the end of each month, all Stardust is used to earn a share of the reward package. At the beginning of a new month, all users start with 0 Stardust.</bold>",
          },
          Payout: {
            title: "Payout",
            explanationHint:
              'At the end of each month, the Stardust loyalty program period closes and the earned money is credited to your "Payout".<newline/><newline/> Once you reach 50 CZK in payout, you can have the money sent to your account.',
          },
          Rewards: {
            title: "Rewards",
            explanationHint:
              "Total number of rewards from {{appName}} payments for the Stardust loyalty program in the current month.",
          },
          MyShare: {
            title: "My share",
            explanationHint:
              "Your current monthly share of rewards for Stardust, this is an estimated share based on the total number of Stardust and Rewards.<newline/><newline/> The actual share at the end of the month may differ from the current one.",
          },
          Rules: {
            Title: "Loyalty program - general rules",
            Link: "{{apiUrl}}/content/terms/Moone-stardust-general.pdf",
          },
          CurrentTerms: {
            Title: "Currently valid Stardust terms",
            Link: "{{apiUrl}}/content/terms/Moone-stardust-monthly-july-2026.pdf",
          },
        },
        Leaderboard: {
          Title: "Stardust Leaderboard",
        },
        Rewards: {
          Columns: {
            PointsLabel: "Stardust",
          },
        },
      },
      Valor: {
        PointsLabel: {
          Points: "{{count}} Valor",
          Points_other: "{{count}} Valors",
        },
        Overview: {
          Points: {
            title: "Valor",
            explanationHint:
              "Valor is a reward unit for Hunters in {{appName}}. You earn it by completing challenges – bringing new users, merchants and hunters.<newline/><newline/><bold>At the end of each month, all Valor is used to earn a share of the reward package. At the beginning of a new month, all Hunters start with 0 Valor.</bold>",
          },
          Payout: {
            title: "Payout",
            explanationHint:
              'At the end of each month, the Valor loyalty program period closes and the earned money is credited to your "Payout".<newline/><newline/> Once you reach 50 CZK in payout, you can have the money sent to your account.',
          },
          Rewards: {
            title: "Rewards",
            explanationHint:
              "Total number of rewards from {{appName}} for the Valor loyalty program in the current month.",
          },
          MyShare: {
            title: "My share",
            explanationHint:
              "Your current monthly share of rewards for Valor, this is an estimated share based on the total number of Valor and Rewards.<newline/><newline/> The actual share at the end of the month may differ from the current one.",
          },
          Rules: {
            Title: "Valor - general rules",
            Link: "{{apiUrl}}/content/terms/legione-valor-general-rules.pdf",
          },
          CurrentChallenges: {
            Title: "VALOR - currently active challenges",
            Link: "{{apiUrl}}/content/terms/legione-valor-monthly-july-2026.pdf",
          },
        },
        TeslaBanner: {
          Title: "Tesla Competition!",
          Description:
            "The most successful Hunter of the month (with the most Valor) can win a Tesla for a whole month!",
        },
        Leaderboard: {
          Title: "Valor Leaderboard",
        },
        Rewards: {
          Columns: {
            PointsLabel: "Valor",
          },
        },
      },
      SetPassword: {
        CreateNewPassword: "Create password",
        ResetPassword: "Reset password",
        Password: "Password",
        PasswordConfirmation: "Password confirmation",
        SetPassword: "Set password",
      },
      AmlUsers: {
        ExportUnderConstruction: "Export to file is coming soon.",
        ExportButton: "Export",
        Error:
          "An unexpected error occurred while loading. Please try again later.",
        TableHeaders: {
          Verification: "Verification",
          ClientIdentifier: "ID",
          Role: "User type",
          FirstName: "First Name",
          LastName: "Last Name",
          Company: "Company",
          PersonalNumber: "Personal number",
          PartyType: "Party type",
          RiskProfile: "Risk profile",
          DateCreated: "Date created",
        },
        Detail: {
          ConnectedBankAccounts: "Connected bank accounts",
          RiskProfileSettings: "Risk profile settings",
          LoadingError: "Error, user information couldn't be loaded",
          RiskProfileUpdateError: "Error when updating risk profile",
          RiskProfileUpdateSuccess: "Risk profile updated successfully.",
          ClientIdentifier: "Client ID",
          CopyClientIdentifier: "Copy Client ID",
          CopyClientIdentifierSuccess: "Client ID copied",
        },
      },
      LicenseDetail: {
        CancelOrderConfirmation: "Are you sure you want to cancel this order?",
        LoadingError: "Error occurred while loading license data",
        Type: "License type",
        LicenseNo: "License number",
        PaymentStatus: "Payment status",
        WalletAddress: "Wallet address",
        Country: "Country",
        PaymentPendingInfo:
          "The license will be activated after the payment is credited. If you have already paid, please wait for the payment to be credited.",
        CancelOrder: "Cancel order",
        OrderLicense: "Order license",
        AwaitingPayment: "Awaiting payment",
        LicenseStatus: {
          Initiated: "Payment initiated",
          Processing: "Processing",
          Success: "Paid",
          Fail: "Failed",
          Cancelled: "Cancelled",
        },
      },
      LegalDocuments: {
        previousVersions: "Previous versions",
        Documents: {
          Hunter: {
            digitalFranchiseContract: {
              title: "Digital franchise contract",
              url: "{{apiUrl}}/content/terms/legione-hunters-franchise-contract.pdf",
            },
            affiliateTerms: {
              title: "Affiliate terms",
              url: "{{apiUrl}}/content/terms/legione-affiliate-terms.pdf",
            },
            commissionTable: {
              title: "Commission table",
              url: "{{apiUrl}}/content/terms/legione-provisions.pdf",
            },
            valorVigorPointRules: {
              title: "Valor and Vigor point rules",
              url: "{{apiUrl}}/content/terms/legione-valor-vigor.pdf",
            },
            valorGeneralRules: {
              title: "Valor - general rules",
              url: "{{apiUrl}}/content/terms/legione-valor-general-rules.pdf",
            },
            nftCollectionRules: {
              title: "NFT collection rules",
              url: "{{apiUrl}}/content/terms/legione-NFT-collection.pdf",
            },
            generalFranchiseTerms: {
              title: "General franchise terms",
              url: "{{apiUrl}}/content/terms/legione-general-franchise-terms.pdf",
            },
            dataProtectionPrinciples: {
              title: "Data protection principles",
              url: "{{apiUrl}}/content/terms/individual/Moone-memorandum.pdf",
            },
            satisfactionGuarantee: {
              title: "Satisfaction guarantee",
              url: "{{apiUrl}}/content/terms/satisfaction-guarantee.pdf",
            },
            previousAffiliateTerms: {
              title: "Affiliate terms valid until 28. 9. 2025",
              url: "{{apiUrl}}/content/terms/legione-affiliate-terms-28-9-2025.pdf",
            },
            previousDataProtectionPrinciples: {
              title:
                "Personal data protection principles valid until 06. 11. 2025",
              url: "{{apiUrl}}/content/terms/legione-gdpr.pdf",
            },
            previousDataProtectionPrinciples2: {
              title:
                "Personal data protection principles valid until 01. 07. 2026",
              url: "{{apiUrl}}/content/terms/archive/Moone-memorandum-01-07-2026.pdf",
            },
            previousAffiliateTerms2: {
              title: "Affiliate terms valid until 08. 02. 2026",
              url: "{{apiUrl}}/content/terms/legione-affiliate-terms-08-02-2026.pdf",
            },
            previousGeneralFranchiseTerms: {
              title: "General franchise terms valid until 08. 02. 2026",
              url: "{{apiUrl}}/content/terms/legione-general-franchise-terms-08-02-2026.pdf",
            },
            previousNftCollectionRules: {
              title: "NFT collection rules valid until 08. 02. 2026",
              url: "{{apiUrl}}/content/terms/legione-NFT-collection-08-02-2026.pdf",
            },
            previousCommissionTable: {
              title: "Commission table - previous version",
              url: "{{apiUrl}}/content/terms/legione-provisions-30-04-2026.pdf",
            },
          },
          mooneLiteTerms: {
            title: "{{appName}} Lite Terms",
            url: "{{apiUrl}}/content/terms/Moone-moone-lite.pdf",
          },
          paymentServicesContract: {
            title: "Payment services contract",
            url: "{{apiUrl}}/content/terms/individual/Moone-payment-services-contract.pdf",
          },
          generalBusinessTerms: {
            title: "General business terms",
            url: "{{apiUrl}}/content/terms/individual/Moone-general-business-terms.pdf",
          },
          previousGeneralBusinessTerms: {
            title: "General business terms valid until 28. 10. 2025",
            url: "{{apiUrl}}/content/terms/individual/ZNPay-general-business-terms-28-10-2025.pdf",
          },
          merchantTerms: {
            title: "Merchant terms",
            url: "{{apiUrl}}/content/terms/individual/Moone-merchant-terms.pdf",
          },
          aisPisProductTerms: {
            title: "AIS-PIS product terms",
            url: "{{apiUrl}}/content/terms/individual/Moone-ais-pis.pdf",
          },
          settlementManual: {
            title: "Direct payment settlement manual",
            url: "{{apiUrl}}/content/terms/individual/Moone-pis-settlement.pdf",
          },
          priceList: {
            Partner: {
              title: "Price list - Partner",
              url: "{{apiUrl}}/content/pricelists/Moone-pricelist-partner-251201.pdf",
            },
            Prime: {
              title: "Price list - Prime",
              url: "{{apiUrl}}/content/pricelists/Moone-pricelist-prime-251201.pdf",
            },
            Standard: {
              title: "Price list",
              url: "{{apiUrl}}/content/pricelists/Moone-pricelist-standard-251201.pdf",
            },
            Individual: {
              title: "Price list - Special 0.49%",
              url: "{{apiUrl}}/content/pricelists/Moone-pricelist-discounted-individual.pdf",
            },
          },
          complaintRules: {
            title: "Complaint rules",
            url: "{{apiUrl}}/content/terms/individual/Moone-complaint-rules.pdf",
          },
          dataProtectionMemorandum: {
            title: "Personal data protection principles",
            url: "{{apiUrl}}/content/terms/individual/Moone-memorandum.pdf",
          },
          previousDataProtectionMemorandum: {
            title: "Data protection memorandum valid until 06. 11. 2025",
            url: "{{apiUrl}}/content/terms/archive/ZNPay-memorandum-06-11-2025.pdf",
          },
          previousDataProtectionMemorandum2: {
            title: "Data protection memorandum valid until 01. 07. 2026",
            url: "{{apiUrl}}/content/terms/archive/Moone-memorandum-01-07-2026.pdf",
          },
          precontractInformation: {
            title: "Precontract information",
            url: "{{apiUrl}}/content/terms/individual/Moone-single-precontract-terms.pdf",
          },
          thanksToYou: {
            title: '"Thanks to you!" program announcement',
            url: "{{apiUrl}}/content/terms/individual/Moone-thanks-to-you.pdf",
          },
          stardust: {
            title: "Stardust Loyalty program announcement",
            url: "{{apiUrl}}/content/terms/Moone-stardust-general.pdf",
          },
          stardustCurrentTerms: {
            title: "Rules - currently active challenges (July 2026)",
            url: "{{apiUrl}}/content/terms/Moone-stardust-monthly-july-2026.pdf",
          },
        },
      },
      Loyalty: {
        Tabs: {
          Challenges: "Challenges",
          Leaderboard: "Leaderboard",
          Rewards: "Rewards",
        },
        Challenges: {
          FeaturedChallenges: {
            Title: "Featured challenges",
            AllChallenges: "All challenges",
          },
          WeeklyChallenges: {
            Title: "Weekly challenges",
            TimeRemaining: "Time remaining: {{days}} days and {{hours}} hours",
          },
          MonthlyChallenges: {
            Title: "Monthly challenges",
            TimeRemaining: "Time remaining: {{days}} days and {{hours}} hours",
          },
          DailyChallenges: {
            Title: "Daily challenges",
          },
          LongTermChallenges: {
            Title: "Long-term challenges",
          },
          UnlimitedChallenges: {
            Title: "Unlimited challenges",
          },
          AllChallenges: {
            Title: "All challenges",
          },
        },
        Rewards: {
          Title: "Thanks to you!",
          Columns: {
            Date: "Date",
            Activity: "Activity",
          },
        },
        Notification: {
          Completed: "Challenge completed!",
        },
        Events: {
          [LoyaltyEventCode.HunterDailyAppOpen]: {
            title: "Daily app open",
            description: "User launches {{appName}} at least once a day",
          },
          [LoyaltyEventCode.HunterDailyAppOpenStreak3]: {
            title: "App open streak 3 days",
            description: "Login to the application 3 days in a row",
          },
          [LoyaltyEventCode.HunterDailyAppOpenStreak5]: {
            title: "App open streak 5 days",
            description: "Login to the application 5 days in a row",
          },
          [LoyaltyEventCode.HunterDailyAppOpenStreak10]: {
            title: "App open streak 10 days",
            description: "Login to the application 10 days in a row",
          },
          [LoyaltyEventCode.HunterDailyAppOpenStreak30]: {
            title: "App open streak 30 days",
            description: "Login to the application 30 days in a row",
          },
          [LoyaltyEventCode.HunterDailyAppOpenStreak180]: {
            title: "App open streak 180 days",
            description: "Login to the application 180 days in a row",
          },
          [LoyaltyEventCode.HunterDailyAppOpenStreak365]: {
            title: "App open streak 365 days",
            description: "Login to the application 365 days in a row",
          },
          [LoyaltyEventCode.Anniversary]: {
            title: "Anniversary",
            description: "Celebrate your anniversary with {{appName}}",
          },
          [LoyaltyEventCode.HunterReferUser]: {
            title: "Thanks to You!",
            description: "Invite and register a new user",
          },
          [LoyaltyEventCode.HunterRefer10]: {
            title: "Thanks to You 10!",
            description: "Invite and register 10 users",
          },
          [LoyaltyEventCode.HunterRefer50]: {
            title: "Thanks to You 50!",
            description: "Invite and register 50 users",
          },
          [LoyaltyEventCode.HunterRefer100]: {
            title: "Thanks to You 100!",
            description: "Invite and register 100 users",
          },
          [LoyaltyEventCode.HunterMerchantOnboarded]: {
            title: "New Addition!",
            description: "Onboard a new Merchant",
          },
          [LoyaltyEventCode.HunterMerchant1]: {
            title: "Beginner Hunter",
            description: "Register your first Merchant",
          },
          [LoyaltyEventCode.HunterMerchant10]: {
            title: "Experienced Hunter",
            description: "Register 10 Merchants",
          },
          [LoyaltyEventCode.HunterMerchant50]: {
            title: "Expert Hunter",
            description: "Register 50 Merchants",
          },
          [LoyaltyEventCode.HunterRecruitHunter]: {
            title: "Pack Catch",
            description: "Invite a new Hunter",
          },
          [LoyaltyEventCode.HunterRecruit3]: {
            title: "Hunter of Hunters",
            description: "Invite 3 new Hunters",
          },
          [LoyaltyEventCode.HunterRecruit10]: {
            title: "Experienced Hunter of Hunters",
            description: "Invite 10 new Hunters",
          },
          [LoyaltyEventCode.HunterMerchantNetwork]: {
            title: "Network Expansion",
            description: "Your merchant referred a new user",
          },
          [LoyaltyEventCode.HunterWeeklyMerchant]: {
            title: "Weekly Mission Start",
            description: "First registered Active Merchant this week",
          },
          [LoyaltyEventCode.HunterMonthlyMerchant]: {
            title: "Monthly Mission Start",
            description: "First registered Active Merchant this month",
          },
          [LoyaltyEventCode.HunterTerminalPlaced]: {
            title: "Terminal Placement",
            description: "Place a terminal at a merchant",
          },
          [LoyaltyEventCode.HunterTerminalPlaced3]: {
            title: "Place 3 Terminals",
            description: "Place 3 terminals at merchants",
          },
          [LoyaltyEventCode.HunterTerminalPlaced10]: {
            title: "Place 10 Terminals",
            description: "Place 10 terminals at merchants",
          },
          [LoyaltyEventCode.HunterTerminalOfTheWeek]: {
            title: "Terminal of the Week",
            description: "First placed terminal this week",
          },
          [LoyaltyEventCode.HunterTerminalOfTheMonth]: {
            title: "Terminal of the Month",
            description: "First placed terminal this month",
          },
          [LoyaltyEventCode.HunterClosedHunter]: {
            title: "Hunter Closure",
            description: "An invited Hunter purchased a license",
          },
          [LoyaltyEventCode.CustomerReferCustomer]: {
            title: "Affiliate referral",
            description: "Invite and register a new user",
          },
          [LoyaltyEventCode.CustomerReferCustomer10]: {
            title: "Thanks to You 10!",
            description: "Invite and register 10 users through Thanks to You!",
          },
          [LoyaltyEventCode.CustomerReferCustomer50]: {
            title: "Thanks to You 50!",
            description: "Invite and register 50 users through Thanks to You!",
          },
          [LoyaltyEventCode.CustomerReferCustomer100]: {
            title: "Thanks to You 100!",
            description: "Invite and register 100 users through Thanks to You!",
          },
          [LoyaltyEventCode.CustomerDailyAppOpen]: {
            title: "Daily app open",
            description: "Launch {{appName}} at least once a day",
          },
          [LoyaltyEventCode.CustomerDailyAppOpenStreak3]: {
            title: "App open streak 3 days",
            description: "Login to the application 3 days in a row",
          },
          [LoyaltyEventCode.CustomerDailyAppOpenStreak5]: {
            title: "App open streak 5 days",
            description: "Login to the application 5 days in a row",
          },
          [LoyaltyEventCode.CustomerDailyAppOpenStreak10]: {
            title: "App open streak 10 days",
            description: "Login to the application 10 days in a row",
          },
          [LoyaltyEventCode.CustomerDailyAppOpenStreak30]: {
            title: "App open streak 30 days",
            description: "Login to the application 30 days in a row",
          },
          [LoyaltyEventCode.CustomerDailyAppOpenStreak180]: {
            title: "App open streak 180 days",
            description: "Login to the application 180 days in a row",
          },
          [LoyaltyEventCode.CustomerDailyAppOpenStreak365]: {
            title: "App open streak 365 days",
            description: "Login to the application 365 days in a row",
          },
          [LoyaltyEventCode.Payment]: {
            title: "{{appName}} Payment",
            description: "Transaction made through {{appName}}",
          },
          [LoyaltyEventCode.PaymentStreak3]: {
            title: "Payment streak 3 days",
            description: "Pay every day for 3 days",
          },
          [LoyaltyEventCode.PaymentStreak5]: {
            title: "Payment streak 5 days",
            description: "Pay every day for 5 days",
          },
          [LoyaltyEventCode.PaymentStreak10]: {
            title: "Payment streak 10 days",
            description: "Pay every day for 10 days",
          },
          [LoyaltyEventCode.PaymentStreak30]: {
            title: "Payment streak 30 days",
            description: "Pay every day for 30 days",
          },
          [LoyaltyEventCode.PaymentStreak180]: {
            title: "Payment streak 180 days",
            description: "Pay every day for 180 days",
          },
          [LoyaltyEventCode.CustomerCompleteRegistration]: {
            title: "Complete registration",
            description: "Complete your user registration",
          },
          [LoyaltyEventCode.TenPayments]: {
            title: "Ten-timer",
            description: "Reach 10 {{appName}} payments",
          },
          [LoyaltyEventCode.FiftyPayments]: {
            title: "Fifty-timer",
            description: "Reach 50 {{appName}} payments",
          },
          [LoyaltyEventCode.HundredPayments]: {
            title: "Hundred-timer",
            description: "Reach 100 {{appName}} payments",
          },
          [LoyaltyEventCode.ThousandPayments]: {
            title: "Thousand times {{appName}}",
            description: "Reach 1,000 {{appName}} payments",
          },
          [LoyaltyEventCode.Explorer]: {
            title: "Explorer",
            description: "Payment at 5 different merchants",
          },
          [LoyaltyEventCode.Discoverer]: {
            title: "Discoverer",
            description: "Payment at 10 different merchants",
          },
          [LoyaltyEventCode.Traveler]: {
            title: "Traveler",
            description: "Payment at 50 different merchants",
          },
          [LoyaltyEventCode.FriendPayment]: {
            title: "Payment to friend",
            description: "First direct payment via {{appName}} to friend",
          },
          [LoyaltyEventCode.LargePayment]: {
            title: "Big payment",
            description: "First payment with {{appName}} over 1000 CZK",
          },
          [LoyaltyEventCode.BankConnection]: {
            title: "Bank connection",
            description: "First bank account connection",
          },
          [LoyaltyEventCode.FirstSpending]: {
            title: "First spend",
            description: "First direct payment via {{appName}} at merchant",
          },
          [LoyaltyEventCode.EngineIgnition]: {
            title: "Engine ignition",
            description: "First payment of the day (from all users)",
          },
          [LoyaltyEventCode.WeeklyMissionStart]: {
            title: "Weekly mission start",
            description: "First payment of the week (from all users)",
          },
          [LoyaltyEventCode.RepeatedUsage]: {
            title: "Repeated use",
            description: "Make 10 payments in a month",
          },
          [LoyaltyEventCode.MonthlyMissionStart]: {
            title: "Monthly mission start",
            description: "First payment of the month (from all users)",
          },
        },
      },
      UserDetailPageContent: {
        SaveSuccess: "User data saved successfully",
        SaveError: "Error occurred while saving user data",
        DuplicateLoginError: "This login is already in use by another user",
        AddressFields: {
          Legend: "Address",
          Type: "Address type",
          Street: "Street",
          StreetNumber: "Street number",
          OrientationNumber: "Orientation number",
          PostalCode: "Postal code",
          Municipality: "Municipality",
          Remove: {
            Button: "Remove address",
            ConfirmTitle: "Remove address",
            ConfirmContent: "Are you sure you want to remove this address?",
            SuccessMessage: "Address removed",
            ErrorMessage: "Error occurred while removing the address",
          },
        },
        Tabs: {
          User: {
            Title: "User",
            Form: {
              Login: "Login",
              Status: "Status",
              VerificationAdditionalInfoStatus: "Additional info status",
              VerificationIdentityVerificationStatus:
                "Identity verification status",
              VerificationRiskProfile: "Risk profile",
              VerificationBankVerificationStatus: "Bank verification status",
              BankVerificationSource: "Bank verification source",
              BankAccountNumber:
                "Verification bank account used for verification",
              VerificationDailyBuyLimit: "Daily limit",
              VerificationMonthlyBuyLimit: "Monthly limit",
              Stardust: {
                Legend: "Stardust loyalty program",
                NotAsked: "Not asked",
                OptedIn: "Enrolled",
                OptedOut: "Opted out",
                Withdrawn: "Left for good",
                ChangedAt: "Since {{date}} ({{source}})",
                Source: {
                  Registration: "registration",
                  ConsentDialog: "Stardust dialog",
                  ProgramSection: "Stardust section",
                },
              },
              ThanksToYou: {
                Legend: "Thanks to you! referral program",
                NotAsked: "Enrolled (no answer)",
                OptedIn: "Enrolled",
                OptedOut: "Declined",
                Withdrawn: "Left for good",
                ChangedAt: "Since {{date}} ({{source}})",
                Source: {
                  Registration: "registration",
                  ConsentDialog: "dialog",
                  ProgramSection: "Thanks to you! section",
                },
              },
              Legend: {
                Account: "Account",
                Verification: "Verification",
                Bank: "Bank",
                Limits: "Limits",
              },
            },
          },
          Party: {
            Title: "Party",
            SetVerified: "Verify identity",
            CancelVerification: "Remove identity verification",
            NoDocumentsUploaded: "This party has no documents uploaded",
            NoActingPersonLinked:
              "No acting person linked — documents unavailable",
            IdentityVerificationLegend: "Identity verification",
            IdentityVerified: "User identity has been verified",
            IdentityNotVerified: "User identity verification has been removed",
            Form: {
              SubmitSuccess: "Party data saved",
              SubmitError: "Error occurred while saving party data",
              Save: "Save",
              ClientIdentifier: "Client ID",
              CopyClientIdentifier: "Copy Client ID",
              CopyClientIdentifierSuccess: "Client ID copied",

              NaturalPerson: {
                Type: "Party type",
                FirstName: "First name",
                LastName: "Last name",
                PersonalNumber: "Personal number",
                BirthDate: "Birth date",
                Gender: "Gender",
                BirthPlace: "Birth place",
                Nationality: "Nationality",
                Phone: "Phone",
                VerifiedPhone: "Verified phone",
                Email: "E-mail",
                Legend: {
                  PersonalData: "Personal data",
                  Contact: "Contact",
                  Documents: "Documents",
                },
              },
              PartyIdentification: {
                Identification: "Identification type",
                IdentificationNumber: "Identification number",
                IdentificationValidFrom: "Valid from",
                IdentificationValidTo: "Valid to",
                IdentificationIssuer: "Issuer",
                NoIdentificationsUploaded:
                  "No uploaded documents found for this party.",
                IdentificationsDownloadPrefix: "identifications_",
                SecondDocumentTitle: "Second Document",
                AutomatedCheck: {
                  Title: "Automated document check",
                  NoChecks: "No automated check has run yet.",
                  LoadingError: "Failed to load the automated check results.",
                  Document: "Document",
                  Documents: {
                    Primary: "Primary document",
                    Secondary: "Second document",
                  },
                  Verdict: "Result",
                  Date: "Date",
                  DocumentType: "Type (Didit)",
                  RequestID: "Didit request ID",
                  Warnings: "Warnings",
                  Error: "Error",
                },
              },
              LegalEntity: {
                Type: "Party type",
                CompanyName: "Company name",
                CompanyNumber: "Company number",
                TaxNumber: "Tax number",
                Phone: "Phone",
                Email: "E-mail",
                Legend: {
                  Company: "Company",
                  Contact: "Contact",
                },
                RegistryLinks: {
                  Or: "Business registry",
                  Isir: "Insolvency registry",
                  Esm: "Owners registry",
                },
              },
              SelfEmployed: {
                Legend: {
                  Company: "Business",
                  Contact: "Contact",
                  Documents: "Documents",
                },
                Type: "Party type",
                CompanyName: "Company name",
                FirstName: "First name",
                LastName: "Last name",
                CompanyNumber: "Company number",
                TaxNumber: "Tax number",
                PersonalNumber: "Personal number",
                BirthDate: "Birth date",
                Gender: "Gender",
                BirthPlace: "Birth place",
                Nationality: "Nationality",
                Phone: "Phone",
                VerifiedPhone: "Verified phone",
                Email: "E-mail",
              },
            },
          },
          BankAccounts: {
            Title: "Bank accounts",
            Subtitle: "Connected bank accounts",
          },
          Licenses: {
            Title: "Licenses",
            Empty: "The user has no active license.",
            Transfer: {
              Button: "Transfer license",
              Title: "Transfer of license {{licenseNo}}",
              Description:
                "The new hunter takes over the merchants registered on the license, the previous hunter's whole affiliate network (referred customers and hunters) and all future rewards from them. Rewards already accrued and paid, the purchase invoice and closed history stay with the previous hunter (who keeps read access but no longer profits). The license wallet address remains unchanged.",
              TargetLabel: "New license holder",
              Submit: "Transfer license",
              Success: "License has been transferred",
              Error: "An error occurred while transferring the license",
            },
            Form: {
              Controls: {
                SubmitSuccess: "License data saved",
                SubmitError: "Error occurred while saving license data",
                Save: "Save",
                SendInvoice: "Send invoice",
                SendingInvoice: "Sending invoice...",
                SendInvoiceSuccess: "Invoice sent successfully",
                SendInvoiceError: "Error occurred while sending invoice",
                InvoiceSent: "Invoice sent",
                RegenerateInvoice: "Regenerate invoice",
                RegeneratingInvoice: "Regenerating invoice...",
                RegenerateInvoiceSuccess: "Invoice has been regenerated",
                RegenerateInvoiceError:
                  "An error occurred while regenerating the invoice",
              },
              Fields: {
                Code: "License type",
                LicenseNo: "License number",
                PaymentDate: "Payment date",
                PaymentStatus: {
                  label: "Payment status",
                  options: {
                    Initiated: "Payment initiated",
                    Processing: "Processing",
                    Success: "Paid",
                    Fail: "Failed",
                    Cancelled: "Cancelled",
                  },
                },
                WalletAddress: "Wallet address",
              },
            },
          },
          Verification: {
            Title: "Verification",
            Form: {
              Controls: {
                SubmitSuccess: "Verification data saved",
                SubmitError: "Error occurred while saving verification data",
                Save: "Save",
              },
              Fields: {
                TaxResidencyCountryID: "Tax residency country",
                BusinessActivity: "Business activity",
                OtherBusinessActivityDetails: "Specify business activity",
                SourceOfIncome: "Source of income",
                OtherSourceOfIncomeDetails: "Specify source of income",
                LegacySourceOfIncome: "Previous source of income",
                ExpectedMonthlyRevenue: "Expected monthly revenue",
                AverageTransactionValue: "Average transaction value",
                IsPoliticallyExposedPerson: "Politically exposed person",
                HasNonEUResidence:
                  "Residence outside the EU for more than 12 months (as of the registration date)",
                HasNonEUNationality: "Non-EU nationality",
              },
            },
          },
          Settlement: {
            Title: "Settlement",
          },
          HunterPrerequisites: {
            Title: "Hunter first steps",
            SelectAll: "Select all",
            DeselectAll: "Deselect all",
            LoadingError: "An error occurred while loading prerequisites.",
            SaveSuccess: "Changes saved successfully.",
            SaveError: "An error occurred while saving changes.",
            Note: "Note",
            NotePlaceholder: "Optional note...",
            AutoVerified: "Auto-verified",
            Items: {
              RegisterAsMerchant:
                "Registered as a Merchant and explored the application",
              InviteUserViaAffiliate: "Invited a new user via Thanks to You!",
              BanksAndPayments:
                "Connected at least 3 banks and made 30 payments",
              OfflineTraining: "Attended offline Mo.one training",
              BringMerchantsWithHunter:
                "Brought 3 Merchants in cooperation with an experienced Hunter",
            },
          },
          RelatedParties: {
            Title: "Related parties",
            NoRelatedParties: "No related parties found for this party.",
            Add: {
              Button: "Add related party",
              Title: "Add related party",
              RelationTypeLabel: "Relation type",
              BeneficialOwnerOption: "Beneficial owner",
              Submit: "Add",
              AddSuccess: "Related party added",
              AddError: "Error occurred while adding related party",
              LinkError:
                "The person was created but could not be linked to the company. Please try again — no duplicate person will be created.",
              DocumentsTitle: "ID documents",
              DocumentsIncomplete:
                "Please attach both sides of the first document and a complete second document.",
            },
            Delete: {
              Button: "Delete related party",
              ConfirmTitle: "Delete related party",
              ConfirmContent:
                "Are you sure you want to delete the related party {{name}}? This removes only this role.",
              SuccessMessage: "Related party deleted",
              ErrorMessage: "Error occurred while deleting related party",
            },
            Form: {
              Controls: {
                SubmitSuccess: "Related party data saved",
                SubmitError: "Error occurred while saving related party data",
                LoadingError: "Error occurred while loading related party data",
                Save: "Save",
              },
              Fields: {
                RelationType: "Relation type",
                IsOwner: "Is company owner",
                BeneficialOwnerFields: {
                  Legend: {
                    PersonalData: "Personal data",
                  },
                  FirstName: "First name",
                  LastName: "Last name",
                  BirthDate: "Birth date",
                  Nationality: "Nationality",
                },
                RelatedPartyFields: {
                  Legend: {
                    PersonalData: "Personal data",
                    Contact: "Contact",
                    Documents: "Documents",
                    FirstDocument: "First document",
                  },
                  DocumentsExpiringSoon: "Documents expiring soon",
                  DocumentsExpired: "Documents expired",
                  DocumentsValidityMissing: "Document validity missing",
                  FirstName: "First name",
                  LastName: "Last name",
                  PersonalNumber: "Personal number",
                  BirthDate: "Birth date",
                  Gender: "Gender",
                  BirthPlace: "Birth place",
                  Nationality: "Nationality",
                  Phone: "Phone",
                  Email: "E-mail",
                },
              },
            },
          },
        },
      },
      Contact: {
        HunterDiscord: {
          Description: "For quick communication, join ",
          Link: "Legi.one on Discord!",
        },
        Phone: "Phone",
        Email: "E-mail",
        PhoneDescription: "You can contact us by phone from 9:00 to 16:00.",
        CompanyInfo: {
          title: "Provider",
          description: "Mo.one a.s.",
          CompanyNumber: "Registration number: 02013517",
          TaxNumber: "Tax number: CZ02013517",
          CompanyAddress:
            "Registered at Palackého třída 3048/124, Královo Pole, 612 00 Brno, registered in the Commercial Register at the Regional Court in Brno, file number B 9099",
        },
        HunterCompanyInfo: {
          Title: "Provider",
          CompanyName: "LEGI.ONE LTD.",
          CompanyInfoLine1:
            "Registered in the United Kingdom under number 15846883",
          CompanyInfoLine2:
            "with its registered office at 20 Wenlock Road, London, England, N1 7GU",
          CompanyInfoLine3:
            "in connection and through its subsidiary in the Czech Republic:",
        },
        BankInfo: {
          Title: "Bank connection",
          CZKAccount: {
            Customer: "Fio bank: 2103087398/2010",
            Hunter: "CZK bank account: 1741535007/2700",
            Merchant: "Fio bank: 2103087398/2010",
            Admin: "Fio bank: 2103087398/2010",
            Aml: "Fio bank: 2103087398/2010",
            Employee: "Fio bank: 2103087398/2010",
            CustomerOnboarding: "Fio bank: 2103087398/2010",
            HunterPlus: "CZK bank account: 1741535007/2700",
            AdminLite: "Fio bank: 2103087398/2010",
            Disponent: "Fio bank: 2103087398/2010",
            Child: "Fio bank: 2103087398/2010",
          },
          EURAccount: {
            Customer: "",
            Hunter: "EUR bank account: 1741535015/2700",
            Merchant: "",
            Admin: "",
            Aml: "",
            Employee: "",
            CustomerOnboarding: "",
            HunterPlus: "EUR bank account: 1741535015/2700",
            AdminLite: "",
            Disponent: "",
            Child: "",
          },
        },
      },
      BankAccounts: {
        ConnectedBankAccounts: "All connected accounts",
        CurrencyGroupTitle: "Accounts in {{currency}}",
        BankIdentityVerificationAccount: "Identity verification account",
        InvalidTokenChip: "Access expired",
        AccountMenu: {
          Renew: "Renew access",
          RenewPayments: "Renew payments",
          Remove: "Disconnect account",
        },
        EditOrder: "Edit order",
        PrimaryAccount: "Primary account",
        PrimaryAccountDescription:
          "You set a primary account with the star, separately for each currency — it is the default account for receiving and sending payments in that currency. It cannot be set to an account in the <limitedChip /> or <awaitingVerificationChip /> state.",
        HunterPrimaryAccountDescription:
          "You set a primary account with the star, separately for each currency — we will send your rewards to the primary account of the given currency.",
        MissingLicenseHolderBankAccount:
          "To receive rewards, connect a bank account owned by the franchise license holder.",
        VerificationStatusesTitle: "Account verification statuses",
        QuickPayment: {
          Label: "Quick Payment",
          Description:
            "After loading a transaction, you will be automatically redirected to your bank to authorize payment with your primary account, skipping the transaction detail and bank selection steps.",
        },
        EditAccountOrderDescription:
          "You can change the order of your accounts by dragging them to the desired position.",
        NoBankAccounts: "No bank accounts connected",
        Error:
          "An unexpected error occurred while loading the bank accounts. Please try again later.",
        NoAccountSet: "No account set",
        DeleteAccount: {
          Title: "Delete account",
          Prompt: "Are you sure you want to delete this account?",
          ReplacementPrimaryAccount: "New primary account",
          PrimaryAccountWarning:
            "You are about to delete your <bold>primary account</bold>. Please select a new primary account.",
        },
        ManualBankAccountVerificationPrompt: {
          ManualVerificationNecessary:
            "This account needs to be verified manually",
          ActivateConfirmationPrompt:
            "Are you sure you want to activate this account? This action cannot be undone.",
          RejectConfirmationPrompt:
            "Are you sure you want to reject this account? This action cannot be undone.",
          Activate: "Activate",
          Reject: "Reject",
        },
        ConnectedBankAccount: {
          Owner: "Account owner",
          DateCreated: "Date created",
          Status: "Status",
          BankVerificationSource: "Bank verification source",
        },
        DeletedVerificationAccount: {
          Error: "The verification account could not be loaded.",
        },
        Detail: {
          IBAN: "IBAN",
          AccountOwners: "Account owners",
          DateConnected: "Date connected",
          AccountNotFound: "This bank account could not be found.",
          BackToList: "Back to bank accounts",
          IsPrimaryNote: "This is your primary bank account.",
          Actions: {
            SetAsPrimary: "Set as primary",
            PrimaryAccountDescription:
              "Primary account is default for receiving and submitting payments.",
            AuthorizeAccess: "Authorize access {{bankName}}",
            AuthorizePayments: "Authorize payments {{bankName}}",
            AuthorizationValidUntil: "Authorization valid until {{date}}",
          },
          IbanCopySuccess: "IBAN copied",
          Alerts: {
            Rejected: {
              Title: "Account rejected",
              Description:
                "This account has been rejected and cannot be used in the app.",
            },
            Inactive: {
              Title: "Account inactive",
              Description:
                "This account is inactive. Contact support if you want to use it again.",
            },
            Restricted: {
              Title: "Complete identity verification",
              Description:
                "To fully use this account, please complete your identity verification.",
              Action: "Complete verification",
            },
            AwaitingVerification: {
              Title: "Verify your account connection",
              Description:
                "This account's information needs to be verified by someone from the {{appName}} team. This usually happens when the account owner's name couldn't be automatically matched with your name.",
            },
            PreApproved: {
              Title: "Your account is pre-approved",
              Description:
                "You can accept account-to-account payments up to {{limit}} before your account is fully verified — {{remaining}} remaining. The limits are removed automatically once verification is complete.",
            },
            TokenInvalidAis: {
              Title: "Account access expired",
              Description:
                "Access to account information has expired. Renew the connection so we can fetch balance and transactions.",
              Action: "Renew access",
            },
            TokenExpiringAis: {
              Title: "Account access expiring soon",
              Description:
                "Access to account information will expire soon. Renew it so we can keep loading your balance.",
              Action: "Renew access",
            },
            TokenInvalidPisp: {
              Title: "Payment authorization expired",
              Description:
                "Authorization to initiate payments from this account has expired. Renew it to keep sending payments.",
              Action: "Renew payments",
            },
            TokenExpiringPisp: {
              Title: "Payment authorization expiring soon",
              Description:
                "Authorization to initiate payments from this account will expire soon. Renew it to keep sending payments.",
              Action: "Renew payments",
            },
          },
        },
      },
      Help: {
        HelpInPreparation:
          "A dedicated help page is currently under construction.",
        ContactEmail:
          "If you have any questions, you can contact us by e-mail at",
      },
      MerchantFeeCalculator: {
        YearlySavingsWidget: {
          PotentialYearlySavings: "Potential yearly savings",
          WithLegione: "With Legi.one up to:",
          WithMoone: "With {{appName}} up to:",
        },
        CurrentTotalFee: "Current total fee",
        ProposedTotalFee: "Proposed total fee",
        HideFeeTable: "Hide detailed fee table",
        ShowFeeTable: "Show detailed fee table",
        MonthlyTurnover: "Monthly turnover",
        AverageTransactionValue: "Average transaction value",
        MooneDirectPayShare: "{{appName}} direct payment share",
        Currency: "CZK",
        FeeSettings: {
          CurrentFees: "Current fees",
          ProposedFee: "Proposed fee",
          DirectPaymentWithMoone: "Direct payment with {{appName}}",
          ProviderFee: "Provider fee",
          Acquiring: "(Acquiring)",
          FixedTransactionCost: "Fixed transaction cost",
          MooneFee: "{{appName}} fee",
        },
        CardTypeRatioSlider: {
          Title: "Card type ratio",
          ToggleButtons: {
            MostlyPersonal: "Mostly personal",
            SomeCommercial: "Bigger share of commercial",
            MostlyCommercial: "Mostly commercial",
          },
          CardTypeRatios: {
            Personal: "Personal",
            Commercial: "Commercial",
          },
        },
        CardRatesTable: {
          Expand: "Show detailed fee table",
          Collapse: "Hide detailed fee table",
          Headers: {
            CardType: "Card type",
            Interchange: "Interchange",
            SchemeFee: "Scheme fee",
            CurrentTotalRate: "Current total rate",
            ProposedTotalRate: "Proposed total rate",
          },
        },
        CostsChart: {
          Title: "Transaction costs",
          HighContrast: "High contrast",
          AnnualTransactionCosts: "Annual transaction costs",
          TickLabels: {
            CurrentCosts: {
              Mobile: "Now",
              Full: "Current costs",
              Description: "Average fee",
            },
            LegioneCosts: {
              Mobile: "Legi.one",
              Full: "Costs with Legi.one",
              Description: "Average fee",
            },
            MooneCosts: {
              Mobile: "{{appName}}",
              Full: "Costs with {{appName}}",
              Description: "Fee",
            },
          },
        },
      },
      Merchants: {
        StatusWidgets: {
          OpenRegistrations: "Open registrations",
          InvitationsSent: "Invitations sent",
          InOnboarding: "In onboarding",
          Completed: "Completed",
        },
        Error:
          "An unexpected error occurred while loading the merchants. Please try again later.",
        TableHeaders: {
          Status: "Status",
          CompanyName: "Company name",
          CompanyNumber: "Company ID",
          LicenseType: "License type",
          LicenseOwner: "License owner",
          LicenseNumber: "License number",
          DateCreated: "Date created",
          TransactionsCount: "Transactions count",
        },
        OrbitLOnboarding: {
          Title: "OrbitL Onboarding",
          ActionRequiredBadge: "Action Required",
          LoadingError: "Failed to load OrbitL onboarding data",
          Table: {
            CompanyNumber: "IČO",
            CompanyName: "Company Name",
            Hunter: "Hunter",
            ActivatedAt: "OrbitL Activated",
            Phase: "Phase",
            ComgateStatus: "Comgate Status",
            ComgateMID: "MID",
            TimeSinceSubmit: "Time Since Submit",
            LastStatusChange: "Last Status Change",
            FinancingModel: "Financing",
            Hours: "{{count}}h",
            Days: "{{count}}d",
            TerminalCount: "Terminals",
            TerminalCountRequest: "Count change request",
            OnlyOpenRequests: "Only with an open request",
          },
          TerminalCountChange: {
            SectionTitle: "Terminal count",
            OpenRequestHeading: "Open change request",
            RequestedBy: "Requested by {{user}}",
            SystemActor: "system",
            ContractTotal: "Contracted count: {{count}}",
            ContractSoftPosTotal: "SoftPOS: {{count}}",
            ContractLevelChange: "Total count",
            SoftPosChange: "SoftPOS {{from}} → {{to}}",
            SoftPosTotal: "SoftPOS: {{from}} → {{to}}",
            HistoryHeading: "Change history",
            NoHistory: "No terminal count changes yet",
            ResolveButton: "Resolved",
            DismissButton: "Dismiss",
            ResolveHint:
              "Resolving only closes the request — change the count itself through the facility edit.",
            DismissDialogTitle: "Dismiss request",
            DismissNoteLabel: "Reason for dismissal",
            DismissNoteRequired: "Enter a reason for the dismissal",
            DismissConfirm: "Dismiss request",
            Cancel: "Cancel",
            ResolveSuccess: "Request marked as resolved",
            DismissSuccess: "Request dismissed",
            Error: "Failed to process the request",
            Source: {
              MerchantSelfService: "Merchant",
              Admin: "Support",
              InitialOrder: "Order",
            },
            Status: {
              Applied: "Applied",
              Pending: "Awaiting support",
              Resolved: "Resolved",
              Dismissed: "Dismissed",
            },
          },
          Detail: {
            Title: "OrbitL Onboarding Detail",
            CloseButton: "Close",
            ChecklistSection: "Checklist",
            RejectionSection: "Comgate Errors",
            TerminalsSection: "Terminal Assignment",
            CommentsSection: "Comgate Comments",
            NoTerminals: "No terminals assigned",
            NoComments: "No comments yet",
            SendNoteLabel: "Send Note to Comgate",
            SendNoteButton: "Send",
            SendNotePlaceholder: "Type your message...",
            SendNoteSuccess: "Comment sent successfully",
            SendNoteError: "Failed to send comment",
            ResubmitButton: "Resubmit to Comgate",
            ResubmitConfirmTitle: "Confirm Resubmit",
            ResubmitConfirmText:
              "This will resubmit the merchant's data and documents to Comgate. Make sure all issues have been resolved before proceeding. The status will be set to REQUESTED.",
            ResubmitConfirmButton: "Resubmit",
            ResubmitCancelButton: "Cancel",
            ResubmitSuccess: "Successfully resubmitted to Comgate",
            ResubmitError: "Failed to resubmit to Comgate",
            CommentAuthor: "Author",
            CommentDate: "Date",
            SwapTerminal: "Swap",
            SwapTerminalTitle: "Swap Terminal",
            SwapTerminalSearch: "Search by serial number...",
            SwapTerminalConfirm: "Swap terminal",
            SwapTerminalCancel: "Cancel",
            SwapTerminalSuccess: "Terminal swapped successfully",
            SwapTerminalSuccessWithCode:
              "Terminal swapped. Activation code {{code}} is ready for the new device",
            SwapTerminalError: "Failed to swap terminal",
            SwapTerminalNoResults: "No terminals available",
            SwapTerminalTmsUpdateWarning:
              "This terminal is already registered in Switchio TMS. The serial number swap will also be applied in TMS.",
            TerminalStatusDeployed: "Registered in TMS",
            Tid: "TID",
            TidTypesSection: "Terminal types (Comgate TIDs)",
            TidTypesHint:
              "The type of these TIDs could not be determined. Set it; the other TIDs are registered without them. A TID beyond the order cannot be typed until the contract's terminal or SoftPOS count is raised.",
            TidTypePhysical: "Physical terminal",
            TidTypeSoftPos: "SoftPOS",
            TidTypeUnknown: "Not set",
            TidTypeManual: "Manual",
            SaveTidTypes: "Save types",
            SaveTidTypesSuccess: "TID types saved",
            SaveTidTypesError: "Failed to save TID types",
            SaveTidTypesExceedError:
              "Selected types exceed the contract counts (physical / SoftPOS). Raise the count on the contract first.",
            RegisterTmsButton: "Run TMS registration",
            RegisterTmsSuccess:
              "TMS registration: {{registered}}/{{expected}} terminals",
            RegisterTmsError: "TMS registration failed",
            ActivationCode: "Activation code",
            ReactivateCodeButton: "Free the code for a new device",
            ReactivateCodeSuccess:
              "Device binding removed, the activation code can be used again",
            ReactivateCodeError: "Could not remove the device binding",
          },
          FindByTerminal: {
            Button: "Search terminal",
            Title: "Find merchant by terminal",
            SearchLabel: "Search by TID or serial number...",
            NoResults: "No terminals found",
            Close: "Close",
          },
        },
      },
      Users: {
        VerificationCell: {
          AdditionalInfo: "Additional info",
          IdentityVerification: "Identity verification",
          RiskProfile: "Risk profile",
          BankVerification: "Bank verification",
        },
        ExcludeAnonymous: "Exclude anonymous users",
        Error:
          "An unexpected error occurred while loading the users. Please try again later.",
        TableHeaders: {
          Verification: "Verification",
          Login: "Login",
          Role: "Role",
          Status: "Status",
          FirstName: "First Name",
          LastName: "Last Name",
          Company: "Company",
          ClientIdentifier: "ID",
          DateCreated: "Date Created",
          PaymentReference: "License Payment Reference",
          Version: "{{appName}} Version",
          DocumentExpiration: "Document Expiration",
          Settlement: "Settlement",
          Admin: "Admin",
        },
        Filters: {
          ExpiringWithin30Days: "Expiring within 30 days",
          Expired: "Expired",
        },
        ConnectedBankAccounts: {
          LoadingError: "Error while loading connected bank accounts.",
          NoBankAccounts: "User has no connected bank  accounts.",
          UnassignedContext: "Not assigned to a role (older connection)",
        },
        ToBeVerified: {
          Subtitles: {
            UserVerification: "Users to be verified",
            BankVerification: "Users with bank accounts to be approved",
          },
          AdditionalInfo: "Additional Info",
          RiskProfile: "Risk Profile",
          BankVerification: "Bank Verification",
          IdentityVerification: "Identity Verification",
        },
        RolesPermissions: {
          TableHeaders: {
            RoleName: "Role Name",
            RoleCode: "Role Code",
            AccessRightCode: "Access Right",
            CanRead: "Read",
            CanCreate: "Create",
            CanUpdate: "Update",
            CanDelete: "Delete",
            CanExport: "Export",
          },
          NoAccessRights: "No access rights",
          AccessRightSingular: "access right",
          AccessRightPlural: "access rights",
        },
      },
      Dashboard: {
        BankAccountsWidget: {
          Title: "Connected accounts",
          ConnectionFailed: "Failed to load connected accounts.",
          NoActiveAccounts: "No active accounts to display.",
        },
        PreApprovalWidget: {
          Title: "Pre-approval limit",
          Description:
            "Until we fully verify your account, the following limits apply:",
          LimitPerTransaction:
            "maximum amount of a single transaction <amount>{{amount}}</amount>",
          LimitTotal: "total limit <amount>{{amount}}</amount>",
          Remaining: "{{remaining}} remaining",
        },
        TransactionFees: {
          Admin: {
            allTime: "Total transaction earnings",
            currentMonth: "Earnings this month",
            lastMonth: "Earnings last month",
          },
        },
      },
      Welcome: {
        RegistrationSuccess:
          "We have created your user account. Below you can read how to start receiving salary in bitcoins.",
        UnverifiedAccountLimits:
          "Since your account is not yet verified, you have low transaction limits set. Additional verification can be done later in your profile.",
        TransactionLimitsLink: "Transaction limits",
        Complete: "Complete",
      },
      AnonymousDashboard: {
        RegistrationRequired:
          "Please sign up to access the full version of the application",
        Register: "Sign up",
      },
      Redeem: {
        Step1: "1. Scratch off the cover layer with a coin",
        Step2: "2. Copy the 16-digit code from the card",
        NoCardYet: "Don't have a card yet? You can buy it",
      },
      RedeemPublic: {
        Step1: "1. Scratch off the cover layer with a coin",
        Step2: "2. Copy the 16-digit code from the card",
      },
      LeadInvitation: {
        Description:
          "Enter the e-mail of a friend you want to invite. If your friend actively uses the account, you will get a reward for them!",
      },
      ColdWallet: {
        WithdrawalTitle: "Withdrawal from system wallet:",
        DepositTitle: "Deposit to system wallet:",
        GenerateAddress: "Generate new system address",
      },
      SystemReport: {
        HotWallet: "+ To Hot Wallet",
        ColdWallet: "+ To Cold Wallet",
        ExchangeWithdrawal: " + To exchange withdrawal ",
        UserAccounts: "- To user accounts",
        ControlSum: "Control sum",
        SystemWalletWithdrawal: "To withdrawal from system wallet",
        ExchangeWithdrawalFee: "Exchange withdrawal fee",
        BalanceOnExchange: "{{currencyCode}} balance on exchange",
        TradingVolume: "Trading volume",
        PerMonth: "Per month",
        PerWeek: "Per week",
        PurchasedBTC: "Purchased BTC",
        WithdrawnOrPending: "Withdrawn or pending",
        BTCLedgerSum: "BTC ledger sum",
        CZKLedgerSum: "CZK ledger sum",
        SystemVersion: "System version: {{version}}",
      },
      BankingAuth: {
        BankListTitle: "Bank offer",
        BanksInPreparation: "Banks in preparation",
      },
      Account: {
        ProfileError:
          "We apologize, an unexpected error occurred while displaying your profile, please try again later.",
      },
      Error403: {
        Logout: "Logout",
      },
      CustomerAnonymousPaymentPay: {
        SignIn: "Sign in.",
        ConfirmBox: {
          Title: "The receipt is already on its way to your e-mail",
          Description:
            "Do you want to pay with one tap next time and have all payments in one place? Create your own {{appName}} account, it will be a breeze! 🚀",
          CreateAccountButton: "Create account",
          CloseButton: "Close",
        },
        TermsAgreement: {
          text: "By clicking the pay button, you agree to the",
          link: "{{apiUrl}}/content/terms/1-one-time-payment-docs.pdf",
          linkText: "AIS and PIS Terms of Service",
        },
        Steps: {
          detail: "Welcome to {{appName}}",
          bank: "Bank Connection",
          account: "Payment with {{appName}}",
        },
        BankSelection: {
          InfoBox:
            'Select a bank for payment via {{appName}} according to our "Bank Rating".',
          WarningBox:
            "Warning: Connection quality depends on banks. If you have a problem with login or payment, contact your bank.",
        },
        AccountSelection: {
          InfoBox:
            "Authorize the payment with your bank. In case of authorization problems, contact your bank.",
        },
      },
      NotificationsManagement: {
        NewNotification: "New notification",
        PlannedNotifications: "Planned notifications",
        SentNotifications: "Sent notifications",
        DisabledNotifications: "Disabled notifications",
        NoNotifications: "No notifications",
      },
      NotificationForm: {
        Name: "Name",
        Description: "Description",
        NotificationType: "Notification type",
        Duration: {
          FromDate: "Publishing date",
          FromTime: "Publishing time",
          ToDate: "Hiding date",
          ToTime: "Hiding time",
        },
        DurationType: {
          Dismiss: "Until the user dismisses it",
          Period: "For a time period",
        },
        DurationPrompt: "How long should the notification remain visible?",
        Filters: {
          Banks: "Only for banks:",
          UserTypes: "Only for users:",
          BanksPlaceholder: "No bank filter",
          UserTypesPlaceholder: "No user filter",
        },
        Link: "Link",
        Create: "create",
        Preview: "Preview",
      },
      Settlements: {
        Error:
          "An unexpected error occurred while loading the settlements. Please try again later.",
        SettlementTrigger: "Start settlement of fees for the current period.",
        SettlementTriggerError:
          "An error occurred while triggering the settlement. Please try again later.",
        SettlementTriggerSuccess: "Settlement triggered successfully.",
        SettlementTriggerLoading: "Starting settlement...",
        SettlementTriggerButtonText: "Start settlement",
      },
    },
  },
  Sentry: {
    FormTitle: "Report Error",
    NameLabel: "Name",
    EmailLabel: "E-mail",
    MessageLabel: "Error Description",
    MessageCharCount: "{{count}} / {{maxCount}}",
    CancelButtonLabel: "Cancel",
    SubmitButtonLabel: "Submit",
    NamePlaceholder: "Your name",
    EmailPlaceholder: "Your e-mail",
    MessagePlaceholder: "Describe the error",
    SuccessMessageText: "Thank you for reporting the error.",
    ErrorMessageText: "The report could not be sent. Please try again.",
    Validation: {
      MessageRequired: "Please describe the error.",
      MessageTooShort:
        "The description must be at least {{minLength}} characters long.",
      EmailInvalid: "Enter a valid e-mail.",
    },
  },
  Support: {
    ButtonLabel: "Support",
    Contact: {
      PhoneNumber: "+420 226 633 296",
      Email: "info@mo.one",
      HunterEmail: "info@legi.one",
    },
    Choice: {
      Title: "Get in touch",
      Description:
        "Please describe your request or problem as precisely as possible, it will help us get back to you sooner.",
      TechnicalProblemButton: "Technical problem",
      TechnicalProblemDescription:
        "App crashes, failed payments, a page that won't load. Goes to engineering.",
      TechnicalProblemUnavailable:
        "Error reporting is currently unavailable. Please send a support request instead.",
      SupportRequestButton: "Support request",
      SupportRequestDescription:
        "Billing, account, orders, how something works. Answered by customer support.",
      UrgentHelp: "In a hurry?",
      CallPhone: "Call {{phoneNumber}}",
    },
    Request: {
      Title: "Support request",
      CategoryLabel: "Request category",
      CategoryPlaceholder: "Select a category",
      SubjectLabel: "Request title",
      DescriptionLabel: "Description",
      DescriptionCharCount: "{{count}} / {{maxCount}}",
      AttachmentLabel: "Attachment",
      AttachmentPlaceholder: "Upload a file",
      AttachmentRemove: "Remove attachment",
      NameLabel: "Full name",
      EmailLabel: "E-mail",
      SubmitButton: "Submit",
      CancelButton: "Cancel",
      Categories: {
        ClientOnboarding: "Client onboarding",
        CommissionsAndPayouts: "Commissions and payouts",
        ValorPoints: "Valor points",
        TechnicalIssues: "Technical issues (sign-in / app)",
        StardustPoints: "Stardust points",
        PaymentIssues: "Payment issues",
        AccountAndSignIn: "Sign-in and account management",
        TransactionComplaints: "Transaction complaints",
        HardwareAndConnectivity: "Hardware and connectivity",
        FinanceAndSettlement: "Finance and settlement",
        AccountAdministration: "Administration and account management",
        Other: "Other",
      },
      Validation: {
        CategoryRequired: "Select a request category.",
        SubjectRequired: "Enter a request title.",
        DescriptionRequired: "Enter a description.",
        DescriptionTooShort:
          "The description must be at least {{minLength}} characters long.",
        NameRequired: "Enter your full name.",
        EmailRequired: "Enter your e-mail.",
        EmailInvalid: "Enter a valid e-mail.",
        AttachmentTooLarge: "The file can be at most {{maxSizeMb}} MB.",
        AttachmentTypeNotSupported: "Supported formats are PNG, JPG and PDF.",
      },
      SuccessMessage:
        "Your request has been sent. We will get back to you as soon as possible.",
      ErrorMessage: "The request could not be sent. Please try again.",
      RateLimitedMessage:
        "You've sent too many requests. Please try again in a few minutes.",
    },
    ContentReport: {
      Title: "Report content",
      PickerButton: "Report a profile or message",
      PickerDescription:
        "A profile or message that breaks the law or our terms. Reviewed by the Mo.one team under the Digital Services Act (DSA).",
      TargetProfile: "You are reporting the profile {{user}}.",
      TargetMessage: "You are reporting a message from {{user}}.",
      HandleLabel: "Username of the profile you are reporting",
      HandlePlaceholder: "e.g. jane.doe",
      MessageHint:
        "To report a chat message, open the message's menu in the conversation and choose Report.",
      CategoryLabel: "Reason for the report",
      CategoryPlaceholder: "Select a reason",
      ExplanationLabel: "Explanation",
      ExplanationHelper:
        "Describe as precisely as you can what is wrong with the content — what exactly it contains and, if you consider it illegal, which law it breaks.",
      NameLabel: "Full name",
      EmailLabel: "E-mail",
      ContactOptionalNote:
        "Reports of child sexual abuse may be filed anonymously. Without an e-mail we cannot tell you the outcome.",
      BonaFideLabel:
        "I confirm that, to the best of my knowledge, the information and allegations in this notice are accurate and complete.",
      LegalNote:
        "You will receive a confirmation of receipt and our decision by e-mail. Notices are handled under Art. 16 of Regulation (EU) 2022/2065 (DSA).",
      SubmitButton: "Submit notice",
      CancelButton: "Cancel",
      Categories: {
        ChildSexualAbuse: "Child sexual abuse",
        ThreatsOrViolence: "Threats, violence or terrorism",
        HateSpeech: "Hate speech",
        FraudOrScam: "Fraud or scam",
        Impersonation: "Impersonation",
        PersonalDataOrPrivacy: "Misuse of personal data or privacy",
        IntellectualProperty: "Copyright or trademark infringement",
        OtherIllegal: "Other illegal content",
        HarassmentOrBullying: "Harassment or bullying",
        SpamOrUnwantedContact: "Spam or unwanted contact",
        OtherTermsViolation: "Other violation of the terms",
      },
      Validation: {
        CategoryRequired: "Select a reason for the report.",
        HandleRequired: "Enter the username of the profile you are reporting.",
        ExplanationRequired: "Enter an explanation.",
        ExplanationTooShort:
          "The explanation must be at least {{minLength}} characters long.",
        NameRequired: "Enter your full name.",
        EmailRequired: "Enter your e-mail.",
        EmailInvalid: "Enter a valid e-mail.",
        BonaFideRequired:
          "The notice cannot be submitted without this statement.",
      },
      SuccessMessage:
        "Your notice has been received. We will review it without undue delay and let you know the outcome by e-mail.",
      SuccessMessageWithReference:
        "Your notice has been received under reference {{referenceNumber}}. A confirmation is on its way to your e-mail; we will let you know the outcome.",
      ErrorMessage: "The notice could not be sent. Please try again.",
      NotFoundMessage: "We couldn't find that profile. Check the username.",
      SelfReportMessage: "You cannot report your own content.",
      RateLimitedMessage:
        "You've sent too many notices. Please try again in a few minutes.",
    },
    Result: {
      ErrorTitle: "Something went wrong",
    },
  },
  Employee: {
    Create: {
      Title: "Add employee",
      CreateButton: "Add employee",
      CreateError: "An error occurred while creating the employee.",
      CreateSuccess: "Employee created",
      MissingMerchantID: "Error - Merchant ID is not available.",
      Legend: "New employee",
    },
    EmployeeStatus: {
      Inactive: "Inactive",
      InvitedWaitingForConfirmation: "Invited, waiting for confirmation",
      Active: "Active",
      Created: "Created",
    },
    Detail: {
      Title: "Employee detail",
      NoEmployeeSelectedError:
        "Error - Employee not selected or does not exist",
      EmployeeNotFoundError: "Error - Employee does not exist",
      SendAccess: "Send access",
      CancelInvitation: "Cancel invitation",
      CancelInvitationConfirmText:
        "The invitation will be withdrawn and the employee will no longer be able to accept it. You can send a new one at any time.",
      RemoveEmployee: "Remove employee",
      RemoveEmployeeConfirmText:
        "The employee loses access to your company and can no longer switch into the employee context. The record stays in the list as inactive and you can send access again at any time.",
      RemovedInfo:
        "This employee was removed. Send access to invite them again.",
      SaveError: "An error occurred while saving the employee.",
      SaveSuccess: "Employee saved successfully.",
      AccessSent: "Access sent",
    },
    List: {
      LoadingError: "Employees list could not be loaded.",
      AddEmployee: "Add employee",
      FilterLabels: {
        Created: "Created",
        InvitedWaitingForConfirmation: "Access sent",
        Active: "Active",
        Inactive: "Inactive",
      },
    },
    Dashboard: {
      Title: "Dashboard",
    },
    KeypadPayment: {
      Title: "Keypad payment",
      ComingSoon: "We are preparing the keypad payment.",
    },
  },
  TransactionErrors: {
    UnknownError: "An unknown error occurred while processing the transaction",
    UnknownPaymentError: "Unknown error during payment",
    TransactionNotFound: "Transaction does not exist or is unavailable",
    BankTemporarilyUnavailable:
      "The bank is temporarily unavailable, please try again in a moment.",
    BankConnectionExpired:
      "The bank connection has expired, please connect the account again.",
    InsufficientBalance: "Insufficient balance on account",
    BalanceVerificationFailed: "Failed to verify account balance",
    SameAccountError: "Sender and recipient account cannot be the same",
    InvalidAccountCurrency: "Invalid account currency",
    InvalidTransactionCurrency: "Invalid transaction currency",
    TransactionTypeNotAllowed:
      "Transaction for this account type is not allowed",
    TransactionLimitExceeded: "Transaction limit exceeded",
    UnspecifiedError: "An unspecified error occurred.",
    TransactionProcessingFailed:
      "Failed to process transaction, or it has already been paid",
    UnspecifiedPaymentError: "An unspecified error occurred",
    BankAccount: "No primary bank account set",
    User: "{{appName}} Lite users can send payments only to merchants",
    InvalidBankAccountForReceivingPayment:
      "The selected bank account cannot receive payments",
    ActiveTransactionExists:
      "There is already an active transaction on this paypoint. Please wait for it to complete before initiating a new one.",
    AuthLimitExceeded: "Authorization limit exceeded",
    InvalidJsonFormat: "Invalid JSON format",
    GeneralRejection: "General rejection",
    NotUniqueTransactionReference: "Non-unique transaction reference",
    InvalidCharacterSet: "Invalid character set",
    InvalidAccountNumber: "Invalid account number",
    FieldInvalid: "Invalid field value",
    FieldMissing: "Required field is missing",
    TransactionAmountForbidden: "Transaction amount is forbidden",
    AccountInvalid: "Recipient or sender account is invalid",
    TransactionDecimalNumbersForbidden: "Forbidden number of decimal places",
    InvalidDate: "Invalid date",
    TransactionInvalidIBAN: "Invalid IBAN of recipient",
    InstantPaymentsNotAvailable: "Instant payments are not available",
    TransactionNotSignable: "Transaction cannot be authorized",
    DebtorBankAccountForbidden:
      "The sender's account is not allowed for this transaction.",
    PaymentLimitExceeded:
      "Payment failed – the account limit has been exceeded.",
    PaymentRejectedByBank: "The bank rejected the payment.",
    PaymentCancelledByUser: "The payment was not confirmed in the bank.",
    PaymentExpired: "The payment was not completed in time and was cancelled.",
    PaymentUnauthorized:
      "The bank did not authorize the payment. Reconnect the account and try again.",
    PaymentTechnicalError:
      "The payment failed due to a technical error. Please try again or contact support.",
    DebtorCreditorAccountIdentical:
      "Recipient and payer accounts are identical",
    TransactionLimits: {
      generatedTransactionLimitExceeded:
        "Generated payment exceeds the limit for 1 transaction.",
      oneTimeSendLimitExceeded: "Amount exceeds the limit for 1 transaction.",
      dailySendLimitExceeded: "Transaction amount exceeds daily limit.",
      generatedDailySendLimitExceeded:
        "Generated transaction amount exceeds daily limit.",
      monthlySendLimitExceeded:
        "Transaction amount exceeds total monthly limit.",
      generatedMonthlySendLimitExceeded:
        "Generated transaction amount exceeds monthly limit.",
      generatedTransactionLimitExceededIncompleteRegistration:
        "Generated payment exceeds the limit for 1 transaction. Please complete registration.",
      oneTimeSendLimitExceededIncompleteRegistration:
        "Amount exceeds the limit for 1 transaction. Please complete registration.",
      dailySendLimitExceededIncompleteRegistration:
        "Transaction amount exceeds daily limit. Please complete registration.",
      generatedDailySendLimitExceededIncompleteRegistration:
        "Generated transaction amount exceeds daily limit. Please complete registration.",
      monthlySendLimitExceededIncompleteRegistration:
        "Transaction amount exceeds monthly limit. Please complete registration.",
      generatedMonthlySendLimitExceededIncompleteRegistration:
        "Generated transaction amount exceeds monthly limit. Please complete registration.",
      oneTimeReceiveLimitExceededPreApproval:
        "The payment exceeds the per-payment limit allowed during pre-approval.",
      cumulativeReceiveLimitExceededPreApproval:
        "The payment would exceed the total amount you can receive during pre-approval. You will be able to receive more once your bank account has been verified.",
      currencyNotSupportedPreApproval:
        "During pre-approval you can only receive payments in CZK. You will be able to receive other currencies once your bank account has been verified.",
      unspecifiedError: "An unspecified error occurred.",
    },
    Status: "Transaction was cancelled.",
    TransactionAlreadyCompleted: "This transaction has already been completed.",
    TransactionCancelled: "This transaction has been cancelled.",
    TransactionExpired:
      "This payment has expired. Please ask the recipient to issue a new one.",
    TransactionNotFoundOrNotAccessible:
      "Transaction {{transactionPublicID}} does not exist or is unavailable.",
  },
  Campaigns: {
    Campaign: {
      DefaultHeader: "Support This Campaign",
      DefaultLearnMore: "Learn More",
      DonationForm: {
        AmountLabel: "Enter donation amount",
        AmountRequired: "Enter amount",
        AmountMin: "Amount must be at least {{minAmount}} {{currencySymbol}}",
        SubmitButton: "Donate",
        TransactionError: "Failed to create transaction. Please try again.",
      },
      Page: {
        Loading: "Loading...",
        CampaignNotFound: "Campaign Not Found",
        CampaignNotFoundMessage:
          "Campaign not found or is no longer available.",
        StatusNotStarted: "Campaign Not Started Yet",
        StatusNotStartedWithDate:
          "This campaign will start on {{date}}. Please check back then to make a donation.",
        StatusNotStartedWithoutDate: "This campaign has not started yet.",
        StatusFinished: "Campaign Has Ended",
        StatusFinishedWithDate:
          "This campaign ended on {{date}}. Thank you for your interest!",
        StatusFinishedWithoutDate:
          "This campaign has ended. Thank you for your interest!",
        EndsOn: "Ends on {{date}}",
        DonationsNotAvailable: "Donations are not available at this time.",
      },
      CreateAccountButton: "CREATE ACCOUNT IN ONE CLICK",
      InfoCards: {
        HelpingNeedy: {
          Title: "Helping those in need",
          Description:
            "We are gradually adding foundations that we help with {{appName}}.",
        },
        TestingPayment: {
          Title: "Testing payment method",
          Description:
            "You will help test the new payment method of the Czech company {{appName}}, which helps keep money in the Czech Republic.",
        },
        CreateAccountAndHelp: {
          Title: "Create an account and help",
          Description:
            "Create an account, pay with {{appName}} and we will contribute to your chosen organization for every payment you make.",
        },
      },
      TimelineSteps: {
        Donate: {
          Title: "Donate any amount to the selected organization",
        },
        JoinTeam: {
          Title: "Create an account and join the {{campaignName}} team!",
          Description:
            "By creating an account, you can pay with {{appName}}, compete in the Stardust competition, and help the {{campaignName}} team.",
        },
        PayAtCafe: {
          Title: "Pay with {{appName}} at your favorite cafe",
          Description: "ADD TEXT LATER",
        },
        AnyMethod: {
          Title: "Cash, card? Only with {{appName}}!",
          Description: "ADD TEXT LATER",
        },
      },
    },
  },
  Notifications: {
    Variants: {
      Critical: "Critical warning",
      Error: "Error",
      Announcement: "Announcement",
      Completed: "Completed",
      Stardust: "Stardust/Valor",
      Raffle: "Prize draw",
    },
  },

  UserMenu: {
    MooneLiteInfo:
      "If you are registered, then {{appName}} pays for your payments.",
    ManageAccount: "Manage {{appName}} account",
    Invitations: "Invitations",
    Logout: "Logout",
    Rewards: {
      TotalRewards: "Total rewards",
      TotalStardust: "Total stardust",
      TotalValor: "Total valor",
      MyShare: "My share",
      OpenRewardsDetails: "Open rewards details",
    },
    Notifications: {
      Title: "Notifications",
      NoNewNotifications: "No new notifications",
      NoNotifications: "No notifications",
      Today: "Today",
      Yesterday: "Yesterday",
      Older: "Older",
      ShowAll: "All notifications",
      DeleteAll: "Dismiss all",
    },
  },
  Raffle: {
    Notifications: {
      Title: "Prize draw",
      Registration:
        "You earned {{count}} tickets for completing your registration.",
      Registration_one:
        "You earned {{count}} ticket for completing your registration.",
      Registration_other:
        "You earned {{count}} tickets for completing your registration.",
      Referral:
        "You earned {{count}} tickets — the friend you invited completed their registration.",
      Referral_one:
        "You earned {{count}} ticket — the friend you invited completed their registration.",
      Referral_other:
        "You earned {{count}} tickets — the friend you invited completed their registration.",
      Payment: "You earned {{count}} tickets for a merchant payment.",
      Payment_one: "You earned {{count}} ticket for a merchant payment.",
      Payment_other: "You earned {{count}} tickets for a merchant payment.",
    },
    Dashboard: {
      Title: "Prize draw",
      TicketCount: "Your tickets",
      DrawDate: "Draw",
      DateToBeAnnounced: "Date to be announced",
      ShowDetail: "Show detail",
      CompetitionsRunning: "{{count}} competitions running",
      CompetitionsRunning_one: "{{count}} competition running",
      CompetitionsRunning_other: "{{count}} competitions running",
    },
    Page: {
      Title: "Prize draw",
      NotRunning: "No prize draw is running right now.",
      LoadError: "Failed to load the prize draw.",
      MainDraw: "Main draw",
      MyTickets: "Your tickets",
      MoveTickets: "Move tickets",
      DrawDate: "Draw date",
      Rewards: "Prizes",
      History: "Past draws",
      HistoryEmpty: "You have not taken part in any draw yet.",
      HistoryTickets: "{{count}} tickets",
      HistoryTickets_one: "{{count}} ticket",
      HistoryTickets_other: "{{count}} tickets",
      Drawn: "Drawn",
    },
    MoveDialog: {
      Title: "Move tickets",
      TargetLabel: "To competition",
      CountLabel: "Number of tickets",
      CountHelper: "Enter a number from 1 to {{max}}",
      Submit: "Move",
      MovedToast: "{{count}} tickets moved",
      MovedToast_one: "{{count}} ticket moved",
      MovedToast_other: "{{count}} tickets moved",
      MoveError: "Failed to move the tickets",
      NothingMoved: "Nothing was moved — your tickets changed in the meantime.",
      CompetitionsChanged:
        "The competitions changed in the meantime — nothing was moved. Check the current state.",
    },
  },
  Chat: {
    Title: "{{appName}} Chat",
    FindPeople: "Find people",
    ReadBy: "Seen by {{count}}",
    SelectConversation: "Select a conversation",
    ClosedConversation: "This conversation is closed.",
    Unavailable: "Chat is not available for your account yet.",
    DisclosedNamesUnavailable:
      "Names couldn't be loaded — some people may show as @handle for now.",
    BackToDashboard: "Back to {{appName}}",
    PushNotifications: {
      DeviceToggleLabel: "Chat notifications on this device",
      DeviceToggleDescription:
        "Get notified about new messages even when the app is closed.",
      DeniedHint:
        "Notifications for {{appName}} are turned off. Enable them in your system settings.",
    },
    Thread: {
      Title: "Thread",
    },
    FindInConversation: {
      MenuItem: "Find in conversation",
      Placeholder: "Search this conversation",
      Counter: "{{current}} of {{total}}",
      NoMatches: "No matches",
      Older: "Older match",
      Newer: "Newer match",
      Close: "Close search",
      JumpError: "Could not go to the message, please try again",
      SearchError: "Search failed",
    },
    HandleGate: {
      Title: "Choose your handle",
      Description: "Others in Chat use it to find you and message you.",
    },
    Reactions: {
      Title: "Reactions",
    },
    PhoneGate: {
      Title: "Phone number verification",
      DescriptionVerify:
        "To use chat, please verify your phone number. It helps keep chat free of spam and fake accounts.",
      DescriptionAdd:
        "To use chat, please add and verify your phone number. It helps keep chat free of spam and fake accounts.",
      PhoneLabel: "Phone number",
      SendCode: "Send code",
      CodeSent: "Code sent to {{phone}}",
      Verify: "Verify",
      EditPhone: "Change phone number",
      ResendCode: "Resend code",
      SmsFailed: "Failed to send verification code",
      TooManyAttempts: "Too many attempts, please try again later",
      WrongCode: "Wrong code. Please request a new one.",
      VerifySuccess: "Phone number verified successfully",
    },
    Layout: {
      SearchPlaceholder: "Search...",
    },
    Categories: {
      All: "All",
      Mutual: "Mutual",
      Shopping: "Shopping",
      Merchants: "Merchants",
      Groups: "Groups",
      Requests: "Requests",
    },
    NoChannels: "Nothing here yet",
    NoSearchResults: "No conversations match your search",
    NewChat: {
      Title: "New conversation",
      DirectTitle: "New chat",
      CreateGroupWith: "Create a group chat with {{name}}",
      GroupTitle: "New group chat",
      OpenError: "Couldn't open the conversation. Please try again.",
      PrivateSenderNotice:
        "Your profile is private — the person you message will see your name.",
      RemoveMember: "Remove {{name}}",
      SearchPlaceholder: "Search by @handle or name",
      MembersLabel: "Members",
      SuggestionsLabel: "Suggestions",
      ResultsLabel: "Search results",
      EligibilityLabel: "Who can join",
      EligibilityAnyone: "Anyone (Moone Lite and Full Moone)",
      EligibilityFullMooneOnly: "Verified users only (Full Moone)",
      FullMooneOnlyBadge: "Verified users only",
      CreateError: "Couldn't create the group. Please try again.",
      CreateIneligibleMemberError:
        "Someone you picked isn't a verified (Full Moone) user and can't join a verified-only group.",
      IneligibleSelectedWarning:
        "Greyed-out people aren't verified (Full Moone) users and can't join a verified-only group. Remove them or let anyone join.",
      CustomerSelectedWarning:
        "Greyed-out people are customers. Merchants and hunters can't start a conversation with customers or add them to a group — a customer has to contact you first. Remove them to continue.",
      GroupNameLabel: "Group name",
      ChannelNamePlaceholder: "Conversation name (optional)",
      NoResults: "No results found",
      UnknownUser: "Unknown user",
      Cancel: "Cancel",
      Create: "Create",
    },
    Members: {
      Title: "Members",
      You: "You",
      AddTitle: "Add member",
      Add: "Add",
      Cancel: "Cancel",
      AddError: "Failed to add member",
      Remove: "Remove",
      RemoveError: "Failed to remove member",
      Owner: "Owner",
      AlreadyMember: "Already a member",
      NotFullMoone: "Not verified",
      CustomerWritesFirst: "Customer needs to contact you first",
      LeaveChannel: "Leave channel",
      LeaveConfirmTitle: "Leave channel?",
      LeaveConfirmMessage:
        "Are you sure you want to leave this channel? You will no longer receive messages from this conversation.",
      Leave: "Leave",
      LeaveError: "Failed to leave channel",
    },
    Roles: {
      Customer: "User",
      Merchant: "Merchant",
      Hunter: "Hunter",
      HunterPlus: "Hunter",
      Admin: "Administrator",
      CustomerOnboarding: "Customer support",
    },
    Rename: {
      Title: "Rename conversation",
      Save: "Save",
      Cancel: "Cancel",
      Error: "Failed to rename conversation",
    },
    Requests: {
      Accept: "Accept",
      Reject: "Reject",
      RequestMessage: "{{name}} wants to chat with you",
      AcceptError: "Failed to accept chat request",
      RejectError: "Failed to reject chat request",
      ShowRejected: "Rejected invites",
      ShowPending: "Back to requests",
      Join: "Join",
      JoinError: "Failed to join channel",
      RejectedMessage: "You rejected this invite",
    },
    PaymentRequest: {
      Pending: "Awaiting payment",
      Pay: "Pay",
      Drawer: {
        Title: "Payment request",
        BankAccount: "Bank account",
        Amount: "Amount",
        Note: "Add note",
        NotePlaceholder: "Note text",
        Submit: "Send request",
        Error: "Failed to create request",
        InvalidCurrency:
          "The selected account is denominated in a different currency than the request can be created in",
        Validation: {
          SelectBankAccount: "Select a bank account",
          EnterAmount: "Enter an amount",
          AmountMustBePositive: "Amount must be positive",
        },
      },
    },
    DirectPayment: {
      Sent: "Payment sent",
      Received: "Payment received",
      Drawer: {
        Title: "Direct payment",
        BankAccount: "Bank account",
        Amount: "Amount",
        Note: "Add note",
        NotePlaceholder: "Note text",
        Submit: "Send payment",
        Error: "Failed to send payment",
        RecipientNoPrimaryAccount:
          "Recipient has no active primary bank account",
        InvalidCurrency:
          "The recipient's account is denominated in a different currency",
        RecipientCurrencyNotSupported:
          "The recipient can only receive CZK payments for now",
        RecipientLimitExceeded:
          "This payment would exceed the recipient's receiving limit",
        NotChannelMember:
          "You can only send a payment to a member of this conversation",
        MessageNotPosted:
          "The payment was sent, but it could not be posted to the conversation",
      },
    },
    MultiPayerRequest: {
      Description: "Payment request for everyone",
      ThreadTitle: "Group payment",
      Pay: "Pay",
      Close: "Close",
      CloseError: "Failed to close the request",
      Closed: "Closed",
      Unavailable: "Unavailable",
      Payments: {
        Count: "{{count}} payments",
        Count_one: "{{count}} payment",
        Count_other: "{{count}} payments",
      },
      Drawer: {
        Title: "Payment request",
        Amount: "Amount per person",
        Hint: "Every member of the group can pay this amount to your primary account, any number of times. The payments appear under the request.",
        Submit: "Send request",
        Error: "Failed to send the request",
      },
    },
    PaymentMenu: {
      DirectPayment: "Send payment",
      PaymentRequest: "Pay me",
      LiteRestriction:
        "Payments in chat are available once you complete your registration.",
      LiteBankRestriction:
        "We already have your identity details. Payments in chat will be available once your bank account is connected and verified.",
      LitePayUnavailable: "Complete your registration to pay",
      RecipientNotFullMoone:
        "This user hasn't completed their registration yet, so payments can't be sent to them.",
    },
    TransactionStatus: {
      Created: "Created",
      Initiated: "Initiated",
      Processing: "Processing",
      Success: "Success",
      Fail: "Failed",
      Cancelled: "Cancelled",
      Unknown: "Unknown",
    },
    Preview: {
      PaymentRequest: "Payment request",
      DirectPayment: "Direct payment",
    },
    PushCaption: {
      PaymentRequest: "Payment request",
      DirectPaymentSent: "Sent",
    },
  },
  Admin: {
    Terminals: {
      PageTitle: "Terminals",
      Table: {
        SerialNumber: "Serial number",
        Tid: "TID",
        Mid: "MID",
        HardwareModel: "Terminal type",
        Status: "Status",
        Financing: "Financing",
        Hunter: "Hunter",
        Merchant: "Merchant",
        Facility: "Facility",
        PurchasePrice: "Purchase price",
        LastChangedAt: "Last change",
        Ship: "Ship",
      },
      Status: {
        InStock: "In stock",
        AssignedToHunter: "With hunter",
        AssignedToMerchant: "Assigned",
        Shipped: "Shipped",
        WithMerchant: "With merchant",
        Deployed: "Deployed",
        Returned: "Returned",
      },
      Financing: {
        Moone: "Mo.one",
        Hunter: "Hunter",
      },
      Form: {
        SerialNumber: "Serial number",
        HardwareModel: "Terminal type",
        PurchasePrice: "Purchase price",
        Currency: "Currency",
        Status: "Status",
        AddTitle: "Add terminal",
        Save: "Save",
        Cancel: "Cancel",
        Delete: "Delete",
      },
      AssignHunter: {
        HunterLabel: "Hunter",
        SearchHint: "Name, company or e-mail",
        NoOptions: "No hunters found",
      },
      Validation: {
        SerialNumberRequired: "Serial number is required",
        HardwareModelRequired: "Terminal type is required",
      },
      Audit: {
        Title: "Change history",
        Empty: "No changes yet",
        When: "When",
        Field: "Field",
        Change: "Change",
        Who: "Who",
      },
      Messages: {
        LoadError: "Failed to load terminals",
        UpdateSuccess: "Terminal saved",
        CreateSuccess: "Terminal was added",
        UpdateError: "Failed to save terminal",
        DuplicateSerialNumber:
          "A terminal with this serial number already exists",
        DeleteSuccess: "Terminal was deleted",
        DeleteError: "Failed to delete terminal",
        DeleteConfirm:
          "Do you really want to delete terminal {{serialNumber}}? Use only for records created by mistake.",
        ShipSuccess: "Terminal was marked as shipped",
        ShipError: "Failed to mark the terminal as shipped",
      },
    },
    SsoClients: {
      PageTitle: "SSO applications",
      CreateButton: "Register application",
      Table: {
        ClientId: "Client ID",
        DisplayName: "Name",
        Type: "Type",
        Confidential: "With backend",
        Public: "Public (PKCE)",
        RedirectUris: "Redirect URIs",
        AllowedRoles: "May act as",
        AllowedScopes: "API scopes",
        Actions: "Actions",
        RotateSecret: "Generate a new secret",
        Delete: "Delete",
      },
      Form: {
        CreateTitle: "Register application",
        EditTitle: "Edit application {{clientId}}",
        ClientId: "Client ID",
        DisplayName: "Name (shown to the user on consent)",
        IsConfidential:
          "The application has its own backend (gets a client secret)",
        IsConfidentialHint:
          "Without a backend (SPA, mobile) the application is public: PKCE only, no secret, cannot act for users. The type cannot be changed later.",
        RedirectUris: "Redirect URIs",
        RedirectUrisHint:
          "Exact match, https (http only for localhost). Enter adds another.",
        PostLogoutRedirectUris: "Post-logout redirect URIs",
        AllowedRoles: "Roles it may act as",
        AllowedRolesHint:
          "Empty = the application only signs people in. With a role it gets the context token exchange.",
        AllowedScopes: "API scopes",
        AllowedScopesHint:
          "What the application may call. “Application: …” scopes are for a backend without a user (client_credentials), the others for acting on behalf of a signed-in user. Empty = sign-in and contexts only.",
        ListPlaceholder: "Type a value and press Enter",
        Submit: "Register",
        Save: "Save",
        Cancel: "Cancel",
      },
      Validation: {
        ClientIdInvalid:
          "3–64 characters: lower-case letters, digits and dashes",
        DisplayNameRequired: "Name is required",
        RedirectUriRequired: "At least one redirect URI is required",
        RedirectUriInvalid:
          "A redirect URI must be an absolute https address without a fragment (http only for localhost)",
        ScopeRequiresRole:
          "An API scope needs at least one role the application acts as",
      },
      Scopes: {
        Payments: "Payments (payment API)",
        Merchant: "Merchant (staff, pay-points, PINs, company records)",
        Hunter: "Hunter (own terminals, points, affiliate link)",
        TerminalsManage: "Application: terminal inventory (read and write)",
        DirectoryRead:
          "Application: user and hunter directories, a hunter's merchants",
        CouponsCreate: "Application: issuing coupons",
        LoyaltyRead: "Application: loyalty programme (periods, leaderboard)",
      },
      Secret: {
        Title: "Client secret for {{clientId}}",
        Warning:
          "The secret is shown only now. Store it in a password vault and hand it over securely; it cannot be shown again, only replaced.",
        Label: "Client secret",
        Copy: "Copy secret",
        Copied: "Copied!",
        Close: "I have saved it",
      },
      Messages: {
        LoadError: "The applications could not be loaded",
        CreateSuccess: "The application was registered",
        CreateError: "The application could not be registered",
        ClientIdTaken: "This Client ID already exists",
        UpdateSuccess: "The application was saved",
        UpdateError: "The application could not be saved",
        DeleteConfirmTitle: "Delete application",
        DeleteConfirm:
          "Delete application {{clientId}}? All its consents and tokens are gone, its users are signed out of it.",
        DeleteSuccess: "The application was deleted",
        DeleteError: "The application could not be deleted",
        RotateConfirmTitle: "New client secret",
        RotateConfirm:
          "Generate a new secret for {{clientId}}? The old one stops working at once; without the new one the application cannot sign in.",
        RotateError: "A new secret could not be generated",
      },
    },
    Coupons: {
      PageTitle: "Discount Coupons",
      CreateButton: "Create Coupon",
      Table: {
        Code: "Code",
        Region: "Country",
        Discount: "Discount",
        DiscountType: "Discount Type",
        Expiration: "Expiration",
        Usage: "Used",
        Status: "Status",
        Delete: "Delete",
        CopyCode: "Copy code",
        CodeCopied: "Copied!",
      },
      Status: {
        Active: "Active",
        Expired: "Expired",
        OutOfStock: "Out of Stock",
      },
      Form: {
        DialogTitle: "Create New Coupon",
        Code: "Coupon Code",
        Region: "Country",
        AutoGenerate: "Generate code automatically",
        Discount: "Discount",
        DiscountType: "Discount Type",
        Percentage: "Percentage (%)",
        FixedAmount: "Fixed Amount",
        Expiration: "Expiration Date",
        Stock: "Number of Uses",
        StockHelp: "Minimum: {{min}} (already used)",
        Submit: "Create",
        Save: "Save",
        Cancel: "Cancel",
        EditTitle: "Edit Coupon",
        EditTitleWithCode: "Edit Coupon {{code}}",
      },
      Validation: {
        CodeRequired: "Coupon code is required",
        DiscountRequired: "Discount is required",
        DiscountPositive: "Discount must be positive",
        DiscountMax100: "Percentage discount cannot exceed 100%",
        ExpirationRequired: "Expiration date is required",
        StockRequired: "Number of uses is required",
        StockMin: "Number of uses must be at least 1",
      },
      Messages: {
        CreateSuccess: "Coupon created",
        CreateError: "Failed to create coupon",
        LoadError: "Error loading coupons",
        DeleteSuccess: "Coupon deleted",
        DeleteError: "Failed to delete coupon",
        DeleteConfirmTitle: "Delete coupon",
        DeleteConfirm: "Are you sure you want to delete coupon {{code}}?",
        UpdateSuccess: "Coupon updated",
        UpdateError: "Failed to update coupon",
      },
    },
    Raffle: {
      PageTitle: "Prize draws",
      CreateButton: "New round",
      Table: {
        Region: "Market",
        Name: "Name",
        Type: "Type",
        TypeDefault: "Main round",
        TypeCompetition: "Competition",
        Status: "Status",
        DrawDate: "Draw date",
        Tickets: "Tickets",
        Participants: "Participants",
        Checksum: "Draw sheet fingerprint (SHA-256)",
        ChecksumHelp:
          "Computed at close from the generated list of numbers (no holder data). The downloaded draw sheet is verified against it, and the same fingerprint belongs in the draw protocol — it proves the draw ran on the file the system produced.",
        ChecksumCopied: "Fingerprint copied",
        AllocationChecksum: "Number allocation fingerprint (SHA-256)",
        AllocationChecksumHelp:
          "Computed at close; it seals who holds which numbers. It belongs in the draw protocol next to the draw sheet fingerprint. The winner lookup verifies it every time — if the allocation stops matching it, the lookup refuses to answer.",
        AllocationChecksumCopied: "Fingerprint copied",
        Empty: "No rounds yet — the first round starts ticket collecting.",
        Unscheduled: "No draw date yet",
      },
      Status: {
        Draft: "Draft",
        Announced: "Announced",
        Closed: "Closed",
        Drawn: "Drawn",
        Cancelled: "Cancelled",
      },
      Actions: {
        Edit: "Edit",
        View: "Show detail",
        Announce: "Announce",
        Close: "Close",
        Complete: "Mark as drawn",
        Export: "Download draw sheet",
        FindHolder: "Find winner",
        Cancel: "Cancel competition",
        SetDefault: "Set as main round",
      },
      Form: {
        CreateTitle: "New draw round",
        EditTitle: "Edit round",
        Region: "Market",
        Name: "Round name",
        DrawDate: "Draw date",
        Rewards: "Prizes",
        AddReward: "Add prize",
        RewardTitle: "Prize name",
        RewardDescription: "Description",
        RewardQuantity: "Quantity",
        RemoveReward: "Remove",
        Submit: "Save",
        Cancel: "Cancel",
        MainRoundWarning:
          "This market has no open main round yet. This round becomes the market's main round and starts collecting tickets right away. A main round cannot be cancelled.",
      },
      Validation: {
        NameRequired: "Enter the round name",
        RewardTitleRequired: "Enter the prize name",
        RegionRequired: "Select the market",
        RewardQuantityInvalid:
          "The prize count must be a whole number, zero or more",
        TicketNumberInvalid: "Enter a whole ticket number of 1 or more",
        TicketNumberRange: "Enter a ticket number from 1 to {{max}}",
      },
      Messages: {
        LoadError: "Failed to load draw rounds",
        CreateSuccess: "Round created",
        CreateError: "Failed to create the round",
        UpdateSuccess: "Round saved",
        UpdateError: "Failed to save the round",
        UpdateConflict:
          "The round was announced or cancelled in the meantime and can no longer be edited.",
        AnnounceSuccess: "Round announced",
        AnnounceError: "Failed to announce the round",
        AnnounceConfirmTitle: "Announce the round “{{name}}” ({{region}})?",
        AnnounceConfirm:
          "Announcing makes the draw date and the prize list a public promise that can no longer be changed. Changes will only be possible for the next round.",
        AnnounceRequirements:
          "Add an upcoming draw date, at least one prize and a real round name before announcing",
        CloseSuccess: "Round closed",
        CloseError: "Failed to close the round",
        CloseConfirmTitle: "Close the round “{{name}}” ({{region}})?",
        CloseConfirm:
          "Closing is irreversible: tickets get their numbers, the draw sheet is created and collecting continues into the next round. Tickets earned after the close belong to the next round.",
        CloseCompetitionConfirm:
          "Closing is irreversible: tickets get their numbers and the draw sheet is created.",
        CompleteSuccess: "Round marked as drawn",
        CompleteError: "Failed to mark the round",
        CompleteConfirmTitle:
          "Mark the round “{{name}}” ({{region}}) as drawn?",
        CompleteConfirm:
          "You confirm the draw took place following the documented process and the record has been filed.",
        CancelSuccess: "Competition cancelled",
        CancelError: "Failed to cancel the competition",
        CancelConfirmTitle: "Cancel the competition “{{name}}” ({{region}})?",
        CancelConfirm:
          "The draft competition will be cancelled and kept on record as such. An announced competition cannot be cancelled.",
        SetDefaultSuccess: "This round is now the market's main round",
        SetDefaultError: "Failed to set the main round",
        SetDefaultConfirmTitle:
          "Set the round “{{name}}” ({{region}}) as main?",
        SetDefaultConfirm:
          "New tickets will accrue into this round from now on. The current main round becomes an ordinary competition: if it is already announced its tickets stay with it, while an unannounced (draft) round hands its tickets over to the new main round.",
        ExportError: "Failed to download the draw sheet",
        ExportIntegrityError:
          "The draw sheet failed its integrity check — do not draw from it and contact the development team.",
      },
      HolderLookup: {
        Title: "Winner lookup",
        TicketNumber: "Ticket number",
        Search: "Look up",
        HolderName: "Holder",
        Email: "E-mail",
        TicketCount: "Tickets in the round",
        Range: "Number range",
        NotFound: "The ticket number could not be resolved",
        SealMismatch:
          "The number allocation no longer matches its state at the round's close — the lookup was refused. Contact the development team.",
        Error: "Lookup failed — try again",
      },
    },
    Campaigns: {
      PageTitle: "Campaign Administration",
      CreatePageTitle: "Create Campaign",
      CreateButton: "Create New Campaign",
      DraftCampaigns: "Drafts",
      PublishedCampaigns: "Published campaigns",
      ArchivedCampaigns: "Archived campaigns",

      Table: {
        CampaignName: "Campaign Name",
        Code: "Code",
        Receiver: "Receiver",
        Affiliate: "Affiliate",
        Status: "Status",
        StatusOnRestore: "Status on Restore",
        StartDate: "Start Date",
        EndDate: "End Date",
        Created: "Created",
        Deleted: "Deleted",
        Actions: "Actions",
        Unlimited: "Unlimited",
        Enabled: "Enabled",
        Disabled: "Disabled",
        NoReceiverAssigned: "No receiver assigned",
        ViewCampaign: "View Campaign",
        Edit: "Edit",
        Delete: "Delete",
        Restore: "Restore",
        CopyUrl: "Copy campaign URL",
        UrlCopied: "URL copied",
      },

      Status: {
        Draft: "Draft",
        DraftDescription: "This campaign is a draft",
        Archived: "Archived",
        ArchivedDescription: "This campaign is archived",
        ActiveUnlimited: "Active (Unlimited)",
        UnlimitedDescription: "This campaign runs indefinitely",
        StartsToday: "Starts today",
        StartsTomorrow: "Starts tomorrow",
        StartsInDays: "Starts in {{days}} days",
        StartsOn: "Starts on {{date}}",
        Finished: "Finished",
        EndedOn: "Ended on {{date}}",
        EndsToday: "Ends today",
        DayLeft: "1 day left",
        DaysLeft: "{{days}} days left",
        EndsOn: "Ends on {{date}}",
        Active: "Active",
        CurrentlyRunning: "Currently running",
        NotStarted: "Upcoming",
      },

      Form: {
        BasicInformation: "Basic Information",
        CampaignName: "Campaign Name",
        CampaignNamePlaceholder: "e.g., Nadace Help Pes",
        CampaignCode: "Campaign Code (URL path)",
        CampaignCodePlaceholder: "e.g., nadace-help-pes",
        CampaignCodeHelperCreate:
          "Use only lowercase letters without diacritics, numbers, and dashes.",
        CampaignCodeHelperEdit: "Code cannot be changed after creation",
        Description: "Description",
        DescriptionPlaceholder: "Short description of the campaign",
        DescriptionCharCount: "{{count}}/500",
        EnableAffiliateProgram: "Enable Affiliate Program",

        CampaignValidity: "Campaign Validity",
        DateModeUnlimited: "Unlimited",
        DateModeUnlimitedDescription: "Campaign has no start or end date",
        DateModeFromTo: "From - To",
        DateModeFromToDescription: "Campaign runs between specific dates",
        StartDate: "Start Date",
        EndDate: "End Date",
        UnlimitedDateInfo:
          "This campaign will run indefinitely with no start or end date.",

        PaymentReceiver: "Payment Receiver",
        PaymentReceiverDescription:
          "Select the merchant user who will receive payments from this campaign",
        MerchantUser: "Merchant User",

        CampaignContent: "Landing Page Content",
        HeaderTitle: "Header Title",
        HeaderTitlePlaceholder: "e.g., Support Guide Dogs Training",
        FoundationLinkName: "Foundation Link Name",
        FoundationLinkNamePlaceholder: "e.g., Learn More",
        FoundationLinkNameHelper: "Text for the link to foundation website",
        FoundationLinkUrl: "Foundation Link URL",
        FoundationLinkUrlPlaceholder: "https://example.org",
        FoundationLinkUrlHelper: "URL to foundation website",
        SuccessfulPaymentText: "Successful Payment Thank You Text",
        SuccessfulPaymentTextPlaceholder: "Thank you for your contribution!",
        SuccessfulPaymentTextHelper:
          "Text shown after successful payment ({{count}}/1000)",

        ThankYouSection: "Thank You Section",
        Color: "Campaign Color",
        ColorHelp:
          "Primary campaign color (hex format, e.g., #FF9400). Hover color will be automatically darker. Leave empty for default theme color.",
        PaymentInstructionsText: "Payment Instructions",
        PaymentInstructionsTextHelper:
          "Text shown in the 'Pay at Cafe' timeline step. Describes how payments help the campaign. Max 500 characters.",
        OnlyWithMooneText: "Only with Mo.one Text",
        OnlyWithMooneTextHelper:
          "Text shown in the 'Any Method' timeline step. Promotes using Mo.one for payments. Max 500 characters.",

        Images: "Images (Coming Soon)",
        Logo: "Logo",
        LogoHelper: "Campaign logo (PNG/SVG)",
        LandingImage: "Landing image",
        LandingImageHelper: "Landing page preview image (PNG/JPG)",
        Icon: "Icon (Optional)",
        IconHelper: "Campaign icon (PNG/SVG)",
        ThankYouImage: "Successful payment image",
        ThankYouImageHelper: "Image shown after successful payment",
        ImageUploadSuccess: "Image uploaded",
        ImageUploadError: "Failed to upload image",
        UploadError: {
          invalidFileType:
            "Unsupported file type. Allowed formats: PNG, JPEG, SVG.",
          fileSizeExceeded: "File is too large.",
          fileContentMismatch: "File content does not match file type.",
        },

        StepBasicInfo: "Basic information",
        StepLandingPage: "Landing page",
        StepThankYouPage: "Thank you page",
        StepValidity: "Validity",
        PreviewCampaignPage: "Preview campaign page",
        PreviewThankYouPage: "Preview thank you page",
        ImageDeleteSuccess: "Image deleted",
        ImageDeleteError: "Failed to delete image",
        CreateButton: "Create Campaign",
        UpdateLandingPageButton: "Update landing page",
        UpdateThankYouPageButton: "Update thank you page",
        NextStepButton: "Next step",
        PreviousStepButton: "Previous step",
        SaveChangesButton: "Save changes",
        SaveButton: "Save",
        CloseButton: "Close",
        PublishButton: "Publish",
        PublishRequiresReceiver:
          "To publish the campaign you must select a payment receiver.",
        ArchiveButton: "Archive",
        UnpublishButton: "Unpublish",
        CampaignIsLive: "Campaign is live",
        UpdateButton: "Update Campaign",
        Validation: {
          NameRequired: "Campaign name is required",
          CodeRequired: "Campaign code is required",
          CodeInvalidFormat:
            "Code must be lowercase, alphanumeric, and dashes only. Special characters not allowed.",
          DescriptionRequired: "Description is required",
          InvalidUrl: "Must be a valid URL",
          DateRangeRequired:
            "Start date and end date are required when date range is selected",
          EndDateAfterStartDate:
            "End date must be after or equal to start date",
          InvalidColor:
            "Must be a valid color in hex format (e.g., #FF9400 or #FF940080)",
          MaxLength: "Maximum length is {{max}} characters",
        },
        PaymentInstructionsTextPlaceholder:
          "Pay for coffee with Mo.one. We will contribute to families with seriously ill children for accommodation in close proximity to the hospital. So they can stay together.",
        OnlyWithMooneTextPlaceholder:
          "Wherever you pay with Mo.one, we send money to support families with seriously ill children for accommodation in close proximity to the hospital. So they can stay together.<newline/><newline/>Just simply create an account and start!",
      },

      Messages: {
        CreateSuccess: "Campaign created successfully! URL: /campaign/{{code}}",
        CreateError: "Failed to create campaign",
        UpdateSuccess: "Campaign updated successfully!",
        UpdateError: "Failed to update campaign",
        PublishSuccess: "Campaign published",
        ArchiveSuccess: "Campaign archived",
        UnpublishSuccess: "Campaign unpublished",
        CodeExistsError: "Campaign code already exists or is invalid",
        DeleteConfirm: "Are you sure you want to delete this campaign?",
        DeleteSuccess: "Campaign deleted successfully",
        DeleteError: "Failed to delete campaign",
        RestoreConfirm: "Are you sure you want to restore this campaign?",
        RestoreSuccess: "Campaign restored successfully",
        RestoreError: "Failed to restore campaign",
        LoadCampaignsError: "Failed to load campaigns",
        EditCampaignTitle: "Edit Campaign: {{name}}",
      },
    },
    SettlementPoolBoosts: {
      StardustBoost: "Stardust Boost",
      ValorBoost: "Valor Boost",
      Regions: {
        CZ: "Czechia",
        SK: "Slovakia",
      },
      BoostAmount: "Boost Amount ({{currency}})",
      DistributionRatio: "Distribution Ratio",
      HunterPoolRatio: "Licenses",
      ValorPoolRatio: "Valor Points",
      CurrentPoolTotal: "Current Pool Total ({{currency}})",
      PoolTotalLoadError: "Failed to load current pool",
      SaveButton: "Save",
      SaveSuccess: "Boost updated successfully",
      LoadingError: "Failed to load boost configuration",
      Validation: {
        BoostAmountNonNegative: "Boost amount cannot be negative",
        RatioSumMustBe100: "Ratios must sum to 100",
      },
    },
    VatStatusRefresh: {
      Title: "VAT registers",
      Description:
        "Re-checks the VAT registration status of every company and sole trader against the registers (CZ: VAT payer register, SK: VIES). Runs in the background; the result is in the Worker log.",
      Button: "Refresh VAT status",
      Loading: "Starting...",
      Success: "VAT status refresh has been started.",
      Error: "VAT status refresh could not be started.",
    },
    PaymentSimulation: {
      Title: "Simulate payment (sandbox)",
      Description:
        "Marks a sandbox payment as paid, as if the bank had reported it settled. Only for transactions of type Payment with a sandbox bank in the state Initiated, Processing or Investigation needed. Available on the test and staging environments only.",
      PublicIDLabel: "Transaction code",
      Button: "Mark as paid",
      Loading: "Marking...",
      ConfirmTitle: "Simulate payment?",
      ConfirmText:
        "Transaction {{publicID}} will be marked as paid. The same follow-up steps run as for a real payment (notifications, webhook, settlement).",
      Success: "Transaction {{publicID}} has been marked as paid.",
      Errors: {
        NotFound:
          "Transaction not found, or simulation is switched off on this environment.",
        Forbidden: "Only an administrator can run the simulation.",
        NotPayment: "Only transactions of type Payment can be simulated.",
        NotSandbox:
          "The transaction has no sandbox payment. Pay it with a sandbox bank first.",
        StatusNotAllowed:
          "The transaction is not in a state from which payment can be simulated.",
        Generic: "The payment simulation failed.",
      },
    },
  },
  Devices: {
    List: {
      LoadingError: "Failed to load devices",
      Name: "Name",
      PayPoint: "Paypoint",
      DateCreated: "Created",
      LastUsedAt: "Last used",
      RevokeConfirm: "Do you really want to revoke this device?",
      RevokeSuccess: "Device revoked",
      RevokeError: "Failed to revoke device",
      RegenerateConfirm:
        "Do you really want to regenerate the key? The existing key will stop working.",
      RegenerateSuccess: "New key generated",
      RegenerateError: "Failed to regenerate the key",
      NewDeviceSecret: "New device key",
      CopyDeviceSecret: "Copy key",
      DeviceSecretCopied: "Key copied to clipboard",
      AddDevice: "Add device",
      DeviceName: "Device name",
      SelectPayPoint: "Select paypoint",
      LinkSuccess: "Device linked",
      LinkError: "Failed to link device",
      NoTerminalPayPoints:
        "You don't have any free terminal paypoint. Create a Terminal paypoint first.",
      DeviceSecretWarning:
        "This key is shown only once. Copy and store it securely — it cannot be retrieved later.",
    },
  },
  PaymentGateways: {
    Legend: "Payment gateway",
    Description:
      "Credentials for integrating the Mo.one payment gateway into your e-shop. The client secret is shown only once when generated.",
    ClientID: "Client ID",
    CopyClientID: "Copy Client ID",
    ClientIDCopied: "Client ID copied",
    LastUsedAt: "Last used",
    NoCredential: "Credentials have not been generated yet.",
    Generate: "Generate credentials",
    Regenerate: "New client secret",
    Revoke: "Revoke credentials",
    Secret: {
      CreatedTitle: "Credentials created",
      RegeneratedTitle: "New client secret generated",
      Warning:
        "The client secret is shown only once. Copy and store it securely — it cannot be retrieved later.",
      ClientID: "Client ID",
      ClientSecret: "Client secret",
      Copied: "Copied to clipboard",
      Close: "Close",
    },
    Messages: {
      GenerateError: "Failed to generate credentials",
      RegenerateConfirmTitle: "Generate new client secret",
      RegenerateConfirm:
        "Do you really want to generate a new client secret? The existing one stops working immediately and must be updated in your e-shop.",
      RegenerateError: "Failed to generate a new client secret",
      RevokeConfirmTitle: "Revoke credentials",
      RevokeConfirm:
        "Do you really want to revoke these credentials? The e-shop stops accepting payments immediately until you generate new ones.",
      RevokeSuccess: "Credentials revoked",
      RevokeError: "Failed to revoke credentials",
    },
  },
  TerminalPin: {
    Title: "Terminal PIN",
    Description:
      "Enter this 4-digit PIN on the terminal when signing in. It identifies who is operating the device.",
    NoPinYet:
      "No PIN issued yet. It will be generated when a merchant assigns you to a terminal paypoint.",
    Reveal: "Reveal",
    Hide: "Hide",
    Regenerate: "Generate new PIN",
    RegenerateConfirm:
      "Do you really want to generate a new PIN? The existing PIN will stop working.",
    RegenerateSuccess: "New PIN generated",
    RegenerateError: "Failed to generate a new PIN",
  },
  UserSettings: {
    BalanceVisibility: {
      Title: "Balance visibility",
      Label: "Show balance on the main screen",
      Description:
        "When disabled, the balance is hidden and only revealed after clicking the eye icon.",
    },
    QuickPayment: {
      Title: "Quick payment",
    },
  },
  Sso: {
    Consent: {
      Intro: "{{app}} is asking for access to your {{appName}} account.",
      PermissionsHeading: "The application will get:",
      RevokeHint:
        "You can withdraw the access at any time under Settings → Connected applications.",
      Allow: "Allow",
      Deny: "Deny",
      Redirecting: "Taking you to the application…",
      ReauthTitle: "Sign in again",
      ReauthText:
        "{{app}} requires a fresh sign-in. You will be brought back here once you sign in again.",
      ReauthButton: "Sign in again",
      SignedInAs: "Signed in as {{who}}",
      UseAnotherAccount: "Use another account",
      MissingRequest:
        "The sign-in request is missing. Please start again from the application you are signing in to.",
    },
    ConnectedApps: {
      Empty: "You have not signed in to any application with {{appName}} yet.",
      ConnectedAt: "Connected {{date}}",
      Disconnect: "Disconnect",
      DisconnectConfirmTitle: "Disconnect application",
      DisconnectConfirm:
        "{{app}} will lose access to your account and will ask for your consent again at its next sign-in.",
      DisconnectSuccess: "The application has been disconnected.",
      DisconnectError: "The application could not be disconnected.",
      LoadError: "Connected applications could not be loaded.",
    },
    Scopes: {
      OpenId: "Sign-in",
      Profile: "Your name",
      Email: "Your e-mail address",
      OfflineAccess: "Stay signed in (no repeated sign-ins)",
      Contexts: "Act on behalf of your roles in {{appName}}",
      Payments: "Accept payments for your company",
      Merchant: "Manage your company's staff, pay-points and PINs",
      Hunter: "Your terminals, points and affiliate link",
      TerminalsManage: "Terminal inventory",
      DirectoryRead: "User and hunter directories",
      CouponsCreate: "Issuing coupons",
      LoyaltyRead: "Loyalty programme",
    },
    Errors: {
      RequestNotFound:
        "The sign-in request does not exist. Please start again from the application.",
      RequestExpired:
        "The sign-in request has expired. Please start again from the application.",
      RequestNotPending:
        "This request has already been answered. Please start again from the application.",
      IdentityRequired:
        "Your account needs to be completed first. Sign in to {{appName}} and try again.",
      IdentityNotActive:
        "Your account is not active, signing in to applications is not possible.",
      BackOfficeNotAllowed:
        "An administrator account cannot sign in to partner applications.",
      ConsentNotFound:
        "The consent is no longer valid. Please start again from the application.",
      ClientNotFound: "The application is not registered.",
      Unknown: "Signing in to the application failed.",
    },
  },
  Beta: {
    Badge: "Beta",
    RoadmapUrl: "https://mo.one/roadmap",
    AuthCard: {
      Title: "Welcome to the {{appName}} Beta!",
      Body: "We're working on improvements. Find our plans in the <roadmap>Roadmap</roadmap>.",
    },
    Drawer: {
      Title: "Welcome to the {{appName}} Beta!",
      BodyFirst:
        "We are still working on the app and pushing it forward every day. If you spot a bug or any issue, please let us know via the <reportButton /> button at the top of the screen.",
      BodySecond:
        "If you want to know where {{appName}} is heading, take a look at <roadmap>our Roadmap</roadmap>.",
      BodyThird: "Thank you for helping us grow.",
      Continue: "Continue",
      Close: "Close",
    },
    MenuItem: "{{appName}} Beta",
  },
};
